import {
  Activity,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  CircleHelp,
  Clock3,
  Coins,
  CreditCard,
  Crown,
  FileCheck2,
  Gift,
  House,
  LockKeyhole,
  LogIn,
  LogOut,
  Landmark,
  Mail,
  Maximize2,
  Megaphone,
  Menu,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  QrCode,
  ReceiptText,
  RefreshCw,
  Search,
  ScanLine,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Store,
  TicketCheck,
  Trash2,
  Users,
  UsersRound,
  WalletCards,
  Workflow,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SUBSCRIPTION_PLANS,
  type SubscriptionBillingCycle,
  type SubscriptionFeature,
  type SubscriptionPaymentMethod,
  type SubscriptionPlanCode,
} from "@loyalty/shared";
import {
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  type CreateTenantInput,
  type CheckoutTransaction,
  type CheckoutRedemptionQuote,
  type InventoryItem,
  type BusinessOwnerAccountDetail,
  type LoginInput,
  type Portal,
  type Redemption,
  type Reward,
  type SessionType,
  type SessionUser,
  type Tenant,
  type TenantRegistrationInput,
  ApiError,
  archivePlatformTenant,
  approveCheckout,
  bootstrapSuperAdmin,
  changeBusinessSubscription,
  checkBusinessPanAvailability,
  createCheckoutTransaction,
  createTierRule,
  createTenant,
  fetchCustomerAccounts,
  fetchCustomerNotifications,
  fetchCheckoutAnalytics,
  fetchCheckoutConfiguration,
  fetchCheckoutTransactions,
  fetchCheckoutRedemptionQuote,
  fetchTenantInventory,
  fetchBusinessOwnerAccount,
  fetchBusinessSetupStatus,
  fetchBusinessSubscription,
  fetchPublicReward,
  fetchPublicRewards,
  fetchPublicTierProgram,
  fetchReceiptSubmissions,
  fetchRedemptions,
  fetchSession,
  fetchTierActivity,
  fetchTierAdminProgram,
  fetchTierStatus,
  fetchTenants,
  login,
  logout,
  messageFromError,
  issueCheckoutClaim,
  joinCustomerBusiness,
  publishTierRule,
  redeemReward,
  redeemCheckoutClaim,
  registerCustomer,
  registerTenant,
  resendVerificationEmail,
  resetPassword,
  reviewReceipt,
  reviewTenantApplication,
  runTierAssessment,
  sendTenantOwnerPasswordReset,
  setTenantStatus,
  submitReceipt,
  customerConfirmCheckout,
  reverseCheckout,
  verifyEmail,
  updateBusinessOwnerAccount,
  uploadBusinessOwnerProfileImage,
} from "./api";
import { QRCodeSVG } from "qrcode.react";
import {
  TenantRewardsWorkspace,
  type TenantRewardWorkspaceView,
} from "./tenant-rewards/TenantRewardsWorkspace";
import {
  CheckoutControlEditor,
  NewRewardPointRule,
  TenantEarningRules,
} from "./tenant-rewards/TenantEarningRules";
import { BusinessSetupWizard } from "./tenant-rewards/BusinessSetupWizard";
import { CustomerExperience } from "./customer/CustomerExperience";
import { BusinessMeroCredits, PlatformMeroCredits } from "./mero-credits/MeroCredits";
import { MarketplaceView, TenantLocations, TenantScanner } from "./experience/LoyaltyExperience";
import { AppAlert } from "./components/AppAlertDialog";
import {
  SubscriptionUpgradeDialog,
  type SubscriptionUpgradeReason,
} from "./components/SubscriptionUpgradeDialog";
import { TenantLocationPicker, type BusinessLocationPin } from "./components/TenantLocationPicker";
import { ArchiveAccountDialog } from "./components/ArchiveAccountDialog";
import { LanguageSwitch } from "./i18n";
import { PlatformTierCard } from "./components/PlatformTierCard";
import {
  PlatformMeroCreditAcceptanceEditor,
  PlatformTenantEditor,
  SuperAdminEarningRulesView,
  SuperAdminHomeView,
  SuperAdminSettingsView,
  SuperAdminUsersView,
} from "./super-admin/SuperAdminViews";
import {
  businessApplicationFeedback,
  businessPasswordResetFeedback,
  type SuperAdminFeedback,
} from "./super-admin/super-admin-feedback";
import {
  AccountDeletionPage,
  CookiePolicyPage,
  PrivacyPolicyPage,
  TermsAndConditionsPage,
} from "./legal/LegalPages";
import {
  businessSetupQueryKey,
  businessSetupTarget,
  isBusinessSetupRouteAllowed,
} from "./business-setup-gate";
import {
  featureUpgradeReason,
  firstPlanWithFeature,
  subscriptionIncludes,
  tenantRouteSubscriptionRequirement,
} from "./subscription-access";

const sessionKey = ["session"] as const;
const publicRewardsKey = ["public-rewards"] as const;

const rememberedLoginKey = (portal: Portal, field: "email" | "business-code") =>
  `mero-perks:login:${portal}:${field}`;

const readRememberedLogin = (portal: Portal, field: "email" | "business-code") => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(rememberedLoginKey(portal, field)) ?? "";
  } catch {
    return "";
  }
};

const writeRememberedLogin = (
  portal: Portal,
  field: "email" | "business-code",
  remember: boolean,
  value: string,
) => {
  if (typeof window === "undefined") return;
  try {
    const key = rememberedLoginKey(portal, field);
    if (remember && value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Login still works when browser storage is unavailable or blocked.
  }
};

const formatPoints = (points: number) => new Intl.NumberFormat("en-US").format(points);

const formatMoney = (minor: number, currency = "NPR") =>
  new Intl.NumberFormat("en-NP", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(minor / 100);

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(
        parsed,
      );
};

const initials = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "MR";

const roleHome = (sessionType: SessionType) =>
  sessionType === "super_admin"
    ? "/super-admin"
    : sessionType === "tenant_staff"
      ? "/tenant"
      : "/account";

function Brand({ inverse = false }: { inverse?: boolean }) {
  return (
    <Link
      className={`brand ${inverse ? "brand--inverse" : ""}`}
      to="/"
      aria-label="Mero Perks home"
    >
      <img
        className="brand__image"
        src="/brand/mero-brand-logo.png"
        width="1254"
        height="1254"
        alt=""
      />
      <span className="brand__name">
        Mero <strong>Perks</strong>
      </span>
      <img
        className="brand__compact-mark"
        src="/brand/mero-brand-logo.png"
        width="1254"
        height="1254"
        alt=""
      />
    </Link>
  );
}

function LoadingBlock({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading-block" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

function Alert({
  children,
  tone = "error",
}: {
  children: ReactNode;
  tone?: "error" | "warning" | "info" | "success";
}) {
  if (tone === "error" || tone === "warning") {
    return (
      <AppAlert
        message={
          typeof children === "string"
            ? children
            : "Something needs your attention before you continue."
        }
        tone={tone}
      />
    );
  }
  return (
    <div className={`alert alert--${tone}`} role="status">
      {children}
    </div>
  );
}

function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: false,
  });
}

function useBusinessSetupStatus(enabled = true) {
  return useQuery({
    queryKey: businessSetupQueryKey,
    queryFn: fetchBusinessSetupStatus,
    enabled,
    refetchInterval: 3_000,
  });
}

function useDismissOnEscape(close: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close]);
}

function RouteScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return undefined;
  }, [location.hash, location.pathname, location.search]);

  return null;
}

const publicNavigation = [
  {
    label: "Platform",
    summary: "One membership, every rewarding moment.",
    links: [
      {
        to: "/rewards",
        label: "Rewards marketplace",
        detail: "Discover and use offers from local businesses.",
        icon: <Gift />,
      },
      {
        to: "/platform/wallet",
        label: "Member wallet",
        detail: "Keep points, Mero Credits, and activity together.",
        icon: <WalletCards />,
      },
      {
        to: "/platform/tiers",
        label: "Membership tiers",
        detail: "Grow from Mero to Elite across the network.",
        icon: <Crown />,
      },
      {
        to: "/platform/credits",
        label: "Mero Credits",
        detail: "Platform-wide value at participating businesses.",
        icon: <Coins />,
      },
    ],
  },
  {
    label: "For business",
    summary: "Loyalty tools that fit the way you serve customers.",
    links: [
      {
        to: "/business",
        label: "Business platform",
        detail: "See the complete Mero Perks business workspace.",
        icon: <Store />,
      },
      {
        to: "/solutions/quick-offers",
        label: "Quick offers",
        detail: "Launch useful promotions without complexity.",
        icon: <TicketCheck />,
      },
      {
        to: "/solutions/checkout",
        label: "Checkout & redemption",
        detail: "Earn, redeem, and scan securely in one flow.",
        icon: <QrCode />,
      },
      {
        to: "/solutions/customer-insights",
        label: "Customer insights",
        detail: "Understand visits, rewards, and return behavior.",
        icon: <BarChart3 />,
      },
    ],
  },
  {
    label: "Solutions",
    summary: "Made for the businesses people return to.",
    links: [
      {
        to: "/solutions/retail",
        label: "Retail",
        detail: "Reward purchases, brands, and repeat visits.",
        icon: <ShoppingBag />,
      },
      {
        to: "/solutions/food-and-beverage",
        label: "Food & beverage",
        detail: "Turn regular orders into lasting habits.",
        icon: <ReceiptText />,
      },
      {
        to: "/solutions/hospitality",
        label: "Hotels & stays",
        detail: "Recognize guests across every visit.",
        icon: <Building2 />,
      },
      {
        to: "/solutions/small-business",
        label: "Small business",
        detail: "Start simple and grow at your pace.",
        icon: <UsersRound />,
      },
    ],
  },
  {
    label: "Resources",
    summary: "Clear answers for members and businesses.",
    links: [
      {
        to: "/how-it-works",
        label: "How Mero Perks works",
        detail: "A simple guide to earning and using value.",
        icon: <CircleHelp />,
      },
      {
        to: "/member-benefits",
        label: "Member benefits",
        detail: "Explore points, tiers, credits, and rewards.",
        icon: <BadgeCheck />,
      },
      {
        to: "/pricing",
        label: "Business pricing",
        detail: "Compare Free, Pro, and Master plans.",
        icon: <CircleDollarSign />,
      },
      {
        to: "/resources/faq",
        label: "Frequently asked questions",
        detail: "Helpful answers before you get started.",
        icon: <CircleHelp />,
      },
    ],
  },
] as const;

function PublicHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const session = useSession();

  useEffect(() => {
    setMenuOpen(false);
    setActiveMenu(null);
  }, [location.pathname]);

  useDismissOnEscape(() => {
    setMenuOpen(false);
    setActiveMenu(null);
  });

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      queryClient.setQueryData(sessionKey, null);
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    }
  };

  return (
    <header className="site-header site-header--club">
      <div className="container site-header__inner">
        <Brand />
        <button
          className="nav-toggle icon-control"
          type="button"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          aria-controls="public-navigation"
          onClick={() => {
            setMenuOpen((value) => !value);
          }}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
        <div className={`site-header__menu ${menuOpen ? "is-open" : ""}`} id="public-navigation">
          <nav className="site-nav" aria-label="Primary navigation">
            {publicNavigation.map((group) => {
              const expanded = activeMenu === group.label;
              return (
                <div className={`site-nav__group ${expanded ? "is-open" : ""}`} key={group.label}>
                  <button
                    className="site-nav__trigger"
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => {
                      setActiveMenu(expanded ? null : group.label);
                    }}
                  >
                    {group.label} <ChevronDown size={15} />
                  </button>
                  <div className="site-mega-menu">
                    <div className="site-mega-menu__links">
                      {group.links.map((item) => (
                        <Link to={item.to} key={item.to}>
                          <span>{item.icon}</span>
                          <span>
                            <strong>{item.label}</strong>
                            <small>{item.detail}</small>
                          </span>
                          <ArrowRight size={17} />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            <NavLink to="/marketplace">Browse businesses</NavLink>
            <NavLink to="/rewards">Browse rewards</NavLink>
          </nav>
          <div className="site-header__actions">
            <LanguageSwitch />
            {session.data ? (
              <>
                <Link className="user-link" to={roleHome(session.data.sessionType)}>
                  <span className="avatar avatar--small">{initials(session.data.name)}</span>
                  <span>{session.data.name}</span>
                </Link>
                <button
                  className="button button--quiet button--compact"
                  type="button"
                  onClick={() => void handleLogout()}
                >
                  <LogOut size={17} /> Log out
                </button>
              </>
            ) : (
              <>
                <Link className="button button--quiet button--compact" to="/login/customer">
                  <LogIn size={17} /> Log in
                </Link>
                <Link className="button button--primary button--compact" to="/register">
                  Join Mero Perks
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer__lead">
        <div>
          <p className="marketing-kicker">One loyalty network</p>
          <h2>Good places deserve to be remembered.</h2>
        </div>
        <Link className="button button--gold button--large" to="/register">
          Join Mero Perks <ArrowRight size={18} />
        </Link>
      </div>
      <div className="container footer__grid">
        <div>
          <Brand inverse />
          <p>More places. Brighter days. Rewards for the moments you love.</p>
        </div>
        <nav aria-label="Customer links">
          <strong>Platform</strong>
          <Link to="/rewards">Rewards marketplace</Link>
          <Link to="/platform/wallet">Member wallet</Link>
          <Link to="/platform/tiers">Membership tiers</Link>
          <Link to="/platform/credits">Mero Credits</Link>
        </nav>
        <nav aria-label="Solutions links">
          <strong>Solutions</strong>
          <Link to="/solutions/retail">Retail</Link>
          <Link to="/solutions/food-and-beverage">Food &amp; beverage</Link>
          <Link to="/solutions/hospitality">Hotels &amp; stays</Link>
          <Link to="/solutions/small-business">Small business</Link>
        </nav>
        <nav aria-label="Explore links">
          <strong>Explore</strong>
          <Link to="/rewards">All rewards</Link>
          <Link to="/marketplace">Business map</Link>
          <Link to="/how-it-works">How it works</Link>
          <Link to="/member-benefits">Member benefits</Link>
          <Link to="/register">Create account</Link>
        </nav>
        <nav aria-label="Business links">
          <strong>Business</strong>
          <Link to="/business">Business platform</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/register/tenant">Become a partner</Link>
          <Link to="/login/tenant">Business Owner login</Link>
        </nav>
        <nav aria-label="Legal links">
          <strong>Legal</strong>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/cookies-policy">Cookie Policy</Link>
          <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
          <Link to="/account-deletion">Account deletion</Link>
        </nav>
        <div className="footer__support">
          <strong>Need help?</strong>
          <a href="mailto:support@meroperks.com">support@meroperks.com</a>
          <span>Sun–Fri · 9:00–18:00 NPT</span>
        </div>
      </div>
      <div className="container footer__technology-partner">
        <span>Technology partner</span>
        <img
          src="/spandan-technology-solutions-nepal.png"
          alt="Spandan Technology Solutions Nepal"
          width="240"
          height="80"
          loading="lazy"
        />
        <p>© 2026 Spandan Technology Solutions Nepal.</p>
      </div>
    </footer>
  );
}

function PublicFrame({ children }: { children: ReactNode }) {
  return (
    <div className="public-shell">
      <PublicHeader />
      {children}
      <Footer />
    </div>
  );
}

function RewardArtwork({ reward, large = false }: { reward: Reward; large?: boolean }) {
  const palette = ["coral", "green", "amber", "blue"];
  let paletteSeed = 0;
  for (const letter of reward.tenantName) paletteSeed += letter.charCodeAt(0);
  const paletteIndex = paletteSeed % palette.length;
  const configuredTheme = reward.mechanismConfig?.displayTheme;
  const configuredTone = configuredTheme ? {
    CRIMSON: "coral",
    GOLD: "amber",
    CREAM: "green",
    NAVY: "blue",
  }[configuredTheme] : undefined;
  const tone = configuredTone ?? palette[paletteIndex] ?? "green";
  return (
    <div className={`reward-art reward-art--${tone} ${large ? "reward-art--large" : ""}`}>
      {reward.imageUrl ? (
        <img src={reward.imageUrl} alt="" />
      ) : (
        <>
          <span className="reward-art__orb" aria-hidden="true" />
          <span className="merchant-monogram" aria-hidden="true">
            {initials(reward.tenantName)}
          </span>
          <Gift className="reward-art__gift" aria-hidden="true" />
        </>
      )}
      {reward.featured && (
        <span className="featured-pill">
          <Sparkles size={13} /> Featured
        </span>
      )}
    </div>
  );
}

function rewardValueSummary(reward: Reward) {
  if (reward.rewardType === "PERCENTAGE_DISCOUNT" && reward.rewardValueMinor !== null) {
    return `${new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(reward.rewardValueMinor / 100)}% off`;
  }
  if (
    ["FIXED_DISCOUNT", "SPEND_AND_GET", "EXCLUSIVE_PRICE"].includes(reward.rewardType) &&
    reward.rewardValueMinor !== null
  ) {
    return `${formatMoney(reward.rewardValueMinor, reward.currencyCode)} value`;
  }
  if (reward.rewardType === "BONUS_POINTS" && reward.rewardValueMinor !== null) {
    return `+${formatPoints(reward.rewardValueMinor)} business points`;
  }
  return reward.rewardType.replaceAll("_", " ").toLowerCase();
}

function rewardGuardrails(reward: Reward) {
  const conditions: string[] = [];
  if (reward.minimumSpendMinor > 0)
    conditions.push(`Minimum spend ${formatMoney(reward.minimumSpendMinor, reward.currencyCode)}`);
  if (reward.maximumDiscountMinor !== null)
    conditions.push(
      `Maximum discount ${formatMoney(reward.maximumDiscountMinor, reward.currencyCode)}`,
    );
  if (reward.eligibleTierCodes.length > 0)
    conditions.push(`Mero tier: ${reward.eligibleTierCodes.join(", ").replaceAll("_", " ")}`);
  if (reward.eligibleLocationNames.length > 0)
    conditions.push(`Locations: ${reward.eligibleLocationNames.join(", ")}`);
  if (reward.channelScope.length > 0)
    conditions.push(`Channels: ${reward.channelScope.join(", ").replaceAll("_", " ")}`);
  if (reward.expirationMode === "DAYS_AFTER_ISSUE" && reward.expirationDaysAfterIssue !== null) {
    conditions.push(
      `Use within ${String(reward.expirationDaysAfterIssue)} day${reward.expirationDaysAfterIssue === 1 ? "" : "s"} after issue`,
    );
  } else if (reward.expiresAt) {
    conditions.push(`Available until ${formatDate(reward.expiresAt)}`);
  }
  conditions.push(
    `${String(reward.perMemberLimit)} redemption${reward.perMemberLimit === 1 ? "" : "s"} per member`,
  );
  return conditions;
}

interface RewardCardProps {
  reward: Reward;
  user: SessionUser | null | undefined;
  pending?: boolean;
  onRedeem?: (reward: Reward) => void;
}

function RewardCard({ reward, user, pending = false, onRedeem }: RewardCardProps) {
  const isCustomer = user?.sessionType === "customer";
  const triggerOnly = ["BONUS_POINTS", "POINTS_MULTIPLIER"].includes(reward.rewardType);
  const displayTheme = (reward.mechanismConfig?.displayTheme ?? "CRIMSON").toLowerCase();
  return (
    <article className={`reward-card reward-card--${displayTheme}`}>
      <RewardArtwork reward={reward} />
      <div className="reward-card__body">
        <div className="merchant-line">
          <span className="merchant-dot">{initials(reward.tenantName)}</span>
          <span>{reward.tenantName}</span>
          <BadgeCheck size={15} aria-label="Verified partner" />
        </div>
        <h3>{reward.title}</h3>
        <p>{reward.description}</p>
        <div className="reward-card__meta">
          <span className="points">
            <Coins size={16} /> {formatPoints(reward.pointsCost)} points
          </span>
          <span className="category-pill">{reward.category}</span>
        </div>
        <div className="reward-card__guardrails" aria-label="Key reward conditions">
          <strong>{rewardValueSummary(reward)}</strong>
          {rewardGuardrails(reward)
            .slice(0, 2)
            .map((condition) => (
              <span key={condition}>{condition}</span>
            ))}
        </div>
        {isCustomer && onRedeem ? (
          <button
            className="button button--primary button--full"
            type="button"
            disabled={pending || !reward.available || triggerOnly}
            onClick={() => {
              onRedeem(reward);
            }}
          >
            {triggerOnly
              ? "Earn on a verified purchase"
              : pending
                ? "Redeeming…"
                : reward.available
                  ? "Redeem reward"
                  : "Unavailable"}
          </button>
        ) : user ? (
          <span className="button button--disabled button--full">Customer account required</span>
        ) : (
          <Link className="button button--primary button--full" to="/login/customer">
            Login to redeem <ArrowRight size={17} />
          </Link>
        )}
      </div>
    </article>
  );
}

function RewardTermsDialog({
  reward,
  pending,
  close,
  confirm,
}: {
  reward: Reward;
  pending: boolean;
  close: () => void;
  confirm: (mechanismSelectionId?: string) => void;
}) {
  useDismissOnEscape(close);
  const [mechanismSelectionId, setMechanismSelectionId] = useState("");
  const conditions = rewardGuardrails(reward);
  const choiceOptions =
    reward.rewardType === "CHOICE_REWARD" ? (reward.mechanismConfig?.options ?? []) : [];
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={pending ? undefined : close}>
      <section
        className="modal reward-terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reward-terms-title"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          className="modal__close icon-control"
          type="button"
          aria-label="Close reward details"
          disabled={pending}
          onClick={close}
        >
          <X />
        </button>
        <p className="eyebrow">Review before redeeming</p>
        <h2 id="reward-terms-title">{reward.title}</h2>
        <p>{reward.description}</p>
        <div className="reward-terms-modal__value">
          <Gift />
          <span>
            <small>Offer value</small>
            <strong>{rewardValueSummary(reward)}</strong>
          </span>
        </div>
        <section aria-labelledby="reward-conditions-title">
          <h3 id="reward-conditions-title">Eligibility and use</h3>
          <ul>
            {[...(reward.mechanismTerms ?? []), ...conditions].map((condition) => (
              <li key={condition}>
                <Check size={16} aria-hidden="true" />
                {condition}
              </li>
            ))}
          </ul>
        </section>
        {choiceOptions.length > 0 && (
          <section aria-labelledby="reward-choice-title">
            <h3 id="reward-choice-title">Choose your benefit</h3>
            <div className="reward-choice-pills">
              {choiceOptions.map((option) => (
                <button
                  className={mechanismSelectionId === option.id ? "is-selected" : ""}
                  key={option.id}
                  type="button"
                  aria-pressed={mechanismSelectionId === option.id}
                  onClick={() => {
                    setMechanismSelectionId(option.id);
                  }}
                >
                  {mechanismSelectionId === option.id && <Check size={15} />} {option.label}
                </button>
              ))}
            </div>
          </section>
        )}
        <section aria-labelledby="tenant-terms-title">
          <h3 id="tenant-terms-title">{reward.tenantName} terms</h3>
          <p className="reward-terms-modal__terms">
            {reward.termsText ||
              "No additional business terms are listed. Reward availability and final acceptance remain subject to the conditions above."}
          </p>
        </section>
        <p className="reward-terms-modal__consent">
          By redeeming, you confirm that you reviewed these conditions and authorize the listed
          business-point deduction.
        </p>
        <div className="modal-actions">
          <button className="button button--quiet" type="button" disabled={pending} onClick={close}>
            Not yet
          </button>
          <button
            autoFocus
            className="button button--primary"
            type="button"
            disabled={pending || (choiceOptions.length > 0 && !mechanismSelectionId)}
            onClick={() => {
              confirm(mechanismSelectionId || undefined);
            }}
          >
            {pending ? "Redeeming…" : `Redeem for ${formatPoints(reward.pointsCost)} points`}
          </button>
        </div>
      </section>
    </div>
  );
}

function RedemptionDialog({ redemption, close }: { redemption: Redemption; close: () => void }) {
  useDismissOnEscape(close);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={close}>
      <section
        className="modal redemption-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="redemption-title"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          autoFocus
          className="modal__close icon-control"
          type="button"
          aria-label="Close"
          onClick={close}
        >
          <X />
        </button>
        <span className="success-mark">
          <Check size={28} />
        </span>
        <p className="eyebrow">Reward secured</p>
        <h2 id="redemption-title">{redemption.rewardTitle}</h2>
        <p>
          Show this redemption at {redemption.tenantName}. You can find it again in your reward
          history.
        </p>
        <p>
          Your secure reward QR is available from the reward wallet and is generated only when you
          open it.
        </p>
        <Link className="button button--primary button--full" to="/account" onClick={close}>
          View my rewards
        </Link>
      </section>
    </div>
  );
}

function PublicMarketplace() {
  return (
    <PublicFrame>
      <MarketplaceView />
    </PublicFrame>
  );
}

function PublicRewardsMarketplace() {
  const rewardsQuery = useQuery({ queryKey: publicRewardsKey, queryFn: fetchPublicRewards });
  const session = useSession();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All rewards");
  const [tenant, setTenant] = useState("All partners");
  const [sort, setSort] = useState<
    "recommended" | "points-low" | "points-high" | "expiring" | "business"
  >("recommended");
  const [visibleCount, setVisibleCount] = useState(12);
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [rewardToConfirm, setRewardToConfirm] = useState<Reward | null>(null);
  const [redeemError, setRedeemError] = useState("");

  const redeemMutation = useMutation({
    mutationFn: ({
      reward,
      mechanismSelectionId,
    }: {
      reward: Reward;
      mechanismSelectionId?: string;
    }) => redeemReward(reward.publicId, mechanismSelectionId),
    onSuccess: async (value) => {
      setRedemption(value);
      setRewardToConfirm(null);
      setRedeemError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer-redemptions"] }),
        queryClient.invalidateQueries({ queryKey: ["customer-accounts"] }),
        queryClient.invalidateQueries({ queryKey: sessionKey }),
      ]);
    },
    onError: (error) => {
      setRedeemError(messageFromError(error));
    },
  });

  const rewards = rewardsQuery.data ?? [];
  const tenantNames = useMemo(
    () => ["All partners", ...new Set(rewards.map((reward) => reward.tenantName))],
    [rewards],
  );
  const categoryNames = useMemo(
    () => ["All rewards", ...new Set(rewards.map((reward) => reward.category))],
    [rewards],
  );
  const filteredRewards = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = rewards.filter((reward) => {
      const matchesSearch =
        !needle ||
        `${reward.title} ${reward.description} ${reward.tenantName} ${reward.category}`
          .toLowerCase()
          .includes(needle);
      const matchesCategory = category === "All rewards" || reward.category === category;
      const matchesTenant = tenant === "All partners" || reward.tenantName === tenant;
      return matchesSearch && matchesCategory && matchesTenant && reward.available;
    });
    return [...matches].sort((left, right) => {
      if (sort === "points-low") return left.pointsCost - right.pointsCost;
      if (sort === "points-high") return right.pointsCost - left.pointsCost;
      if (sort === "business") return left.tenantName.localeCompare(right.tenantName);
      if (sort === "expiring") {
        const leftExpiry = left.expiresAt ? new Date(left.expiresAt).getTime() : Number.MAX_VALUE;
        const rightExpiry = right.expiresAt
          ? new Date(right.expiresAt).getTime()
          : Number.MAX_VALUE;
        return leftExpiry - rightExpiry;
      }
      return Number(right.featured) - Number(left.featured);
    });
  }, [category, query, rewards, sort, tenant]);

  const resetFilters = () => {
    setQuery("");
    setCategory("All rewards");
    setTenant("All partners");
    setSort("recommended");
    setVisibleCount(12);
  };

  return (
    <PublicFrame>
      <main className="public-rewards-marketplace">
        <section className="honors-rewards" aria-labelledby="browse-rewards-title">
          <div className="container">
            <div className="honors-rewards__head">
              <div>
                <p className="honors-eyebrow">Live reward marketplace</p>
                <h1 id="browse-rewards-title">Browse available rewards.</h1>
                <p>
                  Search every currently available Mero Perks reward by business, category, value,
                  or expiry. Sign in only when you are ready to redeem.
                </p>
              </div>
              <div className="honors-rewards__count">
                <strong>{filteredRewards.length}</strong>
                <span>reward{filteredRewards.length === 1 ? "" : "s"} available now</span>
              </div>
            </div>

            {redeemError && <Alert>{redeemError}</Alert>}

            <div className="honors-reward-console">
              <div className="catalog-tools catalog-tools--wide">
                <label className="search-field">
                  <span className="sr-only">Search rewards</span>
                  <Search size={19} />
                  <input
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setVisibleCount(12);
                    }}
                    placeholder="Search rewards, categories, or businesses"
                  />
                </label>
                <label className="select-field">
                  <span className="sr-only">Filter rewards by business</span>
                  <Store size={18} />
                  <select
                    value={tenant}
                    onChange={(event) => {
                      setTenant(event.target.value);
                      setVisibleCount(12);
                    }}
                  >
                    {tenantNames.map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </label>
                <label className="select-field">
                  <span className="sr-only">Sort rewards</span>
                  <SlidersHorizontal size={18} />
                  <select
                    value={sort}
                    onChange={(event) => {
                      setSort(event.target.value as typeof sort);
                      setVisibleCount(12);
                    }}
                  >
                    <option value="recommended">Recommended</option>
                    <option value="points-low">Points: low to high</option>
                    <option value="points-high">Points: high to low</option>
                    <option value="expiring">Expiring soon</option>
                    <option value="business">Business: A to Z</option>
                  </select>
                  <ChevronDown size={16} />
                </label>
              </div>

              <div className="category-tabs" role="group" aria-label="Filter rewards by category">
                {categoryNames.map((item) => (
                  <button
                    key={item}
                    className={category === item ? "is-active" : ""}
                    type="button"
                    onClick={() => {
                      setCategory(item);
                      setVisibleCount(12);
                    }}
                  >
                    {item}
                  </button>
                ))}
                {(query ||
                  category !== "All rewards" ||
                  tenant !== "All partners" ||
                  sort !== "recommended") && (
                  <button type="button" onClick={resetFilters}>
                    <X size={15} /> Clear filters
                  </button>
                )}
              </div>
            </div>

            {rewardsQuery.isError ? (
              <div className="empty-state">
                <Alert>{messageFromError(rewardsQuery.error)}</Alert>
                <span>
                  <Gift />
                </span>
                <h3>Rewards are temporarily unavailable</h3>
                <p>We could not reach the live catalogue. Please try again shortly.</p>
                <button
                  className="button button--outline"
                  type="button"
                  onClick={() => void rewardsQuery.refetch()}
                >
                  Retry catalogue
                </button>
              </div>
            ) : rewardsQuery.isLoading ? (
              <LoadingBlock label="Gathering rewards" />
            ) : filteredRewards.length > 0 ? (
              <>
                <div className="reward-grid">
                  {filteredRewards.slice(0, visibleCount).map((reward) => (
                    <RewardCard
                      key={reward.publicId}
                      reward={reward}
                      user={session.data}
                      pending={
                        redeemMutation.isPending &&
                        redeemMutation.variables.reward.publicId === reward.publicId
                      }
                      onRedeem={setRewardToConfirm}
                    />
                  ))}
                </div>
                {visibleCount < filteredRewards.length && (
                  <div className="center-action">
                    <button
                      className="button button--outline"
                      type="button"
                      onClick={() => {
                        setVisibleCount((count) => count + 12);
                      }}
                    >
                      Show more rewards <ChevronDown size={18} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <span>
                  <SlidersHorizontal />
                </span>
                <h3>No available rewards match those filters</h3>
                <p>Try another business or category, or clear your search.</p>
                <button className="button button--outline" type="button" onClick={resetFilters}>
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      {rewardToConfirm && (
        <RewardTermsDialog
          reward={rewardToConfirm}
          pending={redeemMutation.isPending}
          close={() => {
            setRewardToConfirm(null);
          }}
          confirm={(mechanismSelectionId) => {
            redeemMutation.mutate(
              mechanismSelectionId
                ? { reward: rewardToConfirm, mechanismSelectionId }
                : { reward: rewardToConfirm },
            );
          }}
        />
      )}
      {redemption && (
        <RedemptionDialog
          redemption={redemption}
          close={() => {
            setRedemption(null);
          }}
        />
      )}
    </PublicFrame>
  );
}

const loyaltyJourneySteps = [
  {
    id: "earn",
    label: "Earn",
    kicker: "At checkout",
    title: "A visit becomes value.",
    detail:
      "A secure member scan connects the purchase to the right business balance and tier progress.",
    highlight: "+125",
    meta: "Aadi Coffee Shop Reward Points",
    icon: <ScanLine />,
  },
  {
    id: "unlock",
    label: "Unlock",
    kicker: "Right on time",
    title: "A useful perk becomes ready.",
    detail:
      "Milestones and eligible offers turn into clear rewards without making customers track the math.",
    highlight: "Ready",
    meta: "Complimentary coffee unlocked",
    icon: <Gift />,
  },
  {
    id: "redeem",
    label: "Redeem",
    kicker: "One secure scan",
    title: "Value is simple to use.",
    detail:
      "The customer reveals a fresh QR only when the issuing business is ready to complete the redemption.",
    highlight: "1× QR",
    meta: "Short-lived and business-bound",
    icon: <QrCode />,
  },
  {
    id: "grow",
    label: "Grow",
    kicker: "Across the network",
    title: "Every eligible visit builds status.",
    detail:
      "Tier Points move members from Mero to Plus, Gold, and Elite while spendable balances stay separate.",
    highlight: "Gold",
    meta: "550 Tier Points to Elite",
    icon: <Crown />,
  },
] as const;

function LoyaltyJourney() {
  const [activeStep, setActiveStep] = useState(0);
  const [journeyPaused, setJourneyPaused] = useState(false);
  const step = loyaltyJourneySteps[activeStep] ?? loyaltyJourneySteps[0];

  useEffect(() => {
    if (journeyPaused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setActiveStep((current) => (current + 1) % loyaltyJourneySteps.length);
    }, 4500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [activeStep, journeyPaused]);

  return (
    <section
      className="loyalty-journey"
      aria-labelledby="loyalty-journey-title"
      onFocusCapture={() => {
        setJourneyPaused(true);
      }}
      onBlurCapture={() => {
        setJourneyPaused(false);
      }}
    >
      <div className="container loyalty-journey__grid">
        <div className="loyalty-journey__copy">
          <p className="honors-eyebrow">See loyalty in motion</p>
          <h2 id="loyalty-journey-title">One visit. A clearer reason to return.</h2>
          <p>
            Follow a Mero Perks moment from checkout to the next visit. The journey advances
            automatically, or choose any step to explore it.
          </p>
          <div className="loyalty-journey__tabs" role="group" aria-label="Loyalty journey">
            {loyaltyJourneySteps.map((item, index) => (
              <button
                key={item.id}
                className={activeStep === index ? "is-active" : ""}
                type="button"
                aria-pressed={activeStep === index}
                onClick={() => {
                  setActiveStep(index);
                }}
              >
                <span>0{index + 1}</span>
                {item.icon}
                <strong>{item.label}</strong>
              </button>
            ))}
          </div>
        </div>
        <div
          className={`loyalty-journey__stage loyalty-journey__stage--${step.id}`}
          onMouseEnter={() => {
            setJourneyPaused(true);
          }}
          onMouseLeave={() => {
            setJourneyPaused(false);
          }}
        >
          <div className="loyalty-journey__orbit" aria-hidden="true" />
          <article className="loyalty-journey__card">
            <div className="loyalty-journey__card-head">
              <span>{step.icon}</span>
              <small>{step.kicker}</small>
            </div>
            <strong>{step.highlight}</strong>
            <p>{step.meta}</p>
            <div className={`loyalty-journey__graphic loyalty-journey__graphic--${step.id}`}>
              {step.id === "earn" && (
                <div className="journey-receipt" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <b>+125</b>
                </div>
              )}
              {step.id === "unlock" && (
                <div className="journey-ticket" aria-hidden="true">
                  <Gift />
                  <span>Free coffee</span>
                  <small>Ready to use</small>
                </div>
              )}
              {step.id === "redeem" && (
                <div className="journey-qr" aria-hidden="true">
                  <QrCode />
                  <i />
                  <small>One-time secure scan</small>
                </div>
              )}
              {step.id === "grow" && (
                <div className="journey-tiers" aria-hidden="true">
                  <i />
                  <i />
                  <i className="is-current" />
                  <i />
                  <span>Mero Gold</span>
                </div>
              )}
            </div>
          </article>
          <div className="loyalty-journey__message">
            <span>{step.label}</span>
            <h3>{step.title}</h3>
            <p>{step.detail}</p>
          </div>
          <div className="loyalty-journey__progress" aria-hidden="true">
            <i
              style={{
                width: `${String(((activeStep + 1) / loyaltyJourneySteps.length) * 100)}%`,
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function PublicHome() {
  const rewardsQuery = useQuery({ queryKey: publicRewardsKey, queryFn: fetchPublicRewards });
  const tiersQuery = useQuery({ queryKey: ["public-tiers"], queryFn: fetchPublicTierProgram });
  const session = useSession();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All rewards");
  const [tenant, setTenant] = useState("All partners");
  const [visibleCount, setVisibleCount] = useState(6);
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [rewardToConfirm, setRewardToConfirm] = useState<Reward | null>(null);
  const [redeemError, setRedeemError] = useState("");

  const redeemMutation = useMutation({
    mutationFn: ({
      reward,
      mechanismSelectionId,
    }: {
      reward: Reward;
      mechanismSelectionId?: string;
    }) => redeemReward(reward.publicId, mechanismSelectionId),
    onSuccess: async (value) => {
      setRedemption(value);
      setRewardToConfirm(null);
      setRedeemError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer-redemptions"] }),
        queryClient.invalidateQueries({ queryKey: ["customer-accounts"] }),
        queryClient.invalidateQueries({ queryKey: sessionKey }),
      ]);
    },
    onError: (error) => {
      setRedeemError(messageFromError(error));
    },
  });

  const rewards = rewardsQuery.data ?? [];
  const tenantNames = useMemo(
    () => ["All partners", ...new Set(rewards.map((reward) => reward.tenantName))],
    [rewards],
  );
  const categoryNames = useMemo(
    () => ["All rewards", ...new Set(rewards.map((reward) => reward.category))],
    [rewards],
  );
  const filteredRewards = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rewards.filter((reward) => {
      const matchesSearch =
        !needle ||
        `${reward.title} ${reward.description} ${reward.tenantName}`.toLowerCase().includes(needle);
      const matchesCategory = category === "All rewards" || reward.category === category;
      const matchesTenant = tenant === "All partners" || reward.tenantName === tenant;
      return matchesSearch && matchesCategory && matchesTenant && reward.available;
    });
  }, [category, query, rewards, tenant]);
  const livePartnerCount = Math.max(tenantNames.length - 1, 0);
  const tierPreview = tiersQuery.data?.tiers ?? [
    {
      publicId: "mero",
      name: "Mero",
      minimumTierPoints: 0,
      description: "Your membership starts here.",
      colorHex: "#F7F5F0",
      accentHex: "#C8102E",
      code: "MERO",
      benefits: [],
    },
    {
      publicId: "plus",
      name: "Mero Plus",
      minimumTierPoints: 1000,
      description: "More recognition as you explore.",
      colorHex: "#C8102E",
      accentHex: "#F5A623",
      code: "MERO_PLUS",
      benefits: [],
    },
    {
      publicId: "gold",
      name: "Mero Gold",
      minimumTierPoints: 3000,
      description: "Premium partner benefits.",
      colorHex: "#F5A623",
      accentHex: "#122937",
      code: "MERO_GOLD",
      benefits: [],
    },
    {
      publicId: "elite",
      name: "Mero Elite",
      minimumTierPoints: 7500,
      description: "Our highest platform status.",
      colorHex: "#122937",
      accentHex: "#F5A623",
      code: "MERO_ELITE",
      benefits: [],
    },
  ];

  const resetFilters = () => {
    setQuery("");
    setCategory("All rewards");
    setTenant("All partners");
    setVisibleCount(6);
  };

  return (
    <PublicFrame>
      <main className="landing-home">
        <section className="honors-hero" aria-labelledby="honors-hero-title">
          <div className="container honors-hero__layout">
            <div className="honors-hero__content">
              <p className="honors-eyebrow">
                <Sparkles size={15} /> Nepal&apos;s neighborhood rewards network
              </p>
              <h1 id="honors-hero-title">
                Make every visit <em>worth coming back for.</em>
              </h1>
              <p>
                One free membership connects the places you already love with points, useful
                rewards, shared Mero Credits, and a status that grows with you.
              </p>
              <div className="honors-hero__actions">
                <Link className="button button--primary button--large" to="/register">
                  Join free <ArrowRight size={18} />
                </Link>
                <Link className="button button--outline button--large" to="/business">
                  Mero Perks for business
                </Link>
              </div>
              <div className="honors-hero__stats" aria-label="Mero Perks network at a glance">
                <div>
                  <strong>{rewardsQuery.isLoading ? "—" : rewards.length}</strong>
                  <span>Live rewards</span>
                </div>
                <div>
                  <strong>{rewardsQuery.isLoading ? "—" : livePartnerCount}</strong>
                  <span>Local businesses</span>
                </div>
                <div>
                  <strong>4</strong>
                  <span>Member tiers</span>
                </div>
              </div>
            </div>
            <figure className="honors-hero__visual">
              <img
                src="/brand/marketing/hero-community.webp"
                alt="A Mero Perks member sharing coffee with her mother at a neighborhood cafe"
                width="1536"
                height="1024"
                fetchPriority="high"
              />
              <figcaption>
                <span>
                  <Gift size={18} /> Reward ready
                </span>
                <strong>Your next coffee could be on the house.</strong>
                <small>Available rewards stay together in your member wallet.</small>
              </figcaption>
              <div className="honors-hero__member-chip">
                <BadgeCheck size={18} /> One membership. More rewarding places.
              </div>
            </figure>
          </div>
        </section>

        <section className="loyalty-proof" aria-label="Mero Perks promise">
          <div className="container loyalty-proof__inner">
            <span>SHOP</span>
            <i />
            <span>DINE</span>
            <i />
            <span>STAY</span>
            <i />
            <span>EXPLORE</span>
            <i />
            <strong>REPEAT</strong>
          </div>
        </section>

        <section className="honors-intro" id="features" aria-labelledby="honors-intro-title">
          <div className="container honors-intro__heading">
            <p className="honors-eyebrow">Everything in one membership</p>
            <h2 id="honors-intro-title">Loyalty made useful for real life.</h2>
            <p>Simple enough for your daily coffee. Flexible enough for every place you love.</p>
          </div>
          <div className="container honors-benefits" aria-label="Mero Perks member benefits">
            <article>
              <span>
                <Store />
              </span>
              <h3>Earn locally</h3>
              <p>Collect Reward Points from each participating business you visit.</p>
            </article>
            <article>
              <span>
                <Gift />
              </span>
              <h3>Rewards ready</h3>
              <p>Keep available offers and secure one-time reward QR codes in one place.</p>
            </article>
            <article>
              <span>
                <WalletCards />
              </span>
              <h3>One member wallet</h3>
              <p>See Reward Points, Mero Credits, and purchase activity clearly.</p>
            </article>
            <article>
              <span>
                <Crown />
              </span>
              <h3>Status that grows</h3>
              <p>Move from Mero to Elite as eligible activity builds your Tier Points.</p>
            </article>
            <article>
              <span>
                <QrCode />
              </span>
              <h3>Scan securely</h3>
              <p>Short-lived, one-time QR codes help protect membership and reward redemption.</p>
            </article>
            <article>
              <span>
                <BadgeCheck />
              </span>
              <h3>Stay in control</h3>
              <p>Always know what you earned, where it works, and when it is ready to use.</p>
            </article>
          </div>
          <div className="container honors-network" aria-label="Mero Perks network at a glance">
            <div>
              <strong>{rewardsQuery.isLoading ? "—" : rewards.length}</strong>
              <span>rewards available</span>
            </div>
            <div>
              <strong>{rewardsQuery.isLoading ? "—" : livePartnerCount}</strong>
              <span>participating businesses</span>
            </div>
            <div>
              <strong>4</strong>
              <span>membership tiers</span>
            </div>
          </div>
        </section>

        <section id="reward-worlds" className="reward-worlds" aria-labelledby="reward-worlds-title">
          <div className="container reward-worlds__head">
            <div>
              <p className="honors-eyebrow">A reward for every kind of day</p>
              <h2 id="reward-worlds-title">Four places. Four different reasons to come back.</h2>
            </div>
            <p>
              Explore the businesses that make everyday life more rewarding, with a distinct
              experience built for each category.
            </p>
          </div>
          <div className="container reward-worlds__grid">
            <article className="reward-world reward-world--retail">
              <img
                src="/brand/marketing/category-retail.webp"
                alt="A customer discovering local fashion in a Kathmandu boutique"
                loading="lazy"
                decoding="async"
              />
              <span>01</span>
              <div>
                <small>SHOP</small>
                <h3>Find something worth returning for.</h3>
                <p>
                  Discover local style, useful finds, and rewards that make the next visit count.
                </p>
                <Link className="reward-world__action" to="/solutions/retail">
                  Explore retail <ArrowRight />
                </Link>
              </div>
            </article>
            <article className="reward-world reward-world--dining">
              <img
                src="/brand/marketing/category-dining.webp"
                alt="Friends enjoying dinner at a local Nepalese restaurant"
                loading="lazy"
                decoding="async"
              />
              <span>02</span>
              <div>
                <small>DINE</small>
                <h3>Turn a good meal into the next one.</h3>
                <p>Earn something extra whenever you gather, celebrate, or simply eat well.</p>
                <Link className="reward-world__action" to="/solutions/food-and-beverage">
                  Explore dining <ArrowRight />
                </Link>
              </div>
            </article>
            <article className="reward-world reward-world--cafe">
              <img
                src="/brand/marketing/category-cafe.webp"
                alt="A barista serving coffee to a Mero Perks customer"
                loading="lazy"
                decoding="async"
              />
              <span>03</span>
              <div>
                <small>CAFÉ</small>
                <h3>Make the daily stop feel special.</h3>
                <p>
                  Let every coffee run, quick catch-up, and familiar order move you closer to a
                  perk.
                </p>
                <Link className="reward-world__action" to="/rewards">
                  Browse cafe perks <ArrowRight />
                </Link>
              </div>
            </article>
            <article className="reward-world reward-world--stay">
              <img
                src="/brand/marketing/category-hospitality.webp"
                alt="Guests arriving at a boutique hotel in Nepal"
                loading="lazy"
                decoding="async"
              />
              <span>04</span>
              <div>
                <small>STAY</small>
                <h3>Remember the welcome after checkout.</h3>
                <p>
                  Turn memorable stays and warm hospitality into reasons to plan the next escape.
                </p>
                <Link className="reward-world__action" to="/solutions/hospitality">
                  Explore hospitality <ArrowRight />
                </Link>
              </div>
            </article>
          </div>
        </section>

        <LoyaltyJourney />

        <section className="honors-rewards" id="rewards" aria-labelledby="honors-rewards-title">
          <div className="container">
            <div className="honors-rewards__head">
              <div>
                <p className="honors-eyebrow">Live reward marketplace</p>
                <h2 id="honors-rewards-title">A good reason to go back.</h2>
                <p>
                  Explore current offers from participating businesses. Sign in only when you are
                  ready to redeem.
                </p>
              </div>
              <div className="honors-rewards__count">
                <strong>{filteredRewards.length}</strong>
                <span>reward{filteredRewards.length === 1 ? "" : "s"} available now</span>
              </div>
            </div>

            {redeemError && <Alert>{redeemError}</Alert>}

            <div className="honors-reward-console">
              <div className="catalog-tools">
                <label className="search-field">
                  <span className="sr-only">Search rewards</span>
                  <Search size={19} />
                  <input
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setVisibleCount(6);
                    }}
                    placeholder="Search rewards or partners"
                  />
                </label>
                <label className="select-field">
                  <span className="sr-only">Filter by partner</span>
                  <Store size={18} />
                  <select
                    value={tenant}
                    onChange={(event) => {
                      setTenant(event.target.value);
                      setVisibleCount(6);
                    }}
                  >
                    {tenantNames.map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} />
                </label>
              </div>

              <div className="category-tabs" role="group" aria-label="Filter rewards by category">
                {categoryNames.map((item) => (
                  <button
                    key={item}
                    className={category === item ? "is-active" : ""}
                    type="button"
                    onClick={() => {
                      setCategory(item);
                      setVisibleCount(6);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {rewardsQuery.isError ? (
              <div className="empty-state">
                <Alert>{messageFromError(rewardsQuery.error)}</Alert>
                <span>
                  <Gift />
                </span>
                <h3>Rewards are temporarily unavailable</h3>
                <p>
                  We could not reach the live catalogue. Please retry to see current partner
                  rewards.
                </p>
                <button
                  className="button button--outline"
                  type="button"
                  onClick={() => {
                    void rewardsQuery.refetch();
                  }}
                >
                  Retry catalogue
                </button>
              </div>
            ) : rewardsQuery.isLoading ? (
              <LoadingBlock label="Gathering rewards" />
            ) : filteredRewards.length > 0 ? (
              <>
                <div className="reward-grid">
                  {filteredRewards.slice(0, visibleCount).map((reward) => (
                    <RewardCard
                      key={reward.publicId}
                      reward={reward}
                      user={session.data}
                      pending={
                        redeemMutation.isPending &&
                        redeemMutation.variables.reward.publicId === reward.publicId
                      }
                      onRedeem={setRewardToConfirm}
                    />
                  ))}
                </div>
                {visibleCount < filteredRewards.length && (
                  <div className="center-action">
                    <button
                      className="button button--outline"
                      type="button"
                      onClick={() => {
                        setVisibleCount((count) => count + 6);
                      }}
                    >
                      Show more rewards <ChevronDown size={18} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <span>
                  <SlidersHorizontal />
                </span>
                <h3>No rewards match those filters</h3>
                <p>Try another partner or category, or clear your search.</p>
                <button className="button button--outline" type="button" onClick={resetFilters}>
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="honors-points" id="how-it-works" aria-labelledby="honors-points-title">
          <div className="container honors-points__heading">
            <p className="honors-eyebrow">Three ways loyalty works</p>
            <h2 id="honors-points-title">Earn here. Use it your way.</h2>
            <p>
              Mero Perks keeps every kind of value easy to understand—so you always know what you
              earned, where it works, and what comes next.
            </p>
          </div>

          <div className="container honors-points__stories">
            <article className="honors-story honors-story--reward">
              <div className="honors-story__visual honors-story__visual--places" aria-hidden="true">
                <img src="/brand/marketing/category-cafe.webp" alt="" loading="lazy" />
                <img src="/brand/marketing/category-dining.webp" alt="" loading="lazy" />
              </div>
              <div className="honors-story__copy">
                <span className="honors-story__number">01</span>
                <p className="honors-eyebrow">Business Reward Points</p>
                <h3>Every business keeps its rewards personal.</h3>
                <p>
                  Earn points from a business and redeem them with that same business. Your wallet
                  shows each balance separately, exactly where it belongs.
                </p>
                <a href="#rewards">
                  See participating rewards <ArrowRight size={17} />
                </a>
              </div>
            </article>

            <article className="honors-story honors-story--credits">
              <div className="honors-story__copy">
                <span className="honors-story__number">02</span>
                <p className="honors-eyebrow">Mero Credits</p>
                <h3>One balance across participating places.</h3>
                <p>
                  Mero Credits are platform-wide value. Earn them through eligible activity and use
                  them at businesses that accept Mero Credits.
                </p>
                <Link to="/register">
                  Create your member wallet <ArrowRight size={17} />
                </Link>
              </div>
              <div
                className="honors-story__visual honors-story__visual--wallet"
                aria-label="Illustrative Mero Perks wallet"
              >
                <div className="honors-wallet__brand">
                  <img src="/brand/mero-brand-logo.png" alt="" />
                  <span>
                    MERO <b>PERKS</b>
                  </span>
                </div>
                <small>AVAILABLE MERO CREDITS</small>
                <strong>50</strong>
                <p>Ready at participating businesses</p>
                <div>
                  <ShieldCheck size={18} /> Protected in your member wallet
                </div>
              </div>
            </article>

            <article className="honors-story honors-story--status">
              <div className="honors-story__visual honors-story__visual--status" aria-hidden="true">
                <Crown />
                <span>MERO GOLD</span>
                <div>
                  <i style={{ width: "82%" }} />
                </div>
                <p>550 Tier Points to Elite</p>
              </div>
              <div className="honors-story__copy">
                <span className="honors-story__number">03</span>
                <p className="honors-eyebrow">Mero Tier Points</p>
                <h3>Your activity builds status—not spending money.</h3>
                <p>
                  Tier Points track eligible activity across the Mero Perks network. They are never
                  redeemed; they simply move you toward more recognition.
                </p>
                <a href="#tiers">
                  Explore membership tiers <ArrowRight size={17} />
                </a>
              </div>
            </article>
          </div>
        </section>

        <section className="honors-tiers" id="tiers" aria-labelledby="honors-tiers-title">
          <div className="container honors-tiers__head">
            <p className="honors-eyebrow">Membership that grows with you</p>
            <h2 id="honors-tiers-title">Four tiers. One rewarding journey.</h2>
            <p>
              Start as a Mero member, then build Tier Points across the network to reach Plus, Gold,
              and Elite.
            </p>
          </div>
          <div className="container honors-tier-track">
            {tierPreview.map((tier, index) => (
              <article className="honors-tier" key={tier.publicId}>
                <PlatformTierCard tierCode={tier.code} tierName={tier.name} />
                <div className="honors-tier__label">
                  <span>0{index + 1}</span>
                  <div>
                    <strong>{tier.name}</strong>
                    <small>
                      {index === 0 ? (
                        "Your journey begins"
                      ) : (
                        <>
                          {formatPoints(tier.minimumTierPoints)} <span>Tier Points</span>
                        </>
                      )}
                    </small>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="honors-partner">
          <div className="honors-partner__image">
            <img
              src="/brand/marketing/category-retail.webp"
              alt="A participating local retail business"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="honors-partner__copy">
            <div>
              <p className="honors-eyebrow">Mero Perks for business</p>
              <h2>Turn first visits into familiar faces.</h2>
              <p>
                Launch earning rules, offers, tiers, and secure checkout tools designed around the
                way your business actually works.
              </p>
              <Link className="button button--primary button--large" to="/register/tenant">
                Become a Mero partner <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        <section className="honors-final">
          <div className="container honors-final__inner">
            <div>
              <p className="honors-eyebrow">Ready for more?</p>
              <h2>Make your next visit count.</h2>
              <p>Join once, discover nearby businesses, and keep every reward within reach.</p>
            </div>
            <Link className="button button--light button--large" to="/register">
              Join Mero Perks <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
      {rewardToConfirm && (
        <RewardTermsDialog
          reward={rewardToConfirm}
          pending={redeemMutation.isPending}
          close={() => {
            setRewardToConfirm(null);
          }}
          confirm={(mechanismSelectionId) => {
            redeemMutation.mutate(
              mechanismSelectionId
                ? { reward: rewardToConfirm, mechanismSelectionId }
                : { reward: rewardToConfirm },
            );
          }}
        />
      )}
      {redemption && (
        <RedemptionDialog
          redemption={redemption}
          close={() => {
            setRedemption(null);
          }}
        />
      )}
    </PublicFrame>
  );
}

type MarketingPageKey =
  | "business"
  | "rewards"
  | "wallet"
  | "tiers"
  | "credits"
  | "quick-offers"
  | "checkout"
  | "customer-insights"
  | "retail"
  | "food-and-beverage"
  | "hospitality"
  | "small-business"
  | "how-it-works"
  | "member-benefits"
  | "pricing"
  | "faq";

interface MarketingPageDefinition {
  eyebrow: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  audience: "member" | "business";
  metrics: Array<{ value: string; label: string }>;
  features: Array<{ title: string; detail: string; icon: ReactNode }>;
  steps: Array<{ title: string; detail: string }>;
}

const memberCoreFeatures: MarketingPageDefinition["features"] = [
  {
    title: "Clear by design",
    detail: "See what you earned, where it works, and when it is ready to use.",
    icon: <BadgeCheck />,
  },
  {
    title: "Secure at checkout",
    detail: "Short-lived QR codes protect membership and reward redemption.",
    icon: <QrCode />,
  },
  {
    title: "Always connected",
    detail: "Web and mobile experiences stay in sync after earning or redemption.",
    icon: <RefreshCw />,
  },
];

const businessCoreFeatures: MarketingPageDefinition["features"] = [
  {
    title: "Rules you control",
    detail: "Set earning and redemption value around your real margins and goals.",
    icon: <Settings2 />,
  },
  {
    title: "Offers people understand",
    detail: "Create points, discounts, punch cards, visit rewards, and item offers.",
    icon: <Gift />,
  },
  {
    title: "A checkout built for loyalty",
    detail: "Identify members, apply value, and award the correct balance in one flow.",
    icon: <ScanLine />,
  },
];

const marketingPages: Record<MarketingPageKey, MarketingPageDefinition> = {
  business: {
    eyebrow: "Mero Perks for business",
    title: "Turn everyday customers into familiar faces.",
    summary:
      "Launch a loyalty program that fits your business, from earning rules and secure checkout to quick offers, customer groups, and useful reporting.",
    image: "/brand/marketing/topic-business.webp",
    imageAlt: "A neighborhood business owner welcoming regular customers",
    audience: "business",
    metrics: [
      { value: "3", label: "plans to grow with" },
      { value: "1", label: "connected workspace" },
      { value: "24/7", label: "reward discovery" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Create your business",
        detail: "Verify your email, business details, PAN, and location.",
      },
      {
        title: "Set the value",
        detail: "Use the guided setup to publish earning and redemption rules.",
      },
      {
        title: "Welcome customers back",
        detail: "Scan members, run offers, and learn what drives another visit.",
      },
    ],
  },
  rewards: {
    eyebrow: "Rewards marketplace",
    title: "A better reason to choose local.",
    summary:
      "Discover useful rewards from participating businesses before signing in, then keep every issued reward securely in one place.",
    image: "/brand/marketing/topic-rewards.webp",
    imageAlt: "A Mero Perks member delighting in a newly unlocked cafe reward",
    audience: "member",
    metrics: [
      { value: "Live", label: "availability" },
      { value: "Local", label: "business offers" },
      { value: "1×", label: "secure redemption" },
    ],
    features: memberCoreFeatures,
    steps: [
      {
        title: "Explore openly",
        detail: "Browse participating businesses and live offers without an account.",
      },
      {
        title: "Choose your reward",
        detail: "Review value, eligibility, location, and expiry before redeeming.",
      },
      {
        title: "Show a fresh QR",
        detail: "Open the reward only when the issuing business is ready to scan it.",
      },
    ],
  },
  wallet: {
    eyebrow: "Member wallet",
    title: "Every balance, clearly in its place.",
    summary:
      "Your wallet brings together business-specific Reward Points, Mero Credits, purchase history, and the value available for your next visit.",
    image: "/brand/marketing/topic-wallet.webp",
    imageAlt: "A Mero Perks member wallet with reward cards and tokens",
    audience: "member",
    metrics: [
      { value: "1", label: "member wallet" },
      { value: "Real time", label: "balance updates" },
      { value: "Separate", label: "business points" },
    ],
    features: memberCoreFeatures,
    steps: [
      {
        title: "Earn",
        detail: "A verified purchase adds the correct points after redemptions are applied.",
      },
      {
        title: "Understand",
        detail: "See each business balance and its redemption value separately.",
      },
      {
        title: "Use",
        detail: "Reveal a secure member QR when the business is ready to check out.",
      },
    ],
  },
  tiers: {
    eyebrow: "Mero membership tiers",
    title: "Recognition that grows across the network.",
    summary:
      "Tier Points measure eligible activity without becoming spendable currency, moving members through Mero, Plus, Gold, and Elite.",
    image: "/brand/marketing/topic-tiers.webp",
    imageAlt: "Four premium Mero Perks tier cards progressing from Mero to Elite",
    audience: "member",
    metrics: [
      { value: "4", label: "membership tiers" },
      { value: "1", label: "network status" },
      { value: "0", label: "Tier Points redeemed" },
    ],
    features: memberCoreFeatures,
    steps: [
      {
        title: "Start at Mero",
        detail: "Every verified member begins with a clear place in the program.",
      },
      {
        title: "Build Tier Points",
        detail: "Eligible purchases and platform activity move progress forward.",
      },
      {
        title: "Reach new status",
        detail: "Plus, Gold, and Elite make recognition visible everywhere.",
      },
    ],
  },
  credits: {
    eyebrow: "Mero Credits",
    title: "One balance across participating places.",
    summary:
      "Mero Credits are platform value earned through eligible events and accepted only where a business has enabled them.",
    image: "/brand/marketing/topic-credits.webp",
    imageAlt: "Mero Credits connecting one member wallet to participating places",
    audience: "member",
    metrics: [
      { value: "1", label: "platform balance" },
      { value: "Flexible", label: "earning events" },
      { value: "Visible", label: "acceptance rules" },
    ],
    features: memberCoreFeatures,
    steps: [
      {
        title: "Earn credits",
        detail: "Active platform rules award credits for qualifying events.",
      },
      {
        title: "Find acceptance",
        detail: "Your wallet shows participating businesses and usable value.",
      },
      {
        title: "Apply at checkout",
        detail: "Choose all or part of your balance before the final amount is calculated.",
      },
    ],
  },
  "quick-offers": {
    eyebrow: "Quick offers",
    title: "Launch the kind of offer customers instantly understand.",
    summary:
      "Publish simple discounts, buy-one-get-one offers, visit rewards, item-specific benefits, and digital punch cards with clear limits.",
    image: "/brand/marketing/topic-quick-offers.webp",
    imageAlt: "Cafe owners photographing an item for a new quick offer",
    audience: "business",
    metrics: [
      { value: "8+", label: "offer mechanics" },
      { value: "Item", label: "inventory targeting" },
      { value: "Fast", label: "publish workflow" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Choose a mechanic",
        detail: "Start with a proven offer structure that fits your objective.",
      },
      {
        title: "Define eligibility",
        detail: "Select items, visits, dates, locations, tiers, and limits.",
      },
      {
        title: "Publish with confidence",
        detail: "Customers see the offer and its exact terms in the marketplace.",
      },
    ],
  },
  checkout: {
    eyebrow: "Checkout & redemption",
    title: "One scan. One clear transaction.",
    summary:
      "Identify a member, see business-specific balances, apply points or Mero Credits, and calculate earnings only on the amount actually paid.",
    image: "/brand/marketing/topic-checkout.webp",
    imageAlt: "A business owner securely scanning a member QR at checkout",
    audience: "business",
    metrics: [
      { value: "1", label: "checkout flow" },
      { value: "QR", label: "secure identification" },
      { value: "Live", label: "balance updates" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Enter the purchase",
        detail: "Start with the total and select the correct business location.",
      },
      {
        title: "Scan the member",
        detail: "See member identity, tier, points, credits, and eligible rewards.",
      },
      {
        title: "Complete once",
        detail: "Redemptions, payment, earnings, and receipt history update together.",
      },
    ],
  },
  "customer-insights": {
    eyebrow: "Customer insights",
    title: "See the relationships behind the transactions.",
    summary:
      "Follow return activity, reward demand, liabilities, campaigns, and member segments without losing the audit trail behind each result.",
    image: "/brand/marketing/topic-analytics-v2.webp",
    imageAlt: "A cafe team reviewing Mero Perks customer insights",
    audience: "business",
    metrics: [
      { value: "Live", label: "dashboard" },
      { value: "Audited", label: "activity" },
      { value: "Useful", label: "segments" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Measure activity",
        detail: "Review purchases, redemptions, points, and offer performance.",
      },
      {
        title: "Build a segment",
        detail: "Group customers using meaningful behavior and account signals.",
      },
      {
        title: "Act with purpose",
        detail: "Use the segment in a campaign, automation, or next offer.",
      },
    ],
  },
  retail: {
    eyebrow: "Mero Perks for retail",
    title: "Reward the purchase—and the return visit.",
    summary:
      "Connect offers to selected products or brands, redeem business points at checkout, and make every condition clear before customers arrive.",
    image: "/brand/marketing/category-retail.webp",
    imageAlt: "Retail shopping through Mero Perks",
    audience: "business",
    metrics: [
      { value: "SKU", label: "level targeting" },
      { value: "Multi", label: "location support" },
      { value: "Clear", label: "reward terms" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Add inventory",
        detail: "Create the products, categories, and brands used in offers.",
      },
      {
        title: "Target the offer",
        detail: "Limit benefits to exactly the items or collections intended.",
      },
      {
        title: "Apply at checkout",
        detail: "Confirm eligibility and calculate the correct customer payment.",
      },
    ],
  },
  "food-and-beverage": {
    eyebrow: "For cafes & restaurants",
    title: "Make the next order feel closer.",
    summary:
      "Run coffee punch cards, visit rewards, complimentary items, and point earning that remains easy for staff during a busy service.",
    image: "/brand/marketing/category-dining.webp",
    imageAlt: "Food and beverage rewards through Mero Perks",
    audience: "business",
    metrics: [
      { value: "Punch", label: "card mechanics" },
      { value: "Fast", label: "staff checkout" },
      { value: "Repeat", label: "visit focus" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Choose the habit",
        detail: "Reward visits, specific items, spend, or a combination.",
      },
      {
        title: "Set the milestone",
        detail: "Define how many qualifying purchases unlock the benefit.",
      },
      {
        title: "Issue automatically",
        detail: "The earned coupon appears when the verified milestone is reached.",
      },
    ],
  },
  hospitality: {
    eyebrow: "For hotels & stays",
    title: "Recognize guests beyond a single stay.",
    summary:
      "Create location-aware benefits, tier recognition, and rewards that connect eligible visits across your hospitality business.",
    image: "/brand/marketing/category-hospitality.webp",
    imageAlt: "Hotel stay rewards through Mero Perks",
    audience: "business",
    metrics: [
      { value: "Tier", label: "recognition" },
      { value: "Stay", label: "based rewards" },
      { value: "Multi", label: "location support" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Define the stay",
        detail: "Connect earning rules to eligible, completed guest transactions.",
      },
      {
        title: "Recognize status",
        detail: "See the member tier and applicable benefits at service time.",
      },
      {
        title: "Invite the return",
        detail: "Issue a relevant offer with a clear validity period and location scope.",
      },
    ],
  },
  "small-business": {
    eyebrow: "Built for small business",
    title: "Start with loyalty you can actually run.",
    summary:
      "Use a guided setup, practical offer templates, simple member scanning, and a Free plan that makes the first step easy.",
    image: "/brand/marketing/topic-small-business.webp",
    imageAlt: "A neighborhood kirana owner greeting a familiar customer",
    audience: "business",
    metrics: [
      { value: "Free", label: "plan available" },
      { value: "Guided", label: "setup" },
      { value: "Mobile", label: "business tools" },
    ],
    features: businessCoreFeatures,
    steps: [
      {
        title: "Join the network",
        detail: "Create and verify a business account from web or mobile.",
      },
      {
        title: "Follow the setup",
        detail: "The wizard builds earning, redemption, and your first offer.",
      },
      {
        title: "Serve from your phone",
        detail: "Scan members and manage the essentials without a separate terminal.",
      },
    ],
  },
  "how-it-works": {
    eyebrow: "How Mero Perks works",
    title: "Join once. Earn clearly. Use securely.",
    summary:
      "Mero Perks keeps business points, platform credits, tier progress, rewards, and purchase history understandable from the first visit.",
    image: "/brand/marketing/topic-how-it-works.webp",
    imageAlt: "A customer presenting a secure Mero Perks QR at a cafe checkout",
    audience: "member",
    metrics: [
      { value: "Free", label: "member account" },
      { value: "1", label: "digital card" },
      { value: "Every", label: "eligible visit" },
    ],
    features: memberCoreFeatures,
    steps: [
      {
        title: "Create your account",
        detail: "Verify your email and receive a secure Mero Perks member identity.",
      },
      {
        title: "Visit a partner",
        detail: "Show a fresh member QR when staff are ready to record the purchase.",
      },
      {
        title: "Enjoy the value",
        detail: "Track points, use rewards, and grow your tier across the network.",
      },
    ],
  },
  "member-benefits": {
    eyebrow: "Member benefits",
    title: "More than points. A better view of loyalty.",
    summary:
      "Discover offers publicly, manage separate business balances, use platform credits, and grow a recognizable tier with one account.",
    image: "/brand/marketing/topic-member-benefits.webp",
    imageAlt: "A Nepali family enjoying everyday Mero Perks member benefits together",
    audience: "member",
    metrics: [
      { value: "4", label: "tier levels" },
      { value: "1", label: "secure card" },
      { value: "Many", label: "rewarding places" },
    ],
    features: memberCoreFeatures,
    steps: [
      { title: "Explore", detail: "Find participating businesses and useful live offers." },
      {
        title: "Carry one card",
        detail: "Use the same secure member identity across the Mero Perks network.",
      },
      {
        title: "Stay informed",
        detail: "Notifications and history keep rewards, purchases, and progress visible.",
      },
    ],
  },
  pricing: {
    eyebrow: "Business pricing",
    title: "Start free. Add more when loyalty grows.",
    summary:
      "Choose a plan around the tools and scale your business needs today. Your subscription choice is visible during signup and in your account.",
    image: "/brand/marketing/topic-pricing.webp",
    imageAlt: "Three Mero Perks business plans represented by ascending service plaques",
    audience: "business",
    metrics: [
      { value: "Free", label: "Basic plan" },
      { value: "300", label: "Pro monthly" },
      { value: "500", label: "Master monthly" },
    ],
    features: businessCoreFeatures,
    steps: [
      { title: "Basic", detail: "Essential loyalty tools for a small business getting started." },
      { title: "Pro", detail: "Expanded offers, campaigns, insights, and operating capacity." },
      {
        title: "Master",
        detail: "The fullest feature set for businesses managing more locations and growth.",
      },
    ],
  },
  faq: {
    eyebrow: "Frequently asked questions",
    title: "Straight answers before you get started.",
    summary:
      "Learn how accounts, points, tiers, QR security, business approval, and subscriptions work across Mero Perks.",
    image: "/brand/marketing/topic-faq.webp",
    imageAlt: "A Mero Perks support specialist helping a small-business owner",
    audience: "member",
    metrics: [
      { value: "Free", label: "customer signup" },
      { value: "Verified", label: "email accounts" },
      { value: "Support", label: "when needed" },
    ],
    features: memberCoreFeatures,
    steps: [
      {
        title: "Can one email hold both account types?",
        detail:
          "Yes. A customer account and a Business Owner account may use the same email, with separate credentials and roles.",
      },
      {
        title: "Can points move between businesses?",
        detail:
          "No. Reward Points stay with the business that issued them; Mero Credits are the platform-wide balance.",
      },
      {
        title: "How are redemptions protected?",
        detail:
          "Member and reward QR codes are short-lived and checked by the server before any transaction is accepted.",
      },
    ],
  },
};

function MarketingPage({ pageKey }: { pageKey: MarketingPageKey }) {
  const page = marketingPages[pageKey];
  const businessAudience = page.audience === "business";
  return (
    <PublicFrame>
      <main
        className={`marketing-page marketing-page--${businessAudience ? "business" : "member"}`}
      >
        <section className="marketing-page__hero">
          <div className="container marketing-page__hero-grid">
            <div>
              <p className="marketing-kicker">{page.eyebrow}</p>
              <h1>{page.title}</h1>
              <p>{page.summary}</p>
              <div className="marketing-page__actions">
                <Link
                  className="button button--primary button--large"
                  to={businessAudience ? "/register/tenant" : "/register"}
                >
                  {businessAudience ? "Start your business account" : "Join Mero Perks free"}{" "}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  className="button button--outline button--large"
                  to={businessAudience ? "/login/tenant" : "/rewards"}
                >
                  {businessAudience ? "Business Owner login" : "Browse rewards"}
                </Link>
              </div>
            </div>
            <figure>
              <img
                src={page.image}
                alt={page.imageAlt}
                width="1536"
                height="1024"
                decoding="async"
              />
              <figcaption>
                <Sparkles size={17} /> Mero Perks · More places. Brighter days.
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="marketing-page__metrics" aria-label="Highlights">
          <div className="container">
            {page.metrics.map((metric) => (
              <div key={metric.label}>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </div>
            ))}
          </div>
        </section>

        {pageKey === "pricing" && (
          <section className="marketing-pricing" aria-labelledby="marketing-pricing-title">
            <div className="container">
              <header>
                <p className="marketing-kicker">Plans for every stage</p>
                <h2 id="marketing-pricing-title">Choose the workspace your business needs.</h2>
                <p>
                  Annual billing is shown alongside monthly pricing so the full commitment is always
                  clear.
                </p>
              </header>
              <div className="marketing-pricing__grid">
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <article className={plan.popular ? "is-popular" : ""} key={plan.code}>
                    {plan.popular && <span className="marketing-pricing__badge">Most popular</span>}
                    <p>{plan.name}</p>
                    <h3>{plan.monthlyPrice === 0 ? "Free" : `NPR ${String(plan.monthlyPrice)}`}</h3>
                    <small>
                      {plan.monthlyPrice === 0 ? (
                        "No subscription fee"
                      ) : (
                        <>
                          <span>per month</span> · NPR {String(plan.annualPrice)}{" "}
                          <span>billed yearly</span>
                        </>
                      )}
                    </small>
                    <p>{plan.tagline}</p>
                    <ul>
                      {plan.displayFeatures.map((feature) => (
                        <li key={feature}>
                          <Check size={16} /> {feature}
                        </li>
                      ))}
                    </ul>
                    <Link
                      className={`button ${plan.popular ? "button--primary" : "button--outline"} button--full`}
                      to={`/register/tenant?plan=${plan.code.toLowerCase()}`}
                    >
                      <span>Choose</span> {plan.name}
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {pageKey === "faq" && (
          <section className="marketing-faq" aria-labelledby="marketing-faq-title">
            <div className="container">
              <header>
                <p className="marketing-kicker">Helpful details</p>
                <h2 id="marketing-faq-title">What members and businesses ask most.</h2>
              </header>
              <div>
                {[
                  [
                    "Is a customer account free?",
                    "Yes. Customers can join Mero Perks, browse rewards, and use their member wallet without a subscription fee.",
                  ],
                  [
                    "Can I use the same email for customer and Business Owner accounts?",
                    "Yes. The two account types are separate, so the same email may be used once for each role.",
                  ],
                  [
                    "Can Reward Points be used at another business?",
                    "No. Reward Points belong to the issuing business. Mero Credits are the separate platform-wide balance accepted by enabled businesses.",
                  ],
                  [
                    "Does a business become active immediately?",
                    "A Business Owner verifies their email, then the application is reviewed and approved by a Super Admin before the business can go live.",
                  ],
                  [
                    "What happens if a QR is copied?",
                    "Member and reward QR codes expire quickly and are validated on the server. A reward redemption cannot be accepted twice.",
                  ],
                ].map(([question, answer]) => (
                  <details key={question}>
                    <summary>
                      {question}
                      <Plus size={18} />
                    </summary>
                    <p>{answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="marketing-page__features">
          <div className="container">
            <header>
              <p className="marketing-kicker">Designed for real life</p>
              <h2>Powerful where it matters. Simple where it should be.</h2>
            </header>
            <div className="marketing-page__feature-grid">
              {page.features.map((feature, index) => (
                <article key={feature.title}>
                  <span>{feature.icon}</span>
                  <small>0{index + 1}</small>
                  <h3>{feature.title}</h3>
                  <p>{feature.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="marketing-page__process">
          <div className="container marketing-page__process-grid">
            <div>
              <p className="marketing-kicker">How it comes together</p>
              <h2>A clear path from first step to lasting value.</h2>
              <p>{page.summary}</p>
            </div>
            <ol>
              {page.steps.map((step, index) => (
                <li key={step.title}>
                  <span>{index + 1}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="marketing-page__cta">
          <div className="container">
            <div>
              <p className="marketing-kicker">Ready when you are</p>
              <h2>
                {businessAudience
                  ? "Build loyalty around your business."
                  : "Make the next visit count."}
              </h2>
            </div>
            <Link
              className="button button--gold button--large"
              to={businessAudience ? "/register/tenant" : "/register"}
            >
              {businessAudience ? "Become a partner" : "Create your free account"}{" "}
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
    </PublicFrame>
  );
}

function BusinessPage() {
  return <MarketingPage pageKey="business" />;
}

const portalCopy: Record<
  Portal,
  { eyebrow: string; title: string; detail: string; icon: ReactNode }
> = {
  customer: {
    eyebrow: "Customer account",
    title: "Welcome back",
    detail: "Log in to see your partner balances and redeem rewards.",
    icon: <Gift />,
  },
  tenant: {
    eyebrow: "Business Owner workspace",
    title: "Welcome, partner",
    detail: "Manage rewards and customer engagement for your business.",
    icon: <Store />,
  },
  "super-admin": {
    eyebrow: "Platform administration",
    title: "Super admin login",
    detail: "Manage Mero Perks Business Owners and platform access.",
    icon: <ShieldCheck />,
  },
};

function AuthShell({
  children,
  visualTitle,
  visualDetail,
  visualImage,
  variant,
}: {
  children: ReactNode;
  visualTitle: string;
  visualDetail: string;
  visualImage?: string;
  variant?: Portal;
}) {
  return (
    <main className={`auth-shell${visualImage ? " auth-shell--photo" : ""}${variant ? ` auth-shell--${variant}` : ""}`}>
      <section
        className="auth-visual"
        style={
          visualImage
            ? ({ "--auth-visual-image": `url(${visualImage})` } as CSSProperties)
            : undefined
        }
      >
        <div className="auth-visual__brand">
          <Brand inverse />
        </div>
        <div className="auth-visual__copy">
          <span className="auth-visual__icon">
            <Sparkles />
          </span>
          <h2>{visualTitle}</h2>
          <p>{visualDetail}</p>
        </div>
        <div className="auth-visual__quote">
          <p>“Mero means mine—a loyalty experience that feels personal, wherever you shop.”</p>
          <span>Mero Perks</span>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-panel__toolbar">
          <div className="auth-panel__mobile-brand">
            <Brand />
          </div>
          <LanguageSwitch />
        </div>
        {children}
      </section>
    </main>
  );
}

function LoginPage({ portal }: { portal: Portal }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const copy = portalCopy[portal];
  const rememberedEmail = readRememberedLogin(portal, "email");
  const rememberedBusinessCode =
    portal === "tenant" ? readRememberedLogin(portal, "business-code") : "";
  const [email, setEmail] = useState(rememberedEmail);
  const [password, setPassword] = useState("");
  const [tenantSlug, setTenantSlug] = useState(rememberedBusinessCode);
  const [rememberEmail, setRememberEmail] = useState(Boolean(rememberedEmail));
  const [rememberBusinessCode, setRememberBusinessCode] = useState(Boolean(rememberedBusinessCode));
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const canRequestVerification = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const loginMutation = useMutation({ mutationFn: (input: LoginInput) => login(input) });
  const resendMutation = useMutation({ mutationFn: resendVerificationEmail });

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setNeedsVerification(false);
    setVerificationMessage("");
    try {
      const user = await loginMutation.mutateAsync({
        portal,
        email: email.trim(),
        password,
        ...(portal === "tenant" ? { tenantSlug: tenantSlug.trim() } : {}),
      });
      writeRememberedLogin(portal, "email", rememberEmail, email.trim());
      if (portal === "tenant") {
        writeRememberedLogin(portal, "business-code", rememberBusinessCode, tenantSlug.trim());
      }
      if (user) queryClient.setQueryData(sessionKey, user);
      else await queryClient.invalidateQueries({ queryKey: sessionKey });
      const resolved = user ?? queryClient.getQueryData<SessionUser | null>(sessionKey);
      const expected: SessionType =
        portal === "super-admin"
          ? "super_admin"
          : portal === "tenant"
            ? "tenant_staff"
            : "customer";
      const returnTo = new URLSearchParams(location.search).get("returnTo");
      const destination =
        portal === "customer" && returnTo?.startsWith("/join/")
          ? returnTo
          : roleHome(resolved?.sessionType ?? expected);
      await navigate(destination, { replace: true });
    } catch (caught) {
      setError(messageFromError(caught));
      setNeedsVerification(caught instanceof ApiError && caught.code === "EMAIL_NOT_VERIFIED");
    }
  };

  const resendVerification = async () => {
    setVerificationMessage("");
    try {
      await resendMutation.mutateAsync({
        email: email.trim(),
        portal: portal === "tenant" ? "tenant" : "customer",
      });
      setVerificationMessage(
        "If this email has an unverified account, a fresh verification link is on its way.",
      );
    } catch (caught) {
      setVerificationMessage(messageFromError(caught));
    }
  };

  return (
    <AuthShell
      variant={portal}
      visualTitle={portal === "tenant" ? "Run loyalty from the counter." : "Same places. More perks."}
      visualDetail={portal === "tenant" ? "Sign in to a focused point-of-sale workspace for member scans, checkout, offers, and business performance." : "Shop, dine, stay, and enjoy a shared rewards network made for real life."}
      visualImage={
        portal === "customer"
          ? "/brand/auth-customer.webp"
          : portal === "super-admin"
            ? "/brand/auth-super-admin.webp"
            : "/brand/business-owner-onboarding.webp"
      }
    >
      <div className={`auth-card${portal === "tenant" ? " auth-card--business-owner" : ""}`}>
        <Link className="back-link" to="/">
          <ArrowRight /> Back to rewards
        </Link>
        <span className="auth-card__icon">{copy.icon}</span>
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1>{copy.title}</h1>
        <p className="auth-intro">{copy.detail}</p>
        {error && <Alert>{error}</Alert>}
        <form className="form-stack" onSubmit={(event) => void handleSubmit(event)}>
          {portal === "tenant" && (
            <label>
              <span>Business Code</span>
              <div className="input-wrap">
                <Building2 />
                <input
                  required
                  autoComplete="organization"
                  value={tenantSlug}
                  onChange={(event) => {
                    setTenantSlug(event.target.value);
                  }}
                  placeholder="e.g. sunrisecafe"
                />
              </div>
              <small>Provided by your Mero Perks administrator.</small>
            </label>
          )}
          <label>
            <span>Email address</span>
            <div className="input-wrap">
              <Mail />
              <input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError("");
                  setNeedsVerification(false);
                  setVerificationMessage("");
                }}
                placeholder="you@example.com"
              />
            </div>
          </label>
          <label>
            <span>Password</span>
            <div className="input-wrap">
              <LockKeyhole />
              <input
                required
                minLength={12}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                placeholder="Enter your password"
              />
            </div>
          </label>
          <div
            className={`login-memory-options${portal === "tenant" ? " login-memory-options--two" : ""}`}
          >
            {portal === "tenant" && (
              <label className="check-field">
                <input
                  type="checkbox"
                  checked={rememberBusinessCode}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setRememberBusinessCode(checked);
                    if (!checked) {
                      writeRememberedLogin(portal, "business-code", false, "");
                    }
                  }}
                />
                <span>Remember business code</span>
              </label>
            )}
            <label className="check-field">
              <input
                type="checkbox"
                checked={rememberEmail}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setRememberEmail(checked);
                  if (!checked) writeRememberedLogin(portal, "email", false, "");
                }}
              />
              <span>{portal === "tenant" ? "Remember business email" : "Remember email"}</span>
            </label>
          </div>
          <button
            className="button button--primary button--full button--large"
            disabled={loginMutation.isPending}
            type="submit"
          >
            {loginMutation.isPending ? "Signing in…" : "Sign in"}
            <ArrowRight size={18} />
          </button>
        </form>
        {portal === "customer" && needsVerification && (
          <div
            className="verification-actions verification-actions--signin verification-actions--required"
            aria-live="polite"
          >
            <span>Your account still needs email verification.</span>
            <button
              className="button button--outline button--full"
              type="button"
              disabled={!canRequestVerification || resendMutation.isPending}
              onClick={() => void resendVerification()}
            >
              <Mail size={17} />
              {resendMutation.isPending ? "Requesting…" : "Resend verification email"}
            </button>
            {verificationMessage && <small role="status">{verificationMessage}</small>}
          </div>
        )}
        {portal === "customer" && (
          <p className="auth-switch">
            New to Mero Perks? <Link to="/register">Create a free account</Link>
          </p>
        )}
        {portal === "tenant" && (
          <p className="auth-switch">
            New business? <Link to="/register/tenant">Create a Business Owner account</Link>
          </p>
        )}
        <div className="portal-switch">
          <span>Other portals</span>
          <div>
            {portal !== "customer" && <Link to="/login/customer">Customer</Link>}
            {portal !== "tenant" && <Link to="/login/tenant">Business Owner</Link>}
          </div>
        </div>
      </div>
    </AuthShell>
  );
}

export function SuperAdminBootstrapForm({ cancel }: { cancel: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bootstrapSecret, setBootstrapSecret] = useState("");
  const [error, setError] = useState("");
  const mutation = useMutation({ mutationFn: bootstrapSuperAdmin });
  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      const user = await mutation.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        bootstrapSecret,
      });
      setPassword("");
      setBootstrapSecret("");
      if (user) queryClient.setQueryData(sessionKey, user);
      else await queryClient.invalidateQueries({ queryKey: sessionKey });
      await navigate("/super-admin", { replace: true });
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <AuthShell
      visualTitle="Secure initialization, built in."
      visualDetail="Create the first Mero Perks platform administrator before inviting any Business Owners."
    >
      <div className="auth-card auth-card--register">
        <button className="back-link back-link--button" type="button" onClick={cancel}>
          <ArrowRight /> Back to super admin login
        </button>
        <span className="auth-card__icon">
          <ShieldCheck />
        </span>
        <p className="eyebrow">One-time initialization</p>
        <h1>Create the first super admin</h1>
        <p className="auth-intro">
          This works only once. Enter the bootstrap secret configured securely in Cloudflare; it is
          sent only in the protected request header and is never stored by this page.
        </p>
        {error && <Alert>{error}</Alert>}
        <form className="form-stack" onSubmit={(event) => void handleSubmit(event)}>
          <div className="form-row">
            <label>
              <span>First name</span>
              <input
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                }}
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                }}
              />
            </label>
          </div>
          <label>
            <span>Admin email</span>
            <input
              required
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              placeholder="admin@example.com"
            />
          </label>
          <label>
            <span>Create Business Owner password</span>
            <input
              required
              type="password"
              minLength={12}
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              placeholder="At least 12 characters"
            />
          </label>
          <label>
            <span>Bootstrap secret</span>
            <div className="input-wrap">
              <LockKeyhole />
              <input
                required
                type="password"
                autoComplete="off"
                value={bootstrapSecret}
                onChange={(event) => {
                  setBootstrapSecret(event.target.value);
                }}
                placeholder="Configured in Cloudflare"
              />
            </div>
            <small>Must match the SUPER_ADMIN_BOOTSTRAP_SECRET Worker secret.</small>
          </label>
          <button
            className="button button--primary button--full button--large"
            disabled={mutation.isPending}
            type="submit"
          >
            {mutation.isPending ? "Initializing…" : "Create super admin"}
            <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </AuthShell>
  );
}

function RegisterPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthday, setBirthday] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [error, setError] = useState("");
  const [registration, setRegistration] = useState<Awaited<
    ReturnType<typeof registerCustomer>
  > | null>(null);
  const [resendMessage, setResendMessage] = useState("");
  const registerMutation = useMutation({ mutationFn: registerCustomer });
  const resendMutation = useMutation({ mutationFn: resendVerificationEmail });

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!accepted) {
      setError("Please accept the terms to create your account.");
      return;
    }
    try {
      const result = await registerMutation.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        termsAccepted: true,
        marketingEmail,
        ...(birthday ? { birthday } : {}),
      });
      setRegistration(result);
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };

  const resend = async () => {
    if (!registration) return;
    setResendMessage("");
    try {
      await resendMutation.mutateAsync({ email: registration.email, portal: "customer" });
      setResendMessage("A fresh verification link has been requested. Check your inbox.");
    } catch (caught) {
      setResendMessage(messageFromError(caught));
    }
  };

  if (registration) {
    const verified =
      !registration.verificationRequired || registration.delivery === "already_verified";
    return (
      <AuthShell
        visualTitle="One email. Every way to earn."
        visualDetail="Your verified Mero Perks identity can hold customer and business access securely."
      >
        <div className="auth-card verification-card">
          <span className="auth-card__icon">{verified ? <BadgeCheck /> : <Mail />}</span>
          <p className="eyebrow">{verified ? "ACCOUNT READY" : "VERIFY YOUR EMAIL"}</p>
          <h1>{verified ? "Your customer account is ready" : "Check your inbox"}</h1>
          <p className="auth-intro">
            {verified ? (
              <>
                <strong>{registration.email}</strong>{" "}
                <span>was already verified. Sign in with your existing Mero Perks password.</span>
              </>
            ) : (
              <>
                <span>We sent a secure activation link to</span>{" "}
                <strong>{registration.email}</strong>. <span>It expires in 24 hours.</span>
              </>
            )}
          </p>
          {registration.delivery === "temporarily_unavailable" && (
            <Alert>
              Your account was created, but the email provider could not accept the message yet. Use
              the resend button after the Resend key and sending domain are configured.
            </Alert>
          )}
          {!verified && (
            <button
              className="button button--quiet button--full"
              type="button"
              disabled={resendMutation.isPending}
              onClick={() => void resend()}
            >
              <RefreshCw size={17} />
              {resendMutation.isPending ? "Requesting…" : "Resend verification email"}
            </button>
          )}
          {resendMessage && (
            <p className="verification-message" role="status">
              {resendMessage}
            </p>
          )}
          <Link className="button button--primary button--full button--large" to="/login/customer">
            Continue to customer login <ArrowRight size={18} />
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      visualTitle="Rewards make life sweeter."
      visualDetail="Join once, discover offers from every participating business, and keep your rewards in one place."
    >
      <div className="auth-card auth-card--register">
        <Link className="back-link" to="/">
          <ArrowRight /> Back to rewards
        </Link>
        <p className="eyebrow">Free customer account</p>
        <h1>Start earning more from every visit</h1>
        <p className="auth-intro">It takes less than a minute. No card or fee required.</p>
        {error && <Alert>{error}</Alert>}
        <form className="form-stack" onSubmit={(event) => void handleSubmit(event)}>
          <div className="form-row">
            <label>
              <span>First name</span>
              <input
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                }}
                placeholder="Asha"
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                }}
                placeholder="Shrestha"
              />
            </label>
          </div>
          <label>
            <span>Email address</span>
            <div className="input-wrap">
              <Mail />
              <input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                placeholder="asha@example.com"
              />
            </div>
          </label>
          <label>
            <span>Create password</span>
            <div className="input-wrap">
              <LockKeyhole />
              <input
                required
                type="password"
                autoComplete="new-password"
                minLength={12}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                placeholder="At least 12 characters"
              />
            </div>
          </label>
          <label>
            <span>
              Birthday <small>(optional)</small>
            </span>
            <input
              type="date"
              autoComplete="bday"
              max={new Date().toISOString().slice(0, 10)}
              value={birthday}
              onChange={(event) => {
                setBirthday(event.target.value);
              }}
            />
            <small>
              Used to determine birthday reward eligibility. It is never shared publicly.
            </small>
          </label>
          <label className="check-field">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => {
                setAccepted(event.target.checked);
              }}
            />
            <span>I agree to the Mero Perks terms and acknowledge the privacy policy.</span>
          </label>
          <label className="check-field">
            <input
              type="checkbox"
              checked={marketingEmail}
              onChange={(event) => {
                setMarketingEmail(event.target.checked);
              }}
            />
            <span>Email me optional offers and perks. I can change this at any time.</span>
          </label>
          <button
            className="button button--primary button--full button--large"
            disabled={registerMutation.isPending}
            type="submit"
          >
            {registerMutation.isPending ? "Creating account…" : "Create my account"}
            <ArrowRight size={18} />
          </button>
        </form>
        <p className="auth-switch">
          Already have an account? <Link to="/login/customer">Log in</Link>
        </p>
      </div>
    </AuthShell>
  );
}

function SubscriptionPricingSelector({
  selectedPlan,
  billingCycle,
  onPlanChange,
  onBillingCycleChange,
}: {
  selectedPlan: SubscriptionPlanCode;
  billingCycle: SubscriptionBillingCycle;
  onPlanChange: (plan: SubscriptionPlanCode) => void;
  onBillingCycleChange: (cycle: SubscriptionBillingCycle) => void;
}) {
  return (
    <section className="signup-pricing" aria-label="Subscription plans">
      <div className="signup-pricing__heading">
        <span>Billing cycle</span>
        <div className="billing-toggle" role="group" aria-label="Billing cycle">
          {(["ANNUAL", "MONTHLY"] as const).map((cycle) => (
            <button
              key={cycle}
              type="button"
              className={billingCycle === cycle ? "is-active" : ""}
              aria-pressed={billingCycle === cycle}
              onClick={() => {
                onBillingCycleChange(cycle);
              }}
            >
              {cycle === "ANNUAL" ? "Annual" : "Monthly"}
            </button>
          ))}
        </div>
      </div>
      <div className="signup-pricing__grid">
        {SUBSCRIPTION_PLANS.map((plan) => {
          const selected = selectedPlan === plan.code;
          const price = billingCycle === "ANNUAL" ? plan.annualPrice : plan.monthlyPrice;
          return (
            <button
              className={`signup-plan ${selected ? "is-selected" : ""} ${plan.popular ? "is-popular" : ""}`}
              type="button"
              key={plan.code}
              aria-pressed={selected}
              onClick={() => {
                onPlanChange(plan.code);
              }}
            >
              <span className="signup-plan__topline">
                <strong>{plan.name}</strong>
                {plan.popular && <small>POPULAR</small>}
              </span>
              <span className="signup-plan__price">
                {price === 0 ? "FREE" : `Rs. ${price.toLocaleString()}`}
                {price > 0 && <small>/{billingCycle === "ANNUAL" ? "year" : "month"}</small>}
              </span>
              {plan.trialDays > 0 && <span className="signup-plan__trial">7-day free trial</span>}
              <span className="signup-plan__tagline">{plan.tagline}</span>
              <span className="signup-plan__features">
                {plan.displayFeatures.map((feature) => (
                  <span key={feature}>
                    <Check size={15} /> {feature}
                  </span>
                ))}
              </span>
              <span className="signup-plan__select">
                {selected ? (
                  <>
                    <BadgeCheck size={18} /> Selected
                  </>
                ) : (
                  "Choose plan"
                )}
              </span>
            </button>
          );
        })}
      </div>
      <p className="signup-pricing__note">
        Paid plan selection records your preferred billing cycle. No payment is collected during
        application review.
      </p>
    </section>
  );
}

type TenantSignupStep = 1 | 2 | 3 | 4;

const TENANT_SIGNUP_STEPS: Array<{
  id: TenantSignupStep;
  title: string;
  detail: string;
}> = [
  {
    id: 1,
    title: "Choose your plan",
    detail: "Select the plan and billing cycle that fit your business.",
  },
  {
    id: 2,
    title: "Business identity",
    detail: "Tell us how your business is registered and known to customers.",
  },
  {
    id: 3,
    title: "Business contact & location",
    detail: "Add the contact and operating details customers can rely on.",
  },
  {
    id: 4,
    title: "Owner login & verification",
    detail: "Secure the owner account and confirm the application.",
  },
];

function TenantRegisterPage() {
  type Draft = Omit<TenantRegistrationInput, "termsAccepted">;
  const initial: Draft = {
    slug: "",
    businessName: "",
    legalName: "",
    businessType: "Retail",
    businessEmail: "",
    businessPhone: "",
    website: "",
    defaultCurrency: "NPR",
    defaultTimezone: "Asia/Kathmandu",
    country: "Nepal",
    address: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    businessPanNumber: "",
    ownerEmail: "",
    ownerFirstName: "",
    ownerLastName: "",
    initialPassword: "",
    subscriptionPlan: "BASIC",
    billingCycle: "ANNUAL",
  };
  const [form, setForm] = useState<Draft>(initial);
  const [locationPin, setLocationPin] = useState<BusinessLocationPin | null>(null);
  const [step, setStep] = useState<TenantSignupStep>(1);
  const [accepted, setAccepted] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState("");
  const [panCheck, setPanCheck] = useState<{
    pan: string;
    status: "idle" | "checking" | "available" | "unavailable" | "error";
    message: string;
  }>({ pan: "", status: "idle", message: "" });
  const [application, setApplication] = useState<Awaited<ReturnType<typeof registerTenant>> | null>(
    null,
  );
  const [resendMessage, setResendMessage] = useState("");
  const mutation = useMutation({ mutationFn: registerTenant });
  const resendMutation = useMutation({ mutationFn: resendVerificationEmail });
  const formRef = useRef<HTMLFormElement>(null);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const update = (field: keyof Draft, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const validatePanAvailability = async (): Promise<boolean> => {
    const pan = form.businessPanNumber.trim();
    if (panCheck.pan === pan && panCheck.status === "available") return true;
    if (!/^\d{9}$/.test(pan)) {
      setPanCheck({
        pan,
        status: "error",
        message: "Enter the complete 9-digit PAN number.",
      });
      return false;
    }
    setPanCheck({ pan, status: "checking", message: "Checking PAN availability…" });
    try {
      const result = await checkBusinessPanAvailability(pan);
      if (result.available) {
        setPanCheck({ pan, status: "available", message: "PAN verified as available." });
        return true;
      }
      const message =
        "This PAN number is already registered. Sign in to the existing Business Owner account or contact Mero Perks support.";
      setPanCheck({ pan, status: "unavailable", message });
      return false;
    } catch (caught) {
      setPanCheck({
        pan,
        status: "error",
        message: "We could not verify this PAN number. Check your connection and try again.",
      });
      setError(messageFromError(caught));
      return false;
    }
  };
  const showStep = (nextStep: TenantSignupStep) => {
    setError("");
    setStep(nextStep);
    window.requestAnimationFrame(() => {
      stepHeadingRef.current?.focus();
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };
  const continueWizard = async () => {
    setError("");
    if (!formRef.current?.reportValidity()) return;
    if (step === 2 && !(await validatePanAvailability())) {
      setError("Please resolve the Business PAN validation before continuing.");
      return;
    }
    const nextStep = Math.min(4, step + 1) as TenantSignupStep;
    showStep(nextStep);
  };
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!accepted) {
      setError("Accept the Mero Perks partner terms before submitting your application.");
      return;
    }
    if (!(await validatePanAvailability())) {
      setError("Please resolve the Business PAN validation before submitting your application.");
      return;
    }
    try {
      const result = await mutation.mutateAsync({
        slug: form.slug,
        businessName: form.businessName.trim(),
        businessType: form.businessType,
        businessEmail: form.businessEmail.trim(),
        defaultCurrency: form.defaultCurrency,
        defaultTimezone: form.defaultTimezone,
        country: form.country,
        ownerFirstName: form.ownerFirstName.trim(),
        ownerLastName: form.ownerLastName.trim(),
        ownerEmail: form.ownerEmail.trim(),
        initialPassword: form.initialPassword,
        businessPanNumber: form.businessPanNumber.trim(),
        subscriptionPlan: form.subscriptionPlan,
        billingCycle: form.billingCycle,
        termsAccepted: true,
        ...((form.legalName ?? "").trim() ? { legalName: (form.legalName ?? "").trim() } : {}),
        ...((form.businessPhone ?? "").trim()
          ? { businessPhone: (form.businessPhone ?? "").trim() }
          : {}),
        ...((form.website ?? "").trim() ? { website: (form.website ?? "").trim() } : {}),
        ...((form.address ?? "").trim() ? { address: (form.address ?? "").trim() } : {}),
        ...((form.city ?? "").trim() ? { city: (form.city ?? "").trim() } : {}),
        ...((form.stateProvince ?? "").trim()
          ? { stateProvince: (form.stateProvince ?? "").trim() }
          : {}),
        ...((form.postalCode ?? "").trim() ? { postalCode: (form.postalCode ?? "").trim() } : {}),
        ...(locationPin ?? {}),
      });
      setApplication(result);
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  const resend = async () => {
    if (!application) return;
    setResendMessage("");
    try {
      await resendMutation.mutateAsync({ email: application.email, portal: "tenant" });
      setResendMessage("A fresh business-account verification email is on its way.");
    } catch (caught) {
      setResendMessage(messageFromError(caught));
    }
  };

  if (application) {
    const verified =
      !application.verificationRequired || application.delivery === "already_verified";
    return (
      <AuthShell
        visualTitle="Your partnership starts here."
        visualDetail="Every Mero Perks business is email-verified and reviewed before receiving platform access."
      >
        <div className="auth-card verification-card partner-application-result">
          <span className="auth-card__icon">{verified ? <BadgeCheck /> : <Mail />}</span>
          <p className="eyebrow">BUSINESS APPLICATION RECEIVED</p>
          <h1>{verified ? "Ready for platform review" : "Verify your business email"}</h1>
          <p className="auth-intro">
            {verified ? (
              <>
                <strong>{application.tenantSlug}</strong>{" "}
                <span>
                  is in the Super Admin approval queue. We will activate Business Owner access only
                  after review.
                </span>
              </>
            ) : (
              <>
                <span>We sent a secure verification link to</span>{" "}
                <strong>{application.email}</strong>.{" "}
                <span>Verification moves your application into the Super Admin review queue.</span>
              </>
            )}
          </p>
          <div className="application-plan-summary">
            <Crown />
            <span>
              <strong>{application.subscriptionPlan} plan</strong>
              {application.subscriptionPlan === "BASIC" ? (
                "Free—no payment required"
              ) : (
                <>
                  <span>7-day trial after approval</span> ·{" "}
                  <span>
                    {application.billingCycle === "ANNUAL" ? "Annual billing" : "Monthly billing"}
                  </span>
                </>
              )}
            </span>
          </div>
          <div className="partner-approval-steps" aria-label="Business approval progress">
            <span className="is-complete">
              <Check /> Application submitted
            </span>
            <span className={verified ? "is-complete" : "is-current"}>
              {verified ? <Check /> : <Mail />} Email verified
            </span>
            <span className={verified ? "is-current" : ""}>
              <ShieldCheck /> Super Admin approval
            </span>
          </div>
          {application.delivery === "temporarily_unavailable" && (
            <Alert>
              Your application is saved, but the verification message could not be delivered yet.
              Use the resend option below.
            </Alert>
          )}
          {!verified && (
            <button
              className="button button--outline button--full"
              type="button"
              disabled={resendMutation.isPending}
              onClick={() => void resend()}
            >
              <RefreshCw /> {resendMutation.isPending ? "Requesting…" : "Resend verification email"}
            </button>
          )}
          {resendMessage && (
            <p className="verification-message" role="status">
              {resendMessage}
            </p>
          )}
          <Link className="button button--primary button--full" to="/login/tenant">
            Go to Business Owner login <ArrowRight />
          </Link>
          <Link className="button button--quiet button--full" to="/business">
            Return to Mero Perks for business
          </Link>
        </div>
      </AuthShell>
    );
  }

  const currentStep = TENANT_SIGNUP_STEPS.find((item) => item.id === step);
  const selectedPlan = SUBSCRIPTION_PLANS.find((plan) => plan.code === form.subscriptionPlan);

  if (!currentStep) return null;

  return (
    <AuthShell
      visualTitle="Build loyalty that feels local."
      visualDetail="Choose your plan, add your business, and create a secure owner account in four clear steps."
      visualImage="/brand/business-owner-onboarding.webp"
    >
      <div className="auth-card auth-card--register tenant-self-register">
        <Link className="back-link" to="/business">
          <ArrowRight /> Back to Mero Perks for business
        </Link>
        <p className="eyebrow">BECOME A MERO PERKS PARTNER</p>
        <h1>Apply for a Business Owner account</h1>
        <p className="auth-intro">
          Complete each step in order. Your account remains inactive until the owner email is
          verified and a Super Admin approves the application.
        </p>

        <nav className="tenant-wizard__progress" aria-label="Business application steps">
          <ol>
            {TENANT_SIGNUP_STEPS.map((item) => {
              const isCurrent = step === item.id;
              const isComplete = item.id < step;
              const isAvailable = item.id <= step;
              return (
                <li
                  key={item.id}
                  className={(isCurrent ? "is-current" : "") + (isComplete ? " is-complete" : "")}
                >
                  <button
                    type="button"
                    disabled={!isAvailable}
                    aria-current={isCurrent ? "step" : undefined}
                    aria-label={"Step " + String(item.id) + ": " + item.title}
                    onClick={() => {
                      if (isAvailable) showStep(item.id);
                    }}
                  >
                    <span className="tenant-wizard__step-number">
                      {isComplete ? <Check aria-hidden="true" /> : item.id}
                    </span>
                    <span className="tenant-wizard__step-label">{item.title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        {error && <Alert>{error}</Alert>}
        <form
          ref={formRef}
          className="form-stack tenant-wizard__form"
          onSubmit={(event) => {
            if (step < 4) {
              event.preventDefault();
              void continueWizard();
              return;
            }
            void submit(event);
          }}
        >
          <section
            className={"tenant-wizard__panel tenant-wizard__panel--step-" + String(step)}
            aria-labelledby="tenant-wizard-step-title"
          >
            <header className="tenant-wizard__panel-header">
              <div>
                <p className="eyebrow">
                  Step {step} of {TENANT_SIGNUP_STEPS.length}
                </p>
                <h2 id="tenant-wizard-step-title" ref={stepHeadingRef} tabIndex={-1}>
                  {currentStep.title}
                </h2>
                <p>{currentStep.detail}</p>
              </div>
              {step > 1 && selectedPlan && (
                <span className="tenant-wizard__plan-chip">
                  <Crown aria-hidden="true" /> {selectedPlan.name}
                </span>
              )}
            </header>

            {step === 1 && (
              <SubscriptionPricingSelector
                selectedPlan={form.subscriptionPlan}
                billingCycle={form.billingCycle}
                onPlanChange={(subscriptionPlan) => {
                  setForm((current) => ({ ...current, subscriptionPlan }));
                }}
                onBillingCycleChange={(billingCycle) => {
                  setForm((current) => ({ ...current, billingCycle }));
                }}
              />
            )}

            {step === 2 && (
              <div className="tenant-wizard__fields">
                <div className="form-row">
                  <label>
                    <span>Trading name</span>
                    <input
                      required
                      autoComplete="organization"
                      value={form.businessName}
                      onChange={(event) => {
                        const name = event.target.value;
                        update("businessName", name);
                        if (!slugTouched)
                          update("slug", name.toLowerCase().replace(/[^a-z0-9]/g, ""));
                      }}
                      placeholder="Himalayan Coffee House"
                    />
                  </label>
                  <label>
                    <span>Legal business name</span>
                    <input
                      required
                      value={form.legalName}
                      onChange={(event) => {
                        update("legalName", event.target.value);
                      }}
                      placeholder="Himalayan Coffee House Pvt. Ltd."
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    <span>Business PAN number</span>
                    <input
                      required
                      inputMode="numeric"
                      pattern="[0-9]{9}"
                      minLength={9}
                      maxLength={9}
                      value={form.businessPanNumber}
                      onChange={(event) => {
                        const value = event.target.value.replace(/\D/g, "").slice(0, 9);
                        update("businessPanNumber", value);
                        setPanCheck({ pan: value, status: "idle", message: "" });
                      }}
                      onBlur={() => {
                        if (form.businessPanNumber.length === 9) void validatePanAvailability();
                      }}
                      aria-invalid={
                        panCheck.pan === form.businessPanNumber &&
                        (panCheck.status === "unavailable" || panCheck.status === "error")
                      }
                      aria-describedby="business-pan-help"
                      placeholder="123456789"
                    />
                    <small id="business-pan-help">
                      Enter the 9-digit PAN registered to this business.
                    </small>
                    {panCheck.pan === form.businessPanNumber && panCheck.status !== "idle" && (
                      <span
                        className={"pan-availability pan-availability--" + panCheck.status}
                        role={
                          panCheck.status === "unavailable" || panCheck.status === "error"
                            ? "alert"
                            : "status"
                        }
                      >
                        {panCheck.status === "available" && <CheckCircle2 aria-hidden="true" />}
                        {panCheck.status === "checking" && <RefreshCw aria-hidden="true" />}
                        {panCheck.message}
                      </span>
                    )}
                  </label>
                  <label>
                    <span>Business type</span>
                    <select
                      required
                      value={form.businessType}
                      onChange={(event) => {
                        update("businessType", event.target.value);
                      }}
                    >
                      <option>Retail</option>
                      <option>Food & beverage</option>
                      <option>Hospitality</option>
                      <option>Entertainment</option>
                      <option>Travel</option>
                      <option>Other</option>
                    </select>
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    <span>Business code</span>
                    <input
                      required
                      pattern="[a-z0-9]+"
                      value={form.slug}
                      onChange={(event) => {
                        setSlugTouched(true);
                        update("slug", event.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""));
                      }}
                      placeholder="himalayancoffee"
                    />
                    <small>
                      Use letters and numbers only. This is used when your team signs in.
                    </small>
                  </label>
                  <label>
                    <span>
                      Website <small>(optional)</small>
                    </span>
                    <input
                      type="url"
                      value={form.website}
                      onChange={(event) => {
                        update("website", event.target.value);
                      }}
                      placeholder="https://example.com"
                    />
                  </label>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="tenant-wizard__fields">
                <div className="form-row">
                  <label>
                    <span>Business email</span>
                    <input
                      required
                      type="email"
                      autoComplete="email"
                      value={form.businessEmail}
                      onChange={(event) => {
                        update("businessEmail", event.target.value);
                      }}
                    />
                  </label>
                  <label>
                    <span>Business phone</span>
                    <input
                      required
                      type="tel"
                      autoComplete="tel"
                      value={form.businessPhone}
                      onChange={(event) => {
                        update("businessPhone", event.target.value);
                      }}
                    />
                  </label>
                </div>
                <label>
                  <span>Street address</span>
                  <input
                    required
                    autoComplete="street-address"
                    value={form.address}
                    onChange={(event) => {
                      update("address", event.target.value);
                    }}
                  />
                </label>
                <div className="form-row form-row--three">
                  <label>
                    <span>City</span>
                    <input
                      required
                      autoComplete="address-level2"
                      value={form.city}
                      onChange={(event) => {
                        update("city", event.target.value);
                      }}
                    />
                  </label>
                  <label>
                    <span>Province</span>
                    <input
                      required
                      autoComplete="address-level1"
                      value={form.stateProvince}
                      onChange={(event) => {
                        update("stateProvince", event.target.value);
                      }}
                    />
                  </label>
                  <label>
                    <span>Postal code</span>
                    <input
                      autoComplete="postal-code"
                      value={form.postalCode}
                      onChange={(event) => {
                        update("postalCode", event.target.value);
                      }}
                    />
                  </label>
                </div>
                <div className="form-row form-row--three">
                  <label>
                    <span>Country</span>
                    <input
                      required
                      value={form.country}
                      onChange={(event) => {
                        update("country", event.target.value);
                      }}
                    />
                  </label>
                  <label>
                    <span>Currency</span>
                    <input
                      required
                      minLength={3}
                      maxLength={3}
                      value={form.defaultCurrency}
                      onChange={(event) => {
                        update("defaultCurrency", event.target.value.toUpperCase());
                      }}
                    />
                  </label>
                  <label>
                    <span>Timezone</span>
                    <input
                      required
                      value={form.defaultTimezone}
                      onChange={(event) => {
                        update("defaultTimezone", event.target.value);
                      }}
                    />
                  </label>
                </div>
                <TenantLocationPicker value={locationPin} onChange={setLocationPin} />
              </div>
            )}

            {step === 4 && (
              <div className="tenant-wizard__fields">
                <div className="form-row">
                  <label>
                    <span>Owner first name</span>
                    <input
                      required
                      autoComplete="given-name"
                      value={form.ownerFirstName}
                      onChange={(event) => {
                        update("ownerFirstName", event.target.value);
                      }}
                    />
                  </label>
                  <label>
                    <span>Owner last name</span>
                    <input
                      required
                      autoComplete="family-name"
                      value={form.ownerLastName}
                      onChange={(event) => {
                        update("ownerLastName", event.target.value);
                      }}
                    />
                  </label>
                </div>
                <label>
                  <span>Owner email</span>
                  <input
                    required
                    type="email"
                    autoComplete="email"
                    value={form.ownerEmail}
                    onChange={(event) => {
                      update("ownerEmail", event.target.value);
                    }}
                  />
                  <small>
                    This address receives the verification link and becomes the Business Owner
                    login.
                  </small>
                </label>
                <label>
                  <span>Create password</span>
                  <input
                    required
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    value={form.initialPassword}
                    onChange={(event) => {
                      update("initialPassword", event.target.value);
                    }}
                    placeholder="At least 12 characters"
                  />
                  <small>
                    Create a password for this Business Owner account. The same email may also have
                    a Customer account with a different password.
                  </small>
                </label>
                <label className="check-field tenant-wizard__terms">
                  <input
                    required
                    type="checkbox"
                    checked={accepted}
                    onChange={(event) => {
                      setAccepted(event.target.checked);
                    }}
                  />
                  <span>
                    I confirm these business and PAN details are accurate and accept the Mero Perks
                    partner terms.
                  </span>
                </label>
              </div>
            )}

            <footer className="tenant-wizard__actions">
              {step > 1 && (
                <button
                  className="button button--outline"
                  type="button"
                  onClick={() => {
                    showStep((step - 1) as TenantSignupStep);
                  }}
                >
                  Back
                </button>
              )}
              {step < 4 ? (
                <button
                  className="button button--primary tenant-wizard__next"
                  type="button"
                  disabled={panCheck.status === "checking"}
                  onClick={() => void continueWizard()}
                >
                  Continue
                  <ArrowRight />
                </button>
              ) : (
                <button
                  className="button button--primary tenant-wizard__next"
                  disabled={mutation.isPending || panCheck.status === "checking"}
                  type="submit"
                >
                  {mutation.isPending ? "Submitting application…" : "Submit partner application"}
                  <ArrowRight />
                </button>
              )}
            </footer>
          </section>
        </form>
        <p className="auth-switch">
          Already approved? <Link to="/login/tenant">Business Owner login</Link>
        </p>
      </div>
    </AuthShell>
  );
}

function VerifyEmailPage() {
  const location = useLocation();
  const started = useRef(false);
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [message, setMessage] = useState("Confirming your secure verification link…");

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(location.search).get("token");
    window.history.replaceState({}, "", location.pathname);
    if (!token) {
      setStatus("error");
      setMessage(
        "This verification link is missing its secure token. Request a new email and try again.",
      );
      return;
    }
    void verifyEmail(token)
      .then(() => {
        setStatus("success");
        setMessage("Your email is verified. You can now sign in to your Mero Perks account.");
      })
      .catch((caught: unknown) => {
        setStatus("error");
        setMessage(messageFromError(caught));
      });
  }, [location.pathname, location.search]);

  return (
    <AuthShell
      visualTitle="Verified once. Rewarded everywhere."
      visualDetail="A single secure identity connects your customer and business access without mixing their data."
    >
      <div className="auth-card verification-card">
        <span className="auth-card__icon">
          {status === "verifying" ? <RefreshCw className="verification-spinner" /> : <BadgeCheck />}
        </span>
        <p className="eyebrow">EMAIL VERIFICATION</p>
        <h1>
          {status === "verifying"
            ? "Verifying your email"
            : status === "success"
              ? "You’re verified"
              : "That link didn’t work"}
        </h1>
        <p className="auth-intro" role="status">
          {message}
        </p>
        {status === "success" && (
          <div className="verification-login-links">
            <Link className="button button--primary button--full" to="/login/customer">
              Customer login <ArrowRight size={17} />
            </Link>
            <Link className="button button--quiet button--full" to="/login/tenant">
              Business Owner login
            </Link>
          </div>
        )}
        {status === "error" && (
          <Link className="button button--quiet button--full" to="/login/customer">
            Return to login
          </Link>
        )}
      </div>
    </AuthShell>
  );
}

function ResetPasswordPage() {
  const location = useLocation();
  const token = useMemo(
    () => new URLSearchParams(location.search).get("token") ?? "",
    [location.search],
  );
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);
  const mutation = useMutation({ mutationFn: () => resetPassword(token, password) });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!token) {
      setError("This reset link is missing its secure token. Ask an administrator for a new link.");
      return;
    }
    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }
    try {
      await mutation.mutateAsync();
      window.history.replaceState({}, "", location.pathname);
      setComplete(true);
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <AuthShell
      visualTitle="Secure access. Fresh start."
      visualDetail="Choose a strong new password for your Mero Perks customer, business, or administration access."
    >
      <div className="auth-card verification-card">
        <span className="auth-card__icon">
          <LockKeyhole />
        </span>
        <p className="eyebrow">PASSWORD RESET</p>
        <h1>{complete ? "Password updated" : "Choose a new password"}</h1>
        {complete ? (
          <>
            <p className="auth-intro">
              Your password is ready. All previous sessions were securely signed out.
            </p>
            <div className="verification-login-links">
              <Link className="button button--primary button--full" to="/login/customer">
                Customer login
              </Link>
              <Link className="button button--quiet button--full" to="/login/tenant">
                Business Owner login
              </Link>
            </div>
          </>
        ) : (
          <>
            {error && <Alert>{error}</Alert>}
            <form className="form-stack" onSubmit={(event) => void submit(event)}>
              <label>
                <span>New password</span>
                <div className="input-wrap">
                  <LockKeyhole />
                  <input
                    required
                    minLength={12}
                    maxLength={128}
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                  />
                </div>
                <small>Use at least 12 characters.</small>
              </label>
              <label>
                <span>Confirm new password</span>
                <div className="input-wrap">
                  <ShieldCheck />
                  <input
                    required
                    minLength={12}
                    maxLength={128}
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                    }}
                  />
                </div>
              </label>
              <button
                className="button button--primary button--full button--large"
                disabled={mutation.isPending}
                type="submit"
              >
                {mutation.isPending ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </AuthShell>
  );
}

function JoinBusinessPage() {
  const { tenantSlug = "" } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rewards = useQuery({ queryKey: ["public-rewards"], queryFn: fetchPublicRewards });
  const join = useMutation({
    mutationFn: () => joinCustomerBusiness(tenantSlug),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer-experience"] });
      await navigate(`/account/wallet?business=${encodeURIComponent(tenantSlug)}`);
    },
  });
  const matching = (rewards.data ?? []).filter((reward) => reward.tenantSlug === tenantSlug);
  const businessName =
    matching[0]?.tenantName ??
    tenantSlug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  const isCustomer = session.data?.sessionType === "customer";
  return (
    <PublicFrame>
      <main className="join-business-page">
        <section className="join-business-card">
          <span className="join-business-card__mark">
            <Store />
          </span>
          <p className="eyebrow">Join a business loyalty program</p>
          <h1>More to love at {businessName}</h1>
          <p>
            Use your one Mero Perks identity while keeping {businessName} points, rewards,
            preferences, and activity in their own private wallet.
          </p>
          <div className="join-business-benefits">
            <span>
              <Check /> No duplicate customer account
            </span>
            <span>
              <WalletCards /> Business points stay separate
            </span>
            <span>
              <Crown /> Eligible spend can build global tier status
            </span>
          </div>
          {join.isError && <Alert>{messageFromError(join.error)}</Alert>}
          {session.isLoading ? (
            <LoadingBlock label="Checking your account" />
          ) : isCustomer ? (
            <button
              className="button button--primary button--large"
              disabled={join.isPending}
              type="button"
              onClick={() => {
                join.mutate();
              }}
            >
              {join.isPending ? "Joining…" : `Join ${businessName}`}
              <ArrowRight />
            </button>
          ) : (
            <div className="join-business-card__actions">
              <Link
                className="button button--primary button--large"
                to={`/register?tenant=${encodeURIComponent(tenantSlug)}`}
              >
                Create account & join <ArrowRight />
              </Link>
              <Link
                className="button button--outline button--large"
                to={`/login/customer?returnTo=${encodeURIComponent(`/join/${tenantSlug}`)}`}
              >
                I already have an account
              </Link>
            </div>
          )}
          {matching.length > 0 && (
            <div className="join-business-preview">
              <small>AVAILABLE NOW</small>
              <strong>{matching[0]?.title}</strong>
              <span>{formatPoints(matching[0]?.pointsCost ?? 0)} points</span>
            </div>
          )}
        </section>
      </main>
    </PublicFrame>
  );
}

interface PortalNavItem {
  label: string;
  to: string;
  icon: ReactNode;
  feature?: SubscriptionFeature;
  children?: PortalNavItem[];
}

const rewardManagementNavItems: PortalNavItem[] = [
  { label: "Rewards Dashboard", to: "/tenant/rewards/dashboard", icon: <Activity /> },
  { label: "Calendar", to: "/tenant/rewards/calendar", icon: <CalendarDays /> },
  {
    label: "Approvals",
    to: "/tenant/rewards/approvals",
    icon: <CheckCircle2 />,
    feature: "advanced_analytics",
  },
  {
    label: "Liability & Budget",
    to: "/tenant/rewards/liability-budget",
    icon: <CircleDollarSign />,
    feature: "advanced_analytics",
  },
  { label: "Analytics", to: "/tenant/rewards/analytics", icon: <BarChart3 />, feature: "advanced_analytics" },
  { label: "Campaigns", to: "/tenant/rewards/campaigns", icon: <Megaphone />, feature: "campaigns" },
  { label: "Segments", to: "/tenant/rewards/segments", icon: <UsersRound />, feature: "segments" },
  { label: "Automations", to: "/tenant/rewards/automations", icon: <Workflow />, feature: "automations" },
];

const tenantNavItems: PortalNavItem[] = [
  { label: "Mero Checkout", to: "/tenant", icon: <ScanLine /> },
  { label: "Business Rules", to: "/tenant/earning-rules", icon: <SlidersHorizontal /> },
  { label: "Locations", to: "/tenant/locations", icon: <Store /> },
  { label: "Inventory", to: "/tenant/inventory", icon: <ShoppingBag /> },
  { label: "Quick Offers", to: "/tenant/quick-offers", icon: <TicketCheck /> },
  {
    label: "Rewards Management",
    to: "/tenant/rewards/dashboard",
    icon: <Gift />,
    children: rewardManagementNavItems,
  },
  { label: "Mero Credits", to: "/tenant/mero-credits", icon: <Coins />, feature: "mero_credits" },
  { label: "Subscription", to: "/tenant/subscription", icon: <Crown /> },
  { label: "Account", to: "/tenant/account", icon: <Users /> },
];

type TenantCheckoutOperation = "member" | "coupon";

function TenantCheckoutOperationSwitch({ active }: { active: TenantCheckoutOperation }) {
  return (
    <section className="checkout-operation-switch" aria-labelledby="checkout-operation-title">
      <div className="checkout-operation-switch__intro">
        <p className="eyebrow">Mero Checkout</p>
        <h1 id="checkout-operation-title">Choose a checkout operation</h1>
        <p>Record a member purchase or validate a one-time customer coupon from one workspace.</p>
      </div>
      <nav className="checkout-operation-switch__options" aria-label="Checkout operation">
        <Link
          className={active === "member" ? "is-active" : ""}
          to="/tenant"
          aria-current={active === "member" ? "page" : undefined}
        >
          <span><ScanLine /></span>
          <div>
            <strong>Member Scan</strong>
            <small>Scan a member, apply balances, and record the sale.</small>
          </div>
        </Link>
        <Link
          className={active === "coupon" ? "is-active" : ""}
          to="/tenant?operation=coupon"
          aria-current={active === "coupon" ? "page" : undefined}
        >
          <span><TicketCheck /></span>
          <div>
            <strong>Coupon Scan</strong>
            <small>Scan or enter a coupon and redeem it securely.</small>
          </div>
        </Link>
      </nav>
    </section>
  );
}

function TenantSetupRouteGate() {
  const session = useSession();
  const location = useLocation();
  const isBusinessOwner = session.data?.sessionType === "tenant_staff";
  const setup = useBusinessSetupStatus(isBusinessOwner);
  const routeRequirement = tenantRouteSubscriptionRequirement(location.pathname);
  const subscription = useQuery({
    queryKey: ["tenant-subscription"],
    queryFn: fetchBusinessSubscription,
    enabled: isBusinessOwner && setup.data?.isLive === true && routeRequirement !== null,
  });

  if (session.isLoading || (isBusinessOwner && setup.isLoading)) {
    return <LoadingBlock label="Checking Business Owner account setup" />;
  }

  if (!isBusinessOwner) return <Outlet />;

  if (setup.isError || !setup.data) {
    return (
      <main className="access-gate">
        <Brand />
        <span>
          <ShieldCheck />
        </span>
        <h1>We could not verify your account setup</h1>
        <p>
          Business navigation stays locked until Mero Perks can confirm your setup status. Please
          try again.
        </p>
        <button
          className="button button--primary"
          type="button"
          onClick={() => {
            void setup.refetch();
          }}
        >
          <RefreshCw /> Try again
        </button>
      </main>
    );
  }

  if (!isBusinessSetupRouteAllowed(setup.data, location.pathname)) {
    return <Navigate to={businessSetupTarget(setup.data)} replace />;
  }

  if (routeRequirement) {
    if (subscription.isLoading) return <LoadingBlock label="Checking plan access" />;
    if (subscription.isError || !subscription.data) {
      return (
        <main className="access-gate">
          <Brand />
          <span><ShieldCheck /></span>
          <h1>We could not verify your subscription</h1>
          <p>Plan-protected pages stay closed until Mero Perks can confirm the business subscription.</p>
          <button className="button button--primary" type="button" onClick={() => { void subscription.refetch(); }}>
            <RefreshCw /> Try again
          </button>
        </main>
      );
    }
    if (!subscriptionIncludes(subscription.data, routeRequirement.feature)) {
      return (
        <Navigate
          to={`/tenant/subscription?required=${encodeURIComponent(routeRequirement.feature)}`}
          replace
        />
      );
    }
  }

  return <Outlet />;
}

const superAdminNavItems: PortalNavItem[] = [
  { label: "Home", to: "/super-admin", icon: <House /> },
  { label: "Business Owners", to: "/super-admin/tenants", icon: <Building2 /> },
  { label: "Users", to: "/super-admin/users", icon: <Users /> },
  { label: "Earning Rules", to: "/super-admin/earning-rules", icon: <SlidersHorizontal /> },
  { label: "Mero Credits", to: "/super-admin/mero-credits", icon: <Coins /> },
  { label: "Settings", to: "/super-admin/settings", icon: <Settings2 /> },
];

function PortalShell({
  user,
  title,
  navItems,
  children,
}: {
  user: SessionUser;
  title: string;
  navItems: PortalNavItem[];
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() =>
    typeof window !== "undefined" && window.localStorage.getItem("mero-pos-nav-collapsed") === "true",
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState("");
  const [upgradeReason, setUpgradeReason] = useState<SubscriptionUpgradeReason | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isBusinessOwner = user.sessionType === "tenant_staff";
  const ownerAccount = useQuery({
    queryKey: ["tenant-owner-account"],
    queryFn: fetchBusinessOwnerAccount,
    enabled: isBusinessOwner,
  });
  const businessSetup = useBusinessSetupStatus(isBusinessOwner);
  const setupLocked = isBusinessOwner && businessSetup.data?.isLive !== true;
  const subscription = useQuery({
    queryKey: ["tenant-subscription"],
    queryFn: fetchBusinessSubscription,
    enabled: isBusinessOwner && !setupLocked,
  });
  const visibleNavItems: PortalNavItem[] = setupLocked
    ? businessSetup.data
      ? [{ label: "Account Setup", to: businessSetupTarget(businessSetup.data), icon: <Sparkles /> }]
      : []
    : navItems;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!isBusinessOwner) return;
    window.localStorage.setItem("mero-pos-nav-collapsed", String(navCollapsed));
  }, [isBusinessOwner, navCollapsed]);
  useEffect(() => {
    const syncFullscreen = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => { document.removeEventListener("fullscreenchange", syncFullscreen); };
  }, []);
  useEffect(() => {
    const activeScreen = stageRef.current?.querySelector<HTMLElement>(
      ".portal-content, .mc-page--portal",
    );
    if (activeScreen) {
      activeScreen.scrollTop = 0;
      activeScreen.scrollLeft = 0;
    }
  }, [location.pathname]);
  useEffect(() => {
    if (!isBusinessOwner) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isBusinessOwner]);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      queryClient.setQueryData(sessionKey, null);
      await navigate("/", { replace: true });
    }
  };
  const toggleFullscreen = async () => {
    try {
      setFullscreenError("");
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      setFullscreenError("Full screen could not be opened. Allow full-screen access in this browser and try again.");
    }
  };
  const itemIsLocked = (item: PortalNavItem) => Boolean(
    item.feature && subscription.data && !subscriptionIncludes(subscription.data, item.feature),
  );
  const handlePlanNavigation = (item: PortalNavItem, event: SyntheticEvent) => {
    if (!item.feature || !subscription.data || subscriptionIncludes(subscription.data, item.feature)) return;
    event.preventDefault();
    setOpen(false);
    setUpgradeReason(featureUpgradeReason(subscription.data, { feature: item.feature, label: item.label }));
  };
  const businessName = ownerAccount.data?.businessName ?? user.tenantName ?? "Business workspace";
  const businessPhoto = ownerAccount.data?.businessLogoUrl ?? ownerAccount.data?.profileImageUrl;

  return (
    <div
      ref={shellRef}
      className={`portal-shell${isBusinessOwner ? " portal-shell--pos" : ""}${isBusinessOwner && navCollapsed ? " portal-shell--nav-collapsed" : ""}${isFullscreen ? " portal-shell--fullscreen" : ""}`}
    >
      {upgradeReason && <SubscriptionUpgradeDialog reason={upgradeReason} close={() => { setUpgradeReason(null); }} />}
      {fullscreenError && (
        <AppAlert
          title="Full screen unavailable"
          message={fullscreenError}
          onDismiss={() => { setFullscreenError(""); }}
        />
      )}
      {open && (
        <button className="portal-scrim" type="button" aria-label="Close navigation" onClick={() => { setOpen(false); }} />
      )}
      <aside
        id="portal-navigation"
        className={`portal-sidebar ${open ? "is-open" : ""}`}
        aria-label={`${title} navigation`}
      >
        <div className="portal-sidebar__head">
          <Brand inverse />
          <button className="portal-sidebar__close icon-control" type="button" aria-label="Close navigation" onClick={() => { setOpen(false); }}>
            <X />
          </button>
        </div>
        <div className="workspace-label">
          <span>{user.sessionType === "super_admin" ? "SA" : initials(businessName)}</span>
          <div>
            <small>Workspace</small>
            <strong>{user.sessionType === "super_admin" ? "Platform admin" : businessName}</strong>
          </div>
        </div>
        <nav aria-label={`${title} navigation`}>
          {visibleNavItems.map((item) => item.children?.length ? (
            <details className="portal-nav-group" key={item.to} open={location.pathname.startsWith("/tenant/rewards")}>
              <summary
                className={location.pathname.startsWith("/tenant/rewards") ? "is-active" : ""}
                title={item.label}
                onClick={() => { if (navCollapsed) setNavCollapsed(false); }}
              >
                {item.icon}<span>{item.label}</span><ChevronDown className="portal-nav-group__chevron" />
              </summary>
              <div className="portal-nav-group__items">
                {item.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    end
                    className={itemIsLocked(child) ? "is-plan-locked" : ""}
                    aria-disabled={itemIsLocked(child)}
                    title={child.label}
                    onClick={(event) => { handlePlanNavigation(child, event); }}
                  >
                    {child.icon}<span>{child.label}</span>
                    {itemIsLocked(child) && <LockKeyhole className="portal-plan-lock" aria-label="Upgrade required" />}
                  </NavLink>
                ))}
              </div>
            </details>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={itemIsLocked(item) ? "is-plan-locked" : ""}
              aria-disabled={itemIsLocked(item)}
              title={item.label}
              onClick={(event) => { handlePlanNavigation(item, event); }}
            >
              {item.icon}<span>{item.label}</span>
              {itemIsLocked(item) && <LockKeyhole className="portal-plan-lock" aria-label="Upgrade required" />}
            </NavLink>
          ))}
        </nav>
        <div className="portal-sidebar__support">
          <CircleHelp />
          <div><strong>Need a hand?</strong><a href="mailto:support@meroperks.com">Contact support</a></div>
        </div>
        <button className="portal-logout" type="button" onClick={() => void handleLogout()}>
          <LogOut /><span>Log out</span>
        </button>
      </aside>
      <div className="portal-main">
        <header className="portal-topbar">
          <div className="portal-topbar__business">
            <button
              className="portal-menu icon-control"
              type="button"
              aria-label="Open navigation"
              aria-controls="portal-navigation"
              aria-expanded={open}
              onClick={() => { setOpen(true); }}
            >
              <Menu />
            </button>
            {isBusinessOwner && (
              <button
                className="pos-shell-control pos-shell-control--rail"
                type="button"
                aria-label={navCollapsed ? "Expand menu bar" : "Collapse menu bar"}
                title={navCollapsed ? "Expand menu bar" : "Collapse menu bar"}
                onClick={() => { setNavCollapsed((value) => !value); }}
              >
                {navCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
              </button>
            )}
            {isBusinessOwner ? (
              <div className="pos-business-identity">
                <span className="pos-business-identity__photo">
                  {businessPhoto ? <img src={businessPhoto} alt="" /> : initials(businessName)}
                </span>
                <span>
                  <small>ACTIVE BUSINESS</small>
                  <strong>{businessName}</strong>
                  <em>{title}</em>
                </span>
              </div>
            ) : (
              <div className="portal-topbar__title"><span>{title}</span><small>{user.tenantName ?? "Mero Perks"}</small></div>
            )}
          </div>
          <div className="portal-topbar__actions">
            {isBusinessOwner && (
              <button className="pos-shell-control" type="button" onClick={() => { void toggleFullscreen(); }}>
                {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                <span>{isFullscreen ? "Exit full screen" : "Full screen"}</span>
              </button>
            )}
            <div className="portal-account">
              <LanguageSwitch />
              <span className="avatar">{initials(user.name)}</span>
              <span><strong>{user.name}</strong><small>{user.email}</small></span>
            </div>
          </div>
        </header>
        <div ref={stageRef} className="portal-stage">
          {children}
          {!isBusinessOwner && (
            <div className="portal-attribution" aria-label="Technology partner">
              <span>Technology partner</span>
              <img src="/spandan-technology-solutions-nepal.png" alt="Spandan Technology Solutions Nepal" width="210" height="70" loading="lazy" />
              <p>© 2026 Spandan Technology Solutions Nepal.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessGate({ portal }: { portal: Portal }) {
  const copy = portalCopy[portal];
  return (
    <main className="access-gate">
      <Brand />
      <span>{copy.icon}</span>
      <h1>Sign in to continue</h1>
      <p>
        This workspace is protected. Use your {copy.eyebrow.toLowerCase()} credentials to continue.
      </p>
      <Link className="button button--primary" to={`/login/${portal}`}>
        Go to login <ArrowRight size={17} />
      </Link>
      <Link to="/">Return to public rewards</Link>
    </main>
  );
}

function LegacyCustomerAccount() {
  const session = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const rewardsQuery = useQuery({ queryKey: publicRewardsKey, queryFn: fetchPublicRewards });
  const redemptionsQuery = useQuery({
    queryKey: ["customer-redemptions"],
    queryFn: fetchRedemptions,
    enabled: session.data?.sessionType === "customer",
  });
  const accountsQuery = useQuery({
    queryKey: ["customer-accounts"],
    queryFn: fetchCustomerAccounts,
    enabled: session.data?.sessionType === "customer",
  });
  const notificationsQuery = useQuery({
    queryKey: ["customer-notifications"],
    queryFn: fetchCustomerNotifications,
    enabled: session.data?.sessionType === "customer",
  });
  const tierQuery = useQuery({
    queryKey: ["customer-tier"],
    queryFn: fetchTierStatus,
    enabled: session.data?.sessionType === "customer",
  });
  const tierActivityQuery = useQuery({
    queryKey: ["customer-tier-activity"],
    queryFn: fetchTierActivity,
    enabled: session.data?.sessionType === "customer",
  });
  const checkoutQuery = useQuery({
    queryKey: ["checkout-transactions", "customer"],
    queryFn: fetchCheckoutTransactions,
    enabled: session.data?.sessionType === "customer",
  });
  const receiptsQuery = useQuery({
    queryKey: ["receipt-submissions", "customer"],
    queryFn: fetchReceiptSubmissions,
    enabled: session.data?.sessionType === "customer",
  });
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [rewardToConfirm, setRewardToConfirm] = useState<Reward | null>(null);
  const [error, setError] = useState("");
  const [claimCode, setClaimCode] = useState("");
  const [receiptTenant, setReceiptTenant] = useState("");
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const selectedRewardId = new URLSearchParams(location.search).get("reward");
  const selectedRewardQuery = useQuery({
    queryKey: ["public-reward", selectedRewardId],
    queryFn: () => fetchPublicReward(selectedRewardId ?? ""),
    enabled: selectedRewardId !== null,
  });
  const redeemMutation = useMutation({
    mutationFn: ({
      reward,
      mechanismSelectionId,
    }: {
      reward: Reward;
      mechanismSelectionId?: string;
    }) => redeemReward(reward.publicId, mechanismSelectionId),
    onSuccess: async (value) => {
      setRedemption(value);
      setRewardToConfirm(null);
      setError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer-redemptions"] }),
        queryClient.invalidateQueries({ queryKey: ["customer-accounts"] }),
        queryClient.invalidateQueries({ queryKey: sessionKey }),
      ]);
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const refreshEarning = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["customer-tier"] }),
      queryClient.invalidateQueries({ queryKey: ["customer-tier-activity"] }),
      queryClient.invalidateQueries({ queryKey: ["customer-accounts"] }),
      queryClient.invalidateQueries({ queryKey: ["checkout-transactions", "customer"] }),
      queryClient.invalidateQueries({ queryKey: ["receipt-submissions", "customer"] }),
    ]);
  };
  const claimMutation = useMutation({
    mutationFn: redeemCheckoutClaim,
    onSuccess: async () => {
      setClaimCode("");
      setError("");
      await refreshEarning();
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const confirmMutation = useMutation({
    mutationFn: customerConfirmCheckout,
    onSuccess: refreshEarning,
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const receiptMutation = useMutation({
    mutationFn: submitReceipt,
    onSuccess: async () => {
      setReceiptAmount("");
      setReceiptReference("");
      setReceiptFile(null);
      setError("");
      await refreshEarning();
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  if (session.isLoading) return <LoadingBlock label="Opening your rewards" />;
  if (!session.data || session.data.sessionType !== "customer")
    return <AccessGate portal="customer" />;
  const user = session.data;
  const accountRewards = [
    ...(selectedRewardQuery.data ? [selectedRewardQuery.data] : []),
    ...(rewardsQuery.data ?? []).filter(
      (reward) => reward.publicId !== selectedRewardQuery.data?.publicId,
    ),
  ];
  const navItems: PortalNavItem[] = [{ label: "My rewards", to: "/account", icon: <Gift /> }];
  return (
    <PortalShell user={user} title="My rewards" navItems={navItems}>
      <main className="portal-content customer-content">
        <section className="welcome-row">
          <div>
            <p className="eyebrow">Customer account</p>
            <h1>Namaste, {user.name.split(" ")[0]}</h1>
            <p>Your partner rewards and redemptions are ready when you are.</p>
          </div>
          <div className="points-card">
            <span>
              <Sparkles />
            </span>
            <div>
              <small>Rewards for real life</small>
              <strong>Brighter days.</strong>
            </div>
          </div>
        </section>
        {selectedRewardId && !selectedRewardQuery.isError && (
          <Alert tone="success">
            <strong>Your account is ready.</strong> The reward you chose is shown first below.
            Review it, then redeem when you are ready.
          </Alert>
        )}
        {selectedRewardQuery.isError && (
          <Alert>
            The selected reward is no longer available, but you can choose another current offer
            below.
          </Alert>
        )}
        {error && <Alert>{error}</Alert>}
        <section className="member-command-grid">
          {tierQuery.isLoading ? (
            <article className="legacy-tier-state">
              <LoadingBlock label="Loading status" />
            </article>
          ) : tierQuery.isError ? (
            <article className="legacy-tier-state">
              <Alert>Tier status is temporarily unavailable.</Alert>
            </article>
          ) : (
            <PlatformTierCard
              className="legacy-tier-overview"
              tierCode={tierQuery.data?.tier?.code}
              tierName={tierQuery.data?.tier?.name}
              variant="horizontal"
              loading="eager"
            >
              <div className="legacy-tier-overview__copy">
                <p className="eyebrow">Your global tier</p>
                <h2>{tierQuery.data?.tier?.name ?? "Mero"}</h2>
                <p>{formatPoints(tierQuery.data?.activeTierPoints ?? 0)} active Tier Points</p>
                <div
                  className="tier-progress"
                  role="progressbar"
                  aria-label="Progress to next Mero tier"
                  aria-valuemin={0}
                  aria-valuemax={
                    tierQuery.data?.nextTier?.minimumTierPoints ??
                    tierQuery.data?.activeTierPoints ??
                    1
                  }
                  aria-valuenow={tierQuery.data?.activeTierPoints ?? 0}
                >
                  <span
                    style={{
                      width: `${String(Math.min(100, ((tierQuery.data?.activeTierPoints ?? 0) / Math.max(1, tierQuery.data?.nextTier?.minimumTierPoints ?? tierQuery.data?.activeTierPoints ?? 1)) * 100))}%`,
                    }}
                  />
                </div>
                <strong>
                  {tierQuery.data?.nextTier
                    ? `${formatPoints(tierQuery.data.pointsToNextTier ?? 0)} to ${tierQuery.data.nextTier.name}`
                    : "Highest tier reached"}
                </strong>
                <small>Tier Points are status-only and are never spent.</small>
              </div>
            </PlatformTierCard>
          )}
          <article className="member-qr-card">
            <div>
              <p className="eyebrow">Your member code</p>
              <h2>Scan once. Earn both.</h2>
              <p>
                Show this code at a participating business to link eligible spend to your rewards
                and platform tier.
              </p>
            </div>
            <Link className="button button--primary" to="/account/card">
              <QrCode /> Open secure QR
            </Link>
          </article>
        </section>
        <section
          className="portal-panel notification-panel"
          aria-labelledby="customer-notifications-heading"
        >
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Don&apos;t miss a perk</p>
              <h2 id="customer-notifications-heading">Expiration reminders</h2>
              <p>Timely notices about partner rewards and points that are close to expiring.</p>
            </div>
          </div>
          {notificationsQuery.isLoading ? (
            <LoadingBlock label="Loading reminders" />
          ) : notificationsQuery.isError ? (
            <div>
              <Alert>We could not load your reminders.</Alert>
              <button
                className="button button--outline button--compact"
                type="button"
                onClick={() => {
                  void notificationsQuery.refetch();
                }}
              >
                Try again
              </button>
            </div>
          ) : notificationsQuery.data?.length ? (
            <div className="notification-list">
              {notificationsQuery.data.map((notification) => (
                <article key={notification.publicId}>
                  <span className="notification-list__icon">
                    <Bell aria-hidden="true" />
                  </span>
                  <div>
                    <h3>{notification.title}</h3>
                    <p>{notification.message}</p>
                    <small>
                      {notification.tenant.name} · Expires {formatDate(notification.expiresAt)}
                    </small>
                  </div>
                  {notification.actionPath && (
                    <Link
                      className="button button--outline button--compact"
                      to={notification.actionPath}
                    >
                      View
                    </Link>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <Bell />
              <h3>No expiration reminders</h3>
              <p>We will let you know here before eligible rewards or points expire.</p>
            </div>
          )}
        </section>
        <section className="portal-panel checkout-tools-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Mero Checkout</p>
              <h2>Claim a cash purchase</h2>
              <p>
                Enter the six-digit code shown by the business, or submit a receipt when points are
                missing.
              </p>
            </div>
          </div>
          <div className="customer-earn-tools">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (/^\d{6}$/.test(claimCode)) claimMutation.mutate(claimCode);
              }}
            >
              <span className="tool-icon">
                <ScanLine />
              </span>
              <div>
                <strong>Enter checkout code</strong>
                <small>Codes expire quickly and can only be used once.</small>
              </div>
              <label>
                <span className="sr-only">Six-digit checkout code</span>
                <input
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={claimCode}
                  onChange={(event) => {
                    setClaimCode(event.target.value.replace(/\D/g, ""));
                  }}
                  placeholder="000000"
                />
              </label>
              <button
                className="button button--primary"
                disabled={claimMutation.isPending || claimCode.length !== 6}
                type="submit"
              >
                {claimMutation.isPending ? "Claiming…" : "Claim points"}
              </button>
            </form>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                receiptMutation.mutate({
                  tenantSlug: receiptTenant.trim(),
                  grossAmountMinor: Math.round(Number(receiptAmount) * 100),
                  ...(receiptReference.trim()
                    ? { merchantReference: receiptReference.trim() }
                    : {}),
                  ...(receiptFile ? { receipt: receiptFile } : {}),
                });
              }}
            >
              <span className="tool-icon">
                <ReceiptText />
              </span>
              <div>
                <strong>Missing points?</strong>
                <small>Send the business your purchase details for review.</small>
              </div>
              <label>
                <span>Business slug</span>
                <input
                  required
                  value={receiptTenant}
                  onChange={(event) => {
                    setReceiptTenant(event.target.value.toLowerCase());
                  }}
                  placeholder="himal-brew-house"
                />
              </label>
              <label>
                <span>Amount (NPR)</span>
                <input
                  required
                  min="1"
                  step="0.01"
                  type="number"
                  value={receiptAmount}
                  onChange={(event) => {
                    setReceiptAmount(event.target.value);
                  }}
                  placeholder="850"
                />
              </label>
              <label>
                <span>Receipt reference</span>
                <input
                  value={receiptReference}
                  onChange={(event) => {
                    setReceiptReference(event.target.value);
                  }}
                  placeholder="Optional receipt number"
                />
              </label>
              <label className="file-field">
                <span>Receipt image or PDF</span>
                <input
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  type="file"
                  onChange={(event) => {
                    setReceiptFile(event.target.files?.[0] ?? null);
                  }}
                />
              </label>
              <button
                className="button button--outline"
                disabled={receiptMutation.isPending}
                type="submit"
              >
                {receiptMutation.isPending ? "Sending…" : "Submit for review"}
              </button>
            </form>
          </div>
        </section>
        {(checkoutQuery.data ?? []).some((transaction) =>
          ["PENDING_CUSTOMER", "PENDING_MANAGER"].includes(transaction.status),
        ) && (
          <section className="portal-panel">
            <div className="panel-heading">
              <div>
                <h2>Purchases needing attention</h2>
                <p>Confirm only purchases you recognize. Manager approval may still be required.</p>
              </div>
            </div>
            <div className="transaction-list">
              {(checkoutQuery.data ?? [])
                .filter((transaction) =>
                  ["PENDING_CUSTOMER", "PENDING_MANAGER"].includes(transaction.status),
                )
                .map((transaction) => (
                  <article key={transaction.publicId}>
                    <span className="transaction-icon">
                      <Clock3 />
                    </span>
                    <div>
                      <strong>{transaction.tenantName}</strong>
                      <small>
                        {transaction.locationName} · {formatDate(transaction.occurredAt)}
                      </small>
                    </div>
                    <p>
                      <strong>
                        {formatMoney(transaction.netPayableAmountMinor, transaction.currencyCode)}
                      </strong>
                      {transaction.rewardPointsRedeemed > 0 && (
                        <small>
                          {formatPoints(transaction.rewardPointsRedeemed)} points applied
                        </small>
                      )}
                      <small>{transaction.status.replaceAll("_", " ")}</small>
                    </p>
                    {transaction.status === "PENDING_CUSTOMER" && (
                      <button
                        className="button button--primary button--compact"
                        disabled={confirmMutation.isPending}
                        type="button"
                        onClick={() => {
                          confirmMutation.mutate(transaction.publicId);
                        }}
                      >
                        Confirm purchase
                      </button>
                    )}
                  </article>
                ))}
            </div>
          </section>
        )}
        <section className="portal-panel balance-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Earn & enjoy</p>
              <h2>Your partner balances</h2>
              <p>
                Points stay with the business where you earned them—Mero Perks keeps every balance
                easy to find.
              </p>
            </div>
          </div>
          {accountsQuery.isLoading ? (
            <LoadingBlock label="Loading partner balances" />
          ) : accountsQuery.isError ? (
            <div>
              <Alert>We could not load your partner balances.</Alert>
              <button
                className="button button--outline button--compact"
                type="button"
                onClick={() => {
                  void accountsQuery.refetch();
                }}
              >
                Try again
              </button>
            </div>
          ) : accountsQuery.data?.length ? (
            <div className="balance-grid">
              {accountsQuery.data.map((account) => (
                <article
                  className="balance-card"
                  key={`${account.tenant.publicId}:${account.program.publicId}`}
                >
                  <div className="balance-card__merchant">
                    {account.tenant.logoUrl ? (
                      <img src={account.tenant.logoUrl} alt="" />
                    ) : (
                      <span>{initials(account.tenant.businessName)}</span>
                    )}
                    <div>
                      <strong>{account.tenant.businessName}</strong>
                      <small>{account.program.name}</small>
                    </div>
                  </div>
                  <div className="balance-card__points">
                    <Coins />
                    <span>
                      <strong>{formatPoints(account.pointsBalance)}</strong>
                      <small>points</small>
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <Coins />
              <h3>Your first balance will appear here</h3>
              <p>
                Shop with a participating partner to start earning points in their rewards program.
              </p>
            </div>
          )}
        </section>
        <section className="portal-panel">
          <div className="panel-heading">
            <div>
              <h2>Rewards for you</h2>
              <p>Use each partner’s points toward rewards from that same business.</p>
            </div>
            <Link to="/#rewards">
              Browse public catalogue <ArrowRight />
            </Link>
          </div>
          {rewardsQuery.isLoading || selectedRewardQuery.isLoading ? (
            <LoadingBlock label="Loading rewards" />
          ) : rewardsQuery.isError ? (
            <Alert>The reward catalogue could not be loaded. Please try again shortly.</Alert>
          ) : accountRewards.length ? (
            <div className="reward-grid reward-grid--account">
              {accountRewards.slice(0, 3).map((reward) => (
                <RewardCard
                  key={reward.publicId}
                  reward={reward}
                  user={user}
                  pending={
                    redeemMutation.isPending &&
                    redeemMutation.variables.reward.publicId === reward.publicId
                  }
                  onRedeem={setRewardToConfirm}
                />
              ))}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <Gift />
              <h3>New rewards are on the way</h3>
              <p>Check back soon for offers from Mero Perks partners.</p>
            </div>
          )}
        </section>
        <section className="portal-panel">
          <div className="panel-heading">
            <div>
              <h2>Redemption history</h2>
              <p>Your claimed offers and codes.</p>
            </div>
          </div>
          {redemptionsQuery.isLoading ? (
            <LoadingBlock label="Loading history" />
          ) : redemptionsQuery.isError ? (
            <Alert>We could not load your redemption history.</Alert>
          ) : (redemptionsQuery.data ?? []).length ? (
            <div className="redemption-list">
              {(redemptionsQuery.data ?? []).map((item) => (
                <article key={item.publicId}>
                  <span className="redemption-list__icon">
                    <TicketCheck />
                  </span>
                  <div>
                    <strong>{item.rewardTitle}</strong>
                    <span>
                      {item.tenantName} · {formatDate(item.createdAt)}
                    </span>
                  </div>
                  <div>
                    <small>{formatPoints(item.pointsSpent)} pts</small>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <WalletCards />
              <h3>No redemptions yet</h3>
              <p>Choose a reward above when you are ready.</p>
            </div>
          )}
        </section>
        <section className="portal-panel history-split">
          <div>
            <div className="panel-heading">
              <div>
                <h2>Tier Point activity</h2>
                <p>Your global status ledger across every partner.</p>
              </div>
            </div>
            {tierActivityQuery.isLoading ? (
              <LoadingBlock label="Loading activity" />
            ) : (tierActivityQuery.data ?? []).length ? (
              <div className="compact-ledger">
                {(tierActivityQuery.data ?? []).slice(0, 8).map((item) => (
                  <article key={item.publicId}>
                    <span className={item.tierPointsDelta >= 0 ? "is-positive" : "is-negative"}>
                      {item.tierPointsDelta >= 0 ? "+" : ""}
                      {formatPoints(item.tierPointsDelta)}
                    </span>
                    <div>
                      <strong>{item.tenantName ?? "Mero Perks"}</strong>
                      <small>
                        {item.entryType} · {formatDate(item.effectiveAt)}
                      </small>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="empty-state empty-state--compact">
                <Crown />
                <h3>No Tier Points yet</h3>
                <p>Your first qualified purchase will start your journey.</p>
              </div>
            )}
          </div>
          <div>
            <div className="panel-heading">
              <div>
                <h2>Receipt recovery</h2>
                <p>Track receipts submitted for missing points.</p>
              </div>
            </div>
            {(receiptsQuery.data ?? []).length ? (
              <div className="compact-ledger">
                {(receiptsQuery.data ?? []).slice(0, 8).map((item) => (
                  <article key={item.publicId}>
                    <span className={`receipt-state receipt-state--${item.status}`}>
                      {item.status}
                    </span>
                    <div>
                      <strong>{item.tenantName}</strong>
                      <small>
                        {formatMoney(item.grossAmountMinor, item.currencyCode)} ·{" "}
                        {formatDate(item.submittedAt)}
                      </small>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="empty-state empty-state--compact">
                <ReceiptText />
                <h3>No receipt claims</h3>
                <p>Submitted receipts and their review status will appear here.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      {rewardToConfirm && (
        <RewardTermsDialog
          reward={rewardToConfirm}
          pending={redeemMutation.isPending}
          close={() => {
            setRewardToConfirm(null);
          }}
          confirm={(mechanismSelectionId) => {
            redeemMutation.mutate(
              mechanismSelectionId
                ? { reward: rewardToConfirm, mechanismSelectionId }
                : { reward: rewardToConfirm },
            );
          }}
        />
      )}
      {redemption && (
        <RedemptionDialog
          redemption={redemption}
          close={() => {
            setRedemption(null);
          }}
        />
      )}
    </PortalShell>
  );
}

function CustomerAccount() {
  const session = useSession();
  if (session.isLoading) return <LoadingBlock label="Opening your rewards" />;
  if (!session.data || session.data.sessionType !== "customer")
    return <AccessGate portal="customer" />;
  const user = session.data;
  const navItems: PortalNavItem[] = [
    { label: "Home", to: "/account/home", icon: <Sparkles /> },
    { label: "Digital Card", to: "/account/card", icon: <QrCode /> },
    { label: "My Wallet", to: "/account/wallet", icon: <WalletCards /> },
    { label: "My Rewards", to: "/account/rewards", icon: <TicketCheck /> },
    { label: "Offers & Coupons", to: "/account/offers", icon: <Gift /> },
    { label: "Activity", to: "/account/activity", icon: <Clock3 /> },
    { label: "Profile", to: "/account/profile", icon: <Users /> },
  ];
  return (
    <PortalShell user={user} title="My Mero Perks" navItems={navItems}>
      <CustomerExperience user={user} />
    </PortalShell>
  );
}

function TenantAccountPortal() {
  const session = useSession();
  const queryClient = useQueryClient();
  const account = useQuery({
    queryKey: ["tenant-owner-account"],
    queryFn: fetchBusinessOwnerAccount,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const [draft, setDraft] = useState<BusinessOwnerAccountDetail | null>(null);
  const [message, setMessage] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  useEffect(() => {
    if (account.data) setDraft(account.data);
  }, [account.data]);
  const save = useMutation({
    mutationFn: updateBusinessOwnerAccount,
    onSuccess: async (value) => {
      setDraft(value);
      setMessage("Business Owner account updated.");
      await queryClient.invalidateQueries({ queryKey: ["tenant-owner-account"] });
    },
    onError: (error) => {
      setMessage(messageFromError(error));
    },
  });
  if (session.isLoading) return <LoadingBlock label="Opening account" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  if (account.isLoading || !draft) return <LoadingBlock label="Loading account details" />;
  const uploadPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const profileImageUrl = await uploadBusinessOwnerProfileImage(file);
      setDraft((current) => (current ? { ...current, profileImageUrl } : current));
      setMessage("Photo uploaded. Save the account to apply it.");
    } catch (error) {
      setMessage(messageFromError(error));
    } finally {
      setPhotoUploading(false);
    }
  };
  return (
    <PortalShell user={session.data} title="Account" navItems={tenantNavItems}>
      <main className="portal-content tenant-account-page">
        <section className="tenant-account-card">
          <header>
            <div>
              <p className="eyebrow">BUSINESS OWNER ACCOUNT</p>
              <h1>Your profile</h1>
              <p>Keep your contact details and optional checkout profile picture current.</p>
            </div>
          </header>
          {message && (
            <div className="inline-message" role="status">
              {message}
            </div>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate({
                firstName: draft.firstName.trim(),
                lastName: draft.lastName.trim(),
                phone: draft.phone?.trim() || null,
                profileImageUrl: draft.profileImageUrl,
              });
            }}
          >
            <div className="tenant-account-photo">
              {draft.profileImageUrl ? (
                <img src={draft.profileImageUrl} alt={`${draft.firstName} profile`} />
              ) : (
                <span>{initials(`${draft.firstName} ${draft.lastName}`)}</span>
              )}
              <div>
                <div className="tenant-account-photo__name">
                  <strong>
                    {draft.firstName} {draft.lastName}
                  </strong>
                  <small>Business Owner</small>
                </div>
                <label className="button button--outline">
                  <Camera /> Upload picture
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={photoUploading}
                    onChange={(event) => void uploadPhoto(event.target.files?.[0])}
                  />
                </label>
                <label className="button button--outline">
                  <Camera /> Take picture
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    capture="user"
                    disabled={photoUploading}
                    onChange={(event) => void uploadPhoto(event.target.files?.[0])}
                  />
                </label>
                {draft.profileImageUrl && (
                  <button
                    className="button button--text"
                    type="button"
                    onClick={() => {
                      setDraft({ ...draft, profileImageUrl: null });
                    }}
                  >
                    Remove photo
                  </button>
                )}
              </div>
            </div>
            <div className="tenant-account-fields">
              <label>
                <span>First name</span>
                <input
                  required
                  value={draft.firstName}
                  onChange={(event) => {
                    setDraft({ ...draft, firstName: event.target.value });
                  }}
                />
              </label>
              <label>
                <span>Last name</span>
                <input
                  required
                  value={draft.lastName}
                  onChange={(event) => {
                    setDraft({ ...draft, lastName: event.target.value });
                  }}
                />
              </label>
              <label>
                <span>Phone number</span>
                <input
                  type="tel"
                  value={draft.phone ?? ""}
                  onChange={(event) => {
                    setDraft({ ...draft, phone: event.target.value });
                  }}
                />
              </label>
              <label>
                <span>Verified sign-in email</span>
                <input readOnly value={draft.email} />
              </label>
            </div>
            <button
              className="button button--primary"
              type="submit"
              disabled={save.isPending || photoUploading}
            >
              <Check /> {save.isPending ? "Savingâ€¦" : "Save account"}
            </button>
          </form>
        </section>
      </main>
    </PortalShell>
  );
}

function SubscriptionPaymentDialog({
  currentPlan,
  targetPlan,
  initialBillingCycle,
  pending,
  serverError,
  close,
  submit,
}: {
  currentPlan: SubscriptionPlanCode;
  targetPlan: SubscriptionPlanCode;
  initialBillingCycle: SubscriptionBillingCycle;
  pending: boolean;
  serverError: string;
  close: () => void;
  submit: (input: {
    planCode: SubscriptionPlanCode;
    billingCycle: SubscriptionBillingCycle;
    paymentMethod: SubscriptionPaymentMethod;
  }) => void;
}) {
  const plan = SUBSCRIPTION_PLANS.find((candidate) => candidate.code === targetPlan);
  const [billingCycle, setBillingCycle] = useState<SubscriptionBillingCycle>(initialBillingCycle);
  const [paymentMethod, setPaymentMethod] = useState<SubscriptionPaymentMethod>(
    targetPlan === "BASIC" ? "NONE" : "PAYMENT_CARD",
  );
  const [holderName, setHolderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankReference, setBankReference] = useState("");
  const [validationError, setValidationError] = useState("");
  if (!plan) return null;
  const paid = targetPlan !== "BASIC";
  const currentIndex = SUBSCRIPTION_PLANS.findIndex((candidate) => candidate.code === currentPlan);
  const targetIndex = SUBSCRIPTION_PLANS.findIndex((candidate) => candidate.code === targetPlan);
  const action = targetIndex < currentIndex ? "Downgrade" : targetIndex > currentIndex ? "Upgrade" : "Update";
  const amount = billingCycle === "ANNUAL" ? plan.annualPrice : plan.monthlyPrice;
  const validateAndSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    setValidationError("");
    if (paymentMethod === "PAYMENT_CARD") {
      if (!holderName.trim() || cardNumber.replaceAll(" ", "").length < 12 || !cardExpiry.trim() || cardCvc.length < 3) {
        setValidationError("Complete the test card fields before continuing.");
        return;
      }
    }
    if (paymentMethod === "BANK_ACCOUNT" && (!bankName.trim() || !bankReference.trim())) {
      setValidationError("Choose a test bank and enter a test transfer reference.");
      return;
    }
    submit({ planCode: targetPlan, billingCycle, paymentMethod: paid ? paymentMethod : "NONE" });
  };
  const testPayload = `MEROPERKS|TEST|${targetPlan}|${billingCycle}|${String(amount)}`;
  return (
    <div className="subscription-payment-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="subscription-payment-dialog" role="dialog" aria-modal="true" aria-labelledby="subscription-payment-title">
        <header>
          <div>
            <p className="eyebrow">TEST SUBSCRIPTION CHECKOUT</p>
            <h2 id="subscription-payment-title">{action} to {plan.name}</h2>
            <p>No real payment is collected. Sensitive bank and card fields never leave this screen.</p>
          </div>
          <button className="icon-control" type="button" aria-label="Close subscription checkout" onClick={close}><X /></button>
        </header>
        <form onSubmit={validateAndSubmit}>
          <div className="subscription-payment-summary">
            <div><span>Plan</span><strong>{plan.name}</strong></div>
            <div><span>Test amount</span><strong>{amount === 0 ? "Free" : `NPR ${amount.toLocaleString()}`}</strong></div>
            <fieldset className="subscription-billing-toggle">
              <legend>Billing cycle</legend>
              <button type="button" className={billingCycle === "MONTHLY" ? "is-active" : ""} onClick={() => { setBillingCycle("MONTHLY"); }}>Monthly</button>
              <button type="button" className={billingCycle === "ANNUAL" ? "is-active" : ""} onClick={() => { setBillingCycle("ANNUAL"); }}>Annual</button>
            </fieldset>
          </div>
          {paid ? (
            <>
              <div className="subscription-payment-methods" role="group" aria-label="Test payment method">
                <button type="button" className={paymentMethod === "BANK_ACCOUNT" ? "is-active" : ""} onClick={() => { setPaymentMethod("BANK_ACCOUNT"); }}>
                  <Landmark /><span><strong>Bank account</strong><small>Test transfer</small></span>
                </button>
                <button type="button" className={paymentMethod === "PAYMENT_CARD" ? "is-active" : ""} onClick={() => { setPaymentMethod("PAYMENT_CARD"); }}>
                  <CreditCard /><span><strong>Credit / Debit</strong><small>Test card</small></span>
                </button>
                <button type="button" className={paymentMethod === "QR_CODE" ? "is-active" : ""} onClick={() => { setPaymentMethod("QR_CODE"); }}>
                  <QrCode /><span><strong>QR code</strong><small>Test scan</small></span>
                </button>
              </div>
              <div className="subscription-payment-fields">
                {paymentMethod === "PAYMENT_CARD" && (
                  <div className="subscription-card-fields">
                    <label><span>Cardholder name</span><input autoComplete="cc-name" value={holderName} onChange={(event) => { setHolderName(event.target.value); }} /></label>
                    <label className="is-wide"><span>Test card number</span><input inputMode="numeric" autoComplete="cc-number" placeholder="4242 4242 4242 4242" value={cardNumber} onChange={(event) => { setCardNumber(event.target.value.replace(/[^0-9 ]/g, "").slice(0, 19)); }} /></label>
                    <label><span>Expiry</span><input inputMode="numeric" autoComplete="cc-exp" placeholder="MM/YY" value={cardExpiry} onChange={(event) => { setCardExpiry(event.target.value.slice(0, 5)); }} /></label>
                    <label><span>CVC</span><input type="password" inputMode="numeric" autoComplete="cc-csc" maxLength={4} value={cardCvc} onChange={(event) => { setCardCvc(event.target.value.replace(/\D/g, "")); }} /></label>
                  </div>
                )}
                {paymentMethod === "BANK_ACCOUNT" && (
                  <div className="subscription-bank-fields">
                    <label><span>Test bank</span><select value={bankName} onChange={(event) => { setBankName(event.target.value); }}><option value="">Choose bank</option><option>Nepal Test Bank</option><option>Himalayan Demo Bank</option><option>Mero Sandbox Bank</option></select></label>
                    <label><span>Test transfer reference</span><input value={bankReference} onChange={(event) => { setBankReference(event.target.value.slice(0, 32)); }} placeholder="DEMO-TRANSFER-001" /></label>
                  </div>
                )}
                {paymentMethod === "QR_CODE" && (
                  <div className="subscription-qr-payment">
                    <QRCodeSVG value={testPayload} size={152} level="H" />
                    <div><strong>Scan the test QR</strong><p>This QR only confirms the simulated payment flow. It cannot move real funds.</p><code>{testPayload}</code></div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="subscription-downgrade-note"><Crown /><div><strong>Switch to the free Basic plan</strong><p>No payment method is needed. The change is applied immediately after confirmation.</p></div></div>
          )}
          {(validationError || serverError) && <p className="form-error" role="alert">{validationError || serverError}</p>}
          <footer>
            <button className="button button--ghost" type="button" onClick={close}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={pending}>{pending ? "Processing test payment…" : `${action} to ${plan.name}`}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function TenantSubscriptionPortal() {
  const session = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [targetPlan, setTargetPlan] = useState<SubscriptionPlanCode | null>(null);
  const [paymentReceipt, setPaymentReceipt] = useState("");
  const [subscriptionView, setSubscriptionView] = useState<"overview" | "plans">(() => {
    const parameters = new URLSearchParams(location.search);
    return parameters.has("upgrade") || parameters.has("required") ? "plans" : "overview";
  });
  const subscription = useQuery({
    queryKey: ["tenant-subscription"],
    queryFn: fetchBusinessSubscription,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const planChange = useMutation({
    mutationFn: changeBusinessSubscription,
    onSuccess: async (result) => {
      queryClient.setQueryData(["tenant-subscription"], result.subscription);
      setPaymentReceipt(
        result.payment.status === "SUCCEEDED"
          ? `Test payment approved. Reference ${result.payment.reference}. Your ${result.subscription.selectedPlan.name} plan is active.`
          : `Your ${result.subscription.selectedPlan.name} plan is active. No payment was required.`,
      );
      setTargetPlan(null);
      await queryClient.invalidateQueries({ queryKey: ["tenant-subscription"] });
    },
  });
  if (session.isLoading) return <LoadingBlock label="Opening subscription" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  const requiredFeatureParam = new URLSearchParams(location.search).get("required");
  const requiredFeature = SUBSCRIPTION_PLANS
    .flatMap((plan) => plan.features)
    .find((feature) => feature === requiredFeatureParam);
  const requestedUpgrade = new URLSearchParams(location.search).get("upgrade");
  return (
    <PortalShell user={session.data} title="Subscription" navItems={tenantNavItems}>
      {targetPlan && subscription.data && (
        <SubscriptionPaymentDialog
          currentPlan={subscription.data.planCode}
          targetPlan={targetPlan}
          initialBillingCycle={subscription.data.billingCycle}
          pending={planChange.isPending}
          serverError={planChange.isError ? messageFromError(planChange.error) : ""}
          close={() => { if (!planChange.isPending) setTargetPlan(null); }}
          submit={(input) => { planChange.mutate(input); }}
        />
      )}
      <main className="portal-content subscription-page">
        {subscription.isLoading && <LoadingBlock label="Loading your plan" />}
        {subscription.isError && <Alert>{messageFromError(subscription.error)}</Alert>}
        {subscription.data && (
          <>
            <nav className="subscription-view-menu" aria-label="Subscription workspace views">
              <button type="button" className={subscriptionView === "overview" ? "is-active" : ""} onClick={() => { setSubscriptionView("overview"); }}>
                <BarChart3 /><span><strong>Plan overview</strong><small>Status and capacity</small></span>
              </button>
              <button type="button" className={subscriptionView === "plans" ? "is-active" : ""} onClick={() => { setSubscriptionView("plans"); }}>
                <CreditCard /><span><strong>Plans & payment</strong><small>Upgrade or downgrade</small></span>
              </button>
            </nav>
            {paymentReceipt && (
              <section className="subscription-payment-success" role="status">
                <CheckCircle2 />
                <div><strong>Subscription updated</strong><p>{paymentReceipt}</p></div>
                <button type="button" aria-label="Dismiss confirmation" onClick={() => { setPaymentReceipt(""); }}><X /></button>
              </section>
            )}
            {requiredFeature && !subscriptionIncludes(subscription.data, requiredFeature) && (() => {
              const target = firstPlanWithFeature(requiredFeature);
              return <section className="subscription-upgrade-notice" role="status">
                <span><Crown /></span>
                <div>
                  <p className="eyebrow">UPGRADE REQUIRED</p>
                  <h2>{subscription.data.accessPlan.name} does not include this feature</h2>
                  <p>Upgrade to {target?.name ?? "a higher plan"} to unlock it and continue.</p>
                </div>
              </section>;
            })()}
            {subscriptionView === "overview" && <section className="subscription-hero">
              <div>
                <p className="eyebrow">BUSINESS SUBSCRIPTION</p>
                <h1>{subscription.data.selectedPlan.name}</h1>
                <p>{subscription.data.selectedPlan.tagline}</p>
              </div>
              <div className="subscription-hero__status">
                <strong>{subscription.data.status.replaceAll("_", " ")}</strong>
                <span>{subscription.data.billingCycle.toLowerCase()} billing</span>
                {subscription.data.trialEndsAt && (
                  <small>
                    Trial ends {new Date(subscription.data.trialEndsAt).toLocaleDateString()}
                  </small>
                )}
              </div>
            </section>}
            {subscriptionView === "overview" && subscription.data.trialExpired && (
              <Alert>
                Your paid trial has ended, so Basic access is currently applied. Contact Mero Perks
                to activate paid billing.
              </Alert>
            )}
            {subscriptionView === "overview" && <section className="subscription-usage" aria-label="Plan usage">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">PLAN USAGE</p>
                  <h2>Capacity at a glance</h2>
                </div>
              </div>
              <div className="subscription-usage__grid">
                {(
                  [
                    ["locations", "Locations"],
                    ["staffUsers", "Staff"],
                    ["members", "Members"],
                    ["quickOffers", "Quick Offers"],
                    ["inventoryItems", "Inventory"],
                    ["campaigns", "Campaigns"],
                  ] as const
                ).map(([key, label]) => {
                  const used = subscription.data.usage[key] ?? 0;
                  const limit = subscription.data.accessPlan.limits[key];
                  return (
                    <article key={key}>
                      <span>{label}</span>
                      <strong>
                        {used.toLocaleString()}{" "}
                        <small>/ {limit == null ? "Unlimited" : limit.toLocaleString()}</small>
                      </strong>
                    </article>
                  );
                })}
              </div>
            </section>}
            {subscriptionView === "plans" && <section className="subscription-comparison">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">AVAILABLE PLANS</p>
                  <h2>Choose the room your business needs</h2>
                </div>
              </div>
              <div className="subscription-comparison__grid">
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <article
                    key={plan.code}
                    className={`${plan.code === subscription.data.planCode ? "is-current" : ""} ${plan.code === requestedUpgrade ? "is-requested" : ""}`.trim()}
                  >
                    <div>
                      <h3>{plan.name}</h3>
                      {plan.code === subscription.data.planCode && <span>CURRENT</span>}
                    </div>
                    <strong>
                      {plan.annualPrice === 0
                        ? "Free"
                        : `Rs. ${plan.annualPrice.toLocaleString()}/year`}
                    </strong>
                    <p>{plan.tagline}</p>
                    <ul>
                      {plan.displayFeatures.map((feature) => (
                        <li key={feature}>
                          <Check /> {feature}
                        </li>
                      ))}
                    </ul>
                    {plan.code === subscription.data.planCode ? (
                      <button className="button button--ghost button--full" type="button" disabled>
                        Current plan
                      </button>
                    ) : (
                      <button
                        className="button button--primary button--full"
                        type="button"
                        onClick={() => { planChange.reset(); setTargetPlan(plan.code); }}
                      >
                        {SUBSCRIPTION_PLANS.findIndex((candidate) => candidate.code === plan.code) > SUBSCRIPTION_PLANS.findIndex((candidate) => candidate.code === subscription.data.planCode)
                          ? `Upgrade to ${plan.name}`
                          : `Change to ${plan.name}`}
                        <ArrowRight />
                      </button>
                    )}
                  </article>
                ))}
              </div>
              <p className="subscription-contact-note">
                Test mode is active. Bank, card, and QR options simulate payment without moving
                funds or storing financial credentials. Connect a production payment provider
                before accepting real payments.
              </p>
            </section>}
          </>
        )}
      </main>
    </PortalShell>
  );
}

function TenantPortal() {
  const session = useSession();
  const queryClient = useQueryClient();
  const checkoutConfigQuery = useQuery({
    queryKey: ["checkout-configuration"],
    queryFn: fetchCheckoutConfiguration,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const transactionsQuery = useQuery({
    queryKey: ["checkout-transactions", "tenant"],
    queryFn: fetchCheckoutTransactions,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const analyticsQuery = useQuery({
    queryKey: ["checkout-analytics"],
    queryFn: fetchCheckoutAnalytics,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const receiptsQuery = useQuery({
    queryKey: ["receipt-submissions", "tenant"],
    queryFn: fetchReceiptSubmissions,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const inventoryQuery = useQuery({
    queryKey: ["tenant-reward-inventory"],
    queryFn: fetchTenantInventory,
    enabled: session.data?.sessionType === "tenant_staff",
  });
  const [error, setError] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [selectedMemberPhoto, setSelectedMemberPhoto] = useState<string | null>(null);
  const [selectedMemberNumber, setSelectedMemberNumber] = useState("");
  const [selectedMemberTier, setSelectedMemberTier] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [checkoutAmount, setCheckoutAmount] = useState("");
  const [redemptionQuote, setRedemptionQuote] = useState<CheckoutRedemptionQuote | null>(null);
  const [rewardPointsToRedeem, setRewardPointsToRedeem] = useState(0);
  const [useRewardPoints, setUseRewardPoints] = useState(false);
  const [checkoutItems, setCheckoutItems] = useState<Record<string, number>>({});
  const [paymentType, setPaymentType] = useState("CASH");
  const [locationPublicId, setLocationPublicId] = useState("");
  const [activeClaim, setActiveClaim] = useState<{ displayCode: string; expiresAt: string } | null>(
    null,
  );
  const [checkoutWorkspaceView, setCheckoutWorkspaceView] = useState<
    "checkout" | "activity" | "receipts" | "overview"
  >("checkout");
  const [checkoutActivityPage, setCheckoutActivityPage] = useState(0);
  const [receiptQueuePage, setReceiptQueuePage] = useState(0);
  const refreshCheckout = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["checkout-transactions", "tenant"] }),
      queryClient.invalidateQueries({ queryKey: ["checkout-analytics"] }),
      queryClient.invalidateQueries({ queryKey: ["receipt-submissions", "tenant"] }),
    ]);
  };
  const checkoutMutation = useMutation({
    mutationFn: createCheckoutTransaction,
    onSuccess: async () => {
      setCustomerCode("");
      setSelectedMemberName("");
      setSelectedMemberPhoto(null);
      setSelectedMemberNumber("");
      setSelectedMemberTier("");
      setCheckoutAmount("");
      setRedemptionQuote(null);
      setRewardPointsToRedeem(0);
      setUseRewardPoints(false);
      setError("");
      await refreshCheckout();
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const redemptionQuoteMutation = useMutation({
    mutationFn: fetchCheckoutRedemptionQuote,
    onSuccess: (quote) => {
      setRedemptionQuote(quote);
      setRewardPointsToRedeem(0);
      setUseRewardPoints(false);
      setCheckoutItems({});
      setError("");
    },
    onError: (caught) => {
      setRedemptionQuote(null);
      setRewardPointsToRedeem(0);
      setUseRewardPoints(false);
      setError(messageFromError(caught));
    },
  });
  const actionMutation = useMutation({
    mutationFn: ({
      transaction,
      action,
    }: {
      transaction: CheckoutTransaction;
      action: "approve" | "reverse";
    }) =>
      action === "approve"
        ? approveCheckout(transaction.publicId)
        : reverseCheckout(transaction.publicId, "Refunded by merchant"),
    onSuccess: refreshCheckout,
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const claimMutation = useMutation({
    mutationFn: issueCheckoutClaim,
    onSuccess: (claim) => {
      setActiveClaim(claim);
      setError("");
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const receiptReviewMutation = useMutation({
    mutationFn: ({ publicId, action }: { publicId: string; action: "approved" | "rejected" }) =>
      reviewReceipt(
        publicId,
        action,
        action === "rejected" ? "Receipt could not be verified." : undefined,
      ),
    onSuccess: refreshCheckout,
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  if (session.isLoading) return <LoadingBlock label="Opening Business Owner workspace" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  const user = session.data;
  const checkoutLocation =
    locationPublicId || checkoutConfigQuery.data?.locations[0]?.publicId || "";
  const summary = analyticsQuery.data?.summary;
  const checkoutTransactions = transactionsQuery.data ?? [];
  const transactionPageCount = Math.max(1, Math.ceil(checkoutTransactions.length / 6));
  const activeTransactionPage = Math.min(checkoutActivityPage, transactionPageCount - 1);
  const visibleCheckoutTransactions = checkoutTransactions.slice(
    activeTransactionPage * 6,
    activeTransactionPage * 6 + 6,
  );
  const pendingReceipts = (receiptsQuery.data ?? []).filter((item) => item.status === "pending");
  const receiptPageCount = Math.max(1, Math.ceil(pendingReceipts.length / 6));
  const activeReceiptPage = Math.min(receiptQueuePage, receiptPageCount - 1);
  const visiblePendingReceipts = pendingReceipts.slice(
    activeReceiptPage * 6,
    activeReceiptPage * 6 + 6,
  );
  return (
    <PortalShell user={user} title="Business Owner workspace" navItems={tenantNavItems}>
      <main className="portal-content tenant-pos-page">
        <nav className="pos-task-switch" aria-label="Mero Checkout workspace">
          <button className={checkoutWorkspaceView === "checkout" ? "is-active" : ""} type="button" onClick={() => { setCheckoutWorkspaceView("checkout"); }}><ScanLine /><span><strong>Checkout</strong><small>Record a purchase</small></span></button>
          <button className={checkoutWorkspaceView === "activity" ? "is-active" : ""} type="button" onClick={() => { setCheckoutWorkspaceView("activity"); }}><ReceiptText /><span><strong>Activity</strong><small>Review transactions</small></span></button>
          <button className={checkoutWorkspaceView === "receipts" ? "is-active" : ""} type="button" onClick={() => { setCheckoutWorkspaceView("receipts"); }}><FileCheck2 /><span><strong>Receipts</strong><small>Recovery queue</small></span></button>
          <button className={checkoutWorkspaceView === "overview" ? "is-active" : ""} type="button" onClick={() => { setCheckoutWorkspaceView("overview"); }}><BarChart3 /><span><strong>Overview</strong><small>Today at a glance</small></span></button>
        </nav>
        {checkoutWorkspaceView === "overview" && <>
          <section className="welcome-row pos-workspace-heading">
          <div>
            <p className="eyebrow">Merchant loyalty command centre</p>
            <h1>{user.tenantName ?? "Your business"}</h1>
            <p>
              Capture purchases, award points, recover receipts, and publish rewards from one
              workspace.
            </p>
          </div>
          <Link className="button button--primary" to="/tenant/rewards/dashboard">
            <Gift /> Open Rewards Management
          </Link>
          </section>
          <section className="compact-stats merchant-stats">
          <article>
            <span>
              <ScanLine />
            </span>
            <div>
              <small>Checkouts</small>
              <strong>{formatPoints(summary?.transactionCount ?? 0)}</strong>
            </div>
          </article>
          <article>
            <span>
              <WalletCards />
            </span>
            <div>
              <small>Confirmed volume</small>
              <strong>
                {formatMoney(
                  summary?.confirmedVolumeMinor ?? 0,
                  checkoutConfigQuery.data?.settings[0]?.currencyCode ?? "NPR",
                )}
              </strong>
            </div>
          </article>
          <article>
            <span>
              <Coins />
            </span>
            <div>
              <small>Reward points issued</small>
              <strong>{formatPoints(summary?.rewardPointsIssued ?? 0)}</strong>
            </div>
          </article>
          <article>
            <span>
              <Crown />
            </span>
            <div>
              <small>Tier Points linked</small>
              <strong>{formatPoints(summary?.tierPointsIssued ?? 0)}</strong>
            </div>
          </article>
          </section>
        </>}
        {error && <Alert>{error}</Alert>}
        {checkoutWorkspaceView === "checkout" && <>
          <TenantCheckoutOperationSwitch active="member" />
          <section className="merchant-cockpit">
          <article className="checkout-terminal">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Mero Checkout</p>
                <h2>Record a purchase</h2>
                <p>Scan the member’s QR or enter their member ID. Amounts are recorded in NPR.</p>
              </div>
              <span className="live-pill">
                <i /> LIVE
              </span>
            </div>
            {checkoutConfigQuery.isLoading ? (
              <LoadingBlock label="Loading checkout" />
            ) : checkoutConfigQuery.isError ? (
              <Alert>Mero Checkout is not configured yet.</Alert>
            ) : (
              <form
                className="checkout-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  checkoutMutation.mutate({
                    locationPublicId: checkoutLocation,
                    customerCode: customerCode.trim(),
                    grossAmountMinor: Math.round(Number(checkoutAmount) * 100),
                    paymentType,
                    ...(useRewardPoints && rewardPointsToRedeem > 0
                      ? { rewardPointsToRedeem }
                      : {}),
                    ...(Object.keys(checkoutItems).length
                      ? {
                          lineItems: Object.entries(checkoutItems).flatMap(([code, quantity]) => {
                            const item = (inventoryQuery.data ?? []).find(
                              (candidate: InventoryItem) => candidate.code === code,
                            );
                            return item && quantity > 0
                              ? [{ productCode: item.code, categoryCode: item.category, quantity }]
                              : [];
                          }),
                        }
                      : {}),
                  });
                }}
              >
                <div className="checkout-form__field scan-field">
                  <label htmlFor="checkout-customer-code">Customer member code</label>
                  <div>
                    <QrCode />
                    <input
                      id="checkout-customer-code"
                      required
                      value={customerCode}
                      onChange={(event) => {
                        setCustomerCode(event.target.value);
                        setSelectedMemberName("");
                        setSelectedMemberPhoto(null);
                        setSelectedMemberNumber("");
                        setSelectedMemberTier("");
                        setRedemptionQuote(null);
                        setRewardPointsToRedeem(0);
                        setUseRewardPoints(false);
                      }}
                      placeholder="Scan QR or enter customer ID"
                    />
                    <button
                      className="checkout-scan-trigger"
                      type="button"
                      onClick={() => {
                        setScannerOpen(true);
                      }}
                    >
                      <Camera />
                      <span>Scan member</span>
                    </button>
                  </div>
                  {selectedMemberName && (
                    <div className="checkout-selected-member checkout-selected-member--profile">
                      {selectedMemberPhoto ? (
                        <img src={selectedMemberPhoto} alt={`${selectedMemberName} profile`} />
                      ) : (
                        <span>{initials(selectedMemberName)}</span>
                      )}
                      <div>
                        <strong>{selectedMemberName}</strong>
                        <small>
                          {selectedMemberNumber} Â· {selectedMemberTier}
                        </small>
                      </div>
                      <Check />
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <label>
                    <span>Location</span>
                    <select
                      required
                      value={checkoutLocation}
                      onChange={(event) => {
                        setLocationPublicId(event.target.value);
                        setRedemptionQuote(null);
                        setRewardPointsToRedeem(0);
                        setUseRewardPoints(false);
                      }}
                    >
                      {(checkoutConfigQuery.data?.locations ?? []).map((item) => (
                        <option key={item.publicId} value={item.publicId}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Payment</span>
                    <select
                      value={paymentType}
                      onChange={(event) => {
                        setPaymentType(event.target.value);
                      }}
                    >
                      <option value="CASH">Cash</option>
                      <option value="CARD">Card</option>
                      <option value="QR_PAYMENT">QR payment</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </label>
                </div>
                <label className="amount-field">
                  <span>Purchase amount</span>
                  <div>
                    <b>NPR</b>
                    <input
                      required
                      min="1"
                      step="0.01"
                      type="number"
                      value={checkoutAmount}
                      onChange={(event) => {
                        setCheckoutAmount(event.target.value);
                        setRedemptionQuote(null);
                        setRewardPointsToRedeem(0);
                        setUseRewardPoints(false);
                      }}
                      placeholder="0.00"
                    />
                  </div>
                </label>
                {(inventoryQuery.data ?? []).some(
                  (item: InventoryItem) => item.status === "active",
                ) && (
                  <details className="checkout-inventory">
                    <summary>
                      <ShoppingBag /> Basket items{" "}
                      <span>
                        {Object.values(checkoutItems).reduce((sum, value) => sum + value, 0)}{" "}
                        selected
                      </span>
                    </summary>
                    <p>
                      Select products or services when an offer, BOGO, or punch card is
                      item-specific.
                    </p>
                    <div className="checkout-inventory__list">
                      {(inventoryQuery.data ?? [])
                        .filter((item: InventoryItem) => item.status === "active")
                        .map((item: InventoryItem) => {
                          const quantity = checkoutItems[item.code] ?? 0;
                          return (
                            <label key={item.publicId}>
                              <input
                                type="checkbox"
                                checked={quantity > 0}
                                onChange={(event) => {
                                  setCheckoutItems((current) => {
                                    const next = { ...current };
                                    if (event.target.checked) next[item.code] = 1;
                                    else Reflect.deleteProperty(next, item.code);
                                    return next;
                                  });
                                }}
                              />
                              <span>
                                <strong>{item.name}</strong>
                                <small>
                                  {[item.brand, item.category].filter(Boolean).join(" · ")} ·{" "}
                                  {item.code}
                                </small>
                              </span>
                              {quantity > 0 && (
                                <input
                                  aria-label={`${item.name} quantity`}
                                  type="number"
                                  min="1"
                                  max="999"
                                  value={quantity}
                                  onChange={(event) => {
                                    setCheckoutItems((current) => ({
                                      ...current,
                                      [item.code]: Math.max(
                                        1,
                                        Math.min(999, Number(event.target.value) || 1),
                                      ),
                                    }));
                                  }}
                                />
                              )}
                            </label>
                          );
                        })}
                    </div>
                  </details>
                )}
                <div className="checkout-redemption">
                  {!redemptionQuote ? (
                    <button
                      className="button button--quiet button--compact"
                      disabled={
                        redemptionQuoteMutation.isPending ||
                        !customerCode.trim() ||
                        !checkoutLocation ||
                        !(Number(checkoutAmount) > 0)
                      }
                      type="button"
                      onClick={() => {
                        redemptionQuoteMutation.mutate({
                          customerCode: customerCode.trim(),
                          locationPublicId: checkoutLocation,
                          grossAmountMinor: Math.round(Number(checkoutAmount) * 100),
                        });
                      }}
                    >
                      <Coins />{" "}
                      {redemptionQuoteMutation.isPending
                        ? "Checking balance…"
                        : "Check Reward Point balance"}
                    </button>
                  ) : (
                    <div className="checkout-redemption__quote">
                      <div>
                        <small>{redemptionQuote.tenantName} balance</small>
                        <strong>{formatPoints(redemptionQuote.pointsBalance)} points</strong>
                        <span>
                          Worth{" "}
                          {formatMoney(
                            redemptionQuote.pointsBalanceValueMinor ??
                              Math.floor(
                                redemptionQuote.pointsBalance / redemptionQuote.rule.pointsPerUnit,
                              ) * redemptionQuote.rule.valuePerUnitMinor,
                            redemptionQuote.rule.currencyCode,
                          )}
                        </span>
                        <span>
                          Up to{" "}
                          {formatMoney(
                            redemptionQuote.maximumRedemptionValueMinor,
                            redemptionQuote.rule.currencyCode,
                          )}{" "}
                          can be used on this purchase
                        </span>
                      </div>
                      {redemptionQuote.maximumRedeemablePoints > 0 ? (
                        <div className="checkout-redemption__choice">
                          <label className="checkout-redemption__toggle">
                            <input
                              type="checkbox"
                              checked={useRewardPoints}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setUseRewardPoints(checked);
                                setRewardPointsToRedeem(
                                  checked ? redemptionQuote.maximumRedeemablePoints : 0,
                                );
                              }}
                            />
                            <span>Use Reward Points</span>
                          </label>
                          {useRewardPoints && (
                            <label>
                              <span>Points to redeem</span>
                              <input
                                type="number"
                                min={redemptionQuote.rule.minimumRedemptionPoints}
                                max={redemptionQuote.maximumRedeemablePoints}
                                step={redemptionQuote.rule.pointsPerUnit}
                                value={rewardPointsToRedeem}
                                onChange={(event) => {
                                  setRewardPointsToRedeem(Number(event.target.value));
                                }}
                              />
                              <small>
                                Partial redemption is allowed in increments of{" "}
                                {formatPoints(redemptionQuote.rule.pointsPerUnit)} · maximum{" "}
                                {formatPoints(redemptionQuote.maximumRedeemablePoints)}
                              </small>
                            </label>
                          )}
                        </div>
                      ) : (
                        <span className="status-pill status-pill--pending">
                          {redemptionQuote.pointsBalance === 0
                            ? "0 points with this business"
                            : `Minimum ${formatPoints(redemptionQuote.rule.minimumRedemptionPoints)} points required`}
                        </span>
                      )}
                      <div className="checkout-redemption__total">
                        <span>Customer pays</span>
                        <strong>
                          {formatMoney(
                            Math.max(
                              0,
                              Math.round(Number(checkoutAmount) * 100) -
                                (useRewardPoints
                                  ? (rewardPointsToRedeem / redemptionQuote.rule.pointsPerUnit) *
                                    redemptionQuote.rule.valuePerUnitMinor
                                  : 0),
                            ),
                            redemptionQuote.rule.currencyCode,
                          )}
                        </strong>
                        <small>New points are earned only on this net amount.</small>
                      </div>
                    </div>
                  )}
                </div>
                <div className="checkout-form__actions">
                  <button
                    className="button button--primary"
                    disabled={checkoutMutation.isPending || !checkoutLocation}
                    type="submit"
                  >
                    <ScanLine /> {checkoutMutation.isPending ? "Creating…" : "Create checkout"}
                  </button>
                  <button
                    className="button button--outline"
                    disabled={claimMutation.isPending || !checkoutLocation || !checkoutAmount}
                    type="button"
                    onClick={() => {
                      claimMutation.mutate({
                        locationPublicId: checkoutLocation,
                        grossAmountMinor: Math.round(Number(checkoutAmount) * 100),
                        paymentType,
                      });
                    }}
                  >
                    <QrCode /> Generate claim code
                  </button>
                </div>
              </form>
            )}
            {scannerOpen && (
              <TenantScanner
                close={() => {
                  setScannerOpen(false);
                }}
                onMemberSelected={(member) => {
                  setCustomerCode(member.publicId);
                  setSelectedMemberName(member.displayName);
                  setSelectedMemberPhoto(member.profileImageUrl);
                  setSelectedMemberNumber(member.memberNumber);
                  setSelectedMemberTier(member.tierName);
                  setRedemptionQuote(null);
                  setRewardPointsToRedeem(0);
                  setUseRewardPoints(false);
                  setScannerOpen(false);
                }}
              />
            )}
          </article>
          <article className="claim-display">
            <span className="claim-display__mark">
              <img src="/brand/mero-brand-logo.png" width="1254" height="1254" alt="" />
            </span>
            <p className="eyebrow">Customer self-claim</p>
            <h2>{activeClaim ? "Show this code" : "No active code"}</h2>
            {activeClaim ? (
              <>
                <strong>{activeClaim.displayCode}</strong>
                <p>
                  Customer enters this in Mero Perks before{" "}
                  {new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" }).format(
                    new Date(activeClaim.expiresAt),
                  )}
                  .
                </p>
              </>
            ) : (
              <>
                <div className="claim-placeholder">••••••</div>
                <p>
                  Enter an amount and generate a short-lived code when the customer cannot scan at
                  the counter.
                </p>
              </>
            )}
          </article>
          </section>
        </>}
        {checkoutWorkspaceView === "activity" && <section className="portal-panel pos-task-panel">
          <div className="panel-heading">
            <div>
              <h2>Checkout activity</h2>
              <p>
                Completed purchases apply earnings and redemptions immediately. Review only elevated
                approvals, and reverse genuine refunds without deleting ledger history.
              </p>
            </div>
            <button
              className="button button--quiet button--compact"
              type="button"
              onClick={() => {
                void refreshCheckout();
              }}
            >
              <RefreshCw /> Refresh
            </button>
          </div>
          {transactionsQuery.isLoading ? (
            <LoadingBlock label="Loading transactions" />
          ) : checkoutTransactions.length ? (
            <div className="data-table-wrap">
              <table className="data-table checkout-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Points</th>
                    <th>Status</th>
                    <th>When</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCheckoutTransactions.map((transaction) => (
                    <tr key={transaction.publicId}>
                      <td>
                        <div className="table-primary">
                          <span className="table-monogram">
                            {initials(transaction.customerName)}
                          </span>
                          <div>
                            <strong>{transaction.customerName}</strong>
                            <small>
                              {transaction.locationName} ·{" "}
                              {transaction.sourceType.replaceAll("_", " ")}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Amount">
                        <strong>
                          {formatMoney(transaction.netPayableAmountMinor, transaction.currencyCode)}
                        </strong>
                        {transaction.rewardPointsRedeemed > 0 && (
                          <small className="table-subline">
                            {formatPoints(transaction.rewardPointsRedeemed)} points saved{" "}
                            {formatMoney(
                              transaction.redemptionValueMinor,
                              transaction.currencyCode,
                            )}
                          </small>
                        )}
                      </td>
                      <td data-label="Points">
                        <span>{formatPoints(transaction.rewardPointsAwarded)} reward</span>
                        <small className="table-subline">
                          {formatPoints(transaction.tierPointsAwarded)} tier
                        </small>
                      </td>
                      <td data-label="Status">
                        <span
                          className={`status-pill status-pill--${transaction.status.toLowerCase()}`}
                        >
                          {transaction.status.replaceAll("_", " ")}
                        </span>
                      </td>
                      <td data-label="When">{formatDate(transaction.occurredAt)}</td>
                      <td>
                        <div className="table-actions">
                          {["PENDING_MANAGER", "REVIEW"].includes(transaction.status) && (
                            <button
                              className="table-action"
                              disabled={actionMutation.isPending}
                              type="button"
                              onClick={() => {
                                actionMutation.mutate({ transaction, action: "approve" });
                              }}
                            >
                              Approve
                            </button>
                          )}
                          {transaction.status === "CONFIRMED" && (
                            <button
                              className="table-action table-action--danger"
                              disabled={actionMutation.isPending}
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm("Reverse this transaction and both point awards?")
                                )
                                  actionMutation.mutate({ transaction, action: "reverse" });
                              }}
                            >
                              Reverse
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {transactionPageCount > 1 && <div className="pos-pager"><span>Page {String(activeTransactionPage + 1)} of {String(transactionPageCount)}</span><div><button type="button" disabled={activeTransactionPage === 0} onClick={() => { setCheckoutActivityPage((page) => Math.max(0, page - 1)); }}>Previous</button><button type="button" disabled={activeTransactionPage >= transactionPageCount - 1} onClick={() => { setCheckoutActivityPage((page) => Math.min(transactionPageCount - 1, page + 1)); }}>Next</button></div></div>}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <ScanLine />
              <h3>No checkouts yet</h3>
              <p>Your first recorded purchase will appear here.</p>
            </div>
          )}
        </section>}
        {checkoutWorkspaceView === "receipts" && <section className="portal-panel pos-task-panel">
          <div className="panel-heading">
            <div>
              <h2>Receipt recovery queue</h2>
              <p>
                Approve verified receipts to award both eligible ledgers, or reject with a clear
                audit event.
              </p>
            </div>
          </div>
          {pendingReceipts.length ? (
            <div className="receipt-review-grid">
              {visiblePendingReceipts.map((item) => (
                  <article key={item.publicId}>
                    <span>
                      <ReceiptText />
                    </span>
                    <div>
                      <strong>{item.customerName}</strong>
                      <small>
                        {item.merchantReference ?? "No receipt reference"} ·{" "}
                        {formatDate(item.submittedAt)}
                      </small>
                      <b>{formatMoney(item.grossAmountMinor, item.currencyCode)}</b>
                    </div>
                    <div>
                      <button
                        className="button button--primary button--compact"
                        disabled={receiptReviewMutation.isPending}
                        type="button"
                        onClick={() => {
                          receiptReviewMutation.mutate({
                            publicId: item.publicId,
                            action: "approved",
                          });
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="button button--quiet button--compact"
                        disabled={receiptReviewMutation.isPending}
                        type="button"
                        onClick={() => {
                          receiptReviewMutation.mutate({
                            publicId: item.publicId,
                            action: "rejected",
                          });
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </article>
                ))}
              {receiptPageCount > 1 && <div className="pos-pager"><span>Page {String(activeReceiptPage + 1)} of {String(receiptPageCount)}</span><div><button type="button" disabled={activeReceiptPage === 0} onClick={() => { setReceiptQueuePage((page) => Math.max(0, page - 1)); }}>Previous</button><button type="button" disabled={activeReceiptPage >= receiptPageCount - 1} onClick={() => { setReceiptQueuePage((page) => Math.min(receiptPageCount - 1, page + 1)); }}>Next</button></div></div>}
            </div>
          ) : (
            <div className="empty-state empty-state--compact">
              <ReceiptText />
              <h3>Queue cleared</h3>
              <p>No receipt submissions are awaiting review.</p>
            </div>
          )}
        </section>}
        {checkoutWorkspaceView === "overview" && <section className="portal-panel reward-strategy-handoff">
          <span className="reward-strategy-handoff__icon">
            <Gift />
          </span>
          <div>
            <p className="eyebrow">Rewards Management</p>
            <h2>Build offers with complete reward controls</h2>
            <p>
              Create, schedule, target, approve, and activate rewards in the dedicated management
              workspace. Lifecycle changes are protected by the correct approval flow.
            </p>
          </div>
          <Link className="button button--outline" to="/tenant/rewards/dashboard">
            Open Rewards Management <ArrowRight />
          </Link>
        </section>}
      </main>
    </PortalShell>
  );
}

const rewardManagementViews: Record<string, { view: TenantRewardWorkspaceView; title: string }> = {
  dashboard: { view: "overview", title: "Rewards Dashboard" },
  catalog: { view: "catalog", title: "Reward Catalog / Offers & Coupons" },
  calendar: { view: "calendar", title: "Calendar" },
  approvals: { view: "approvals", title: "Approvals" },
  "liability-budget": { view: "liability", title: "Liability & Budget" },
  analytics: { view: "analytics", title: "Analytics" },
  campaigns: { view: "campaigns", title: "Campaigns" },
  segments: { view: "segments", title: "Segments" },
  automations: { view: "automations", title: "Automations" },
};

function TenantRewardsPortal({
  fixedView,
  fixedTitle,
}: {
  fixedView?: TenantRewardWorkspaceView;
  fixedTitle?: string;
} = {}) {
  const session = useSession();
  const location = useLocation();
  const { view: routeView = "" } = useParams<{ view: string }>();
  const route = fixedView && fixedTitle
    ? { view: fixedView, title: fixedTitle }
    : rewardManagementViews[routeView];
  if (session.isLoading) return <LoadingBlock label="Opening Rewards Management" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  if (!route) return <Navigate to="/tenant/rewards/dashboard" replace />;
  const user = session.data;
  return (
    <PortalShell user={user} title={route.title} navItems={tenantNavItems}>
      <TenantRewardsWorkspace
        tenantName={user.tenantName ?? "Your business"}
        tenantSlug={user.tenantSlug ?? ""}
        view={route.view}
        startNew={route.view === "quick-offers" && new URLSearchParams(location.search).get("setup") === "offer"}
        access={{
          roles: user.roles,
          permissions: user.permissions,
          allowedLocationIds: user.allowedLocationIds,
        }}
      />
    </PortalShell>
  );
}

function TenantEarningRulesPortal({ create = false }: { create?: boolean }) {
  const session = useSession();
  const location = useLocation();
  const setup = useBusinessSetupStatus(session.data?.sessionType === "tenant_staff");
  if (session.isLoading || setup.isLoading) return <LoadingBlock label="Opening Business Rules" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  if (setup.data?.isLive === false || new URLSearchParams(location.search).has("setup")) {
    return <Navigate to="/tenant/setup" replace />;
  }
  return (
    <PortalShell
      user={session.data}
      title={create ? "Add Reward Point Rule" : "Business Rules"}
      navItems={tenantNavItems}
    >
      {create ? (
        <NewRewardPointRule permissions={session.data.permissions} />
      ) : (
        <TenantEarningRules
          permissions={session.data.permissions}
        />
      )}
    </PortalShell>
  );
}

function TenantBusinessSetupPortal() {
  const session = useSession();
  if (session.isLoading) return <LoadingBlock label="Opening Business Account Setup" />;
  if (!session.data || session.data.sessionType !== "tenant_staff") {
    return <AccessGate portal="tenant" />;
  }
  return (
    <PortalShell user={session.data} title="Account Setup" navItems={tenantNavItems}>
      <BusinessSetupWizard
        permissions={session.data.permissions}
        tenantName={session.data.tenantName ?? "Your business"}
      />
    </PortalShell>
  );
}

function TenantRedemptionPortal() {
  const session = useSession();
  if (session.isLoading) return <LoadingBlock label="Opening Redemption" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  const user = session.data;
  return (
    <PortalShell user={user} title="Mero Checkout" navItems={tenantNavItems}>
      <TenantRewardsWorkspace
        tenantName={user.tenantName ?? "Your business"}
        tenantSlug={user.tenantSlug ?? ""}
        view="redemptions"
        header={<TenantCheckoutOperationSwitch active="coupon" />}
        access={{
          roles: user.roles,
          permissions: user.permissions,
          allowedLocationIds: user.allowedLocationIds,
        }}
      />
    </PortalShell>
  );
}

function TenantCheckoutPortal() {
  const location = useLocation();
  const operation = new URLSearchParams(location.search).get("operation");
  return operation === "coupon" ? <TenantRedemptionPortal /> : <TenantPortal />;
}

function TenantCheckoutControlPortal({ edit = false }: { edit?: boolean }) {
  const session = useSession();
  const { controlId } = useParams<{ controlId: string }>();
  if (session.isLoading) return <LoadingBlock label="Opening Checkout Controls" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  return (
    <PortalShell
      user={session.data}
      title={edit ? "Edit Checkout Control" : "Add Checkout Control"}
      navItems={tenantNavItems}
    >
      <CheckoutControlEditor
        permissions={session.data.permissions}
        {...(edit && controlId ? { controlId } : {})}
      />
    </PortalShell>
  );
}

function TenantMeroCreditsPortal() {
  const session = useSession();
  if (session.isLoading) return <LoadingBlock label="Opening Mero Credits" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  return (
    <PortalShell user={session.data} title="Business Mero Credits" navItems={tenantNavItems}>
      <BusinessMeroCredits />
    </PortalShell>
  );
}

function TenantLocationsPortal() {
  const session = useSession();
  if (session.isLoading) return <LoadingBlock label="Opening business locations" />;
  if (!session.data || session.data.sessionType !== "tenant_staff")
    return <AccessGate portal="tenant" />;
  return (
    <PortalShell user={session.data} title="Business locations" navItems={tenantNavItems}>
      <TenantLocations />
    </PortalShell>
  );
}

function TenantCreator({ close, saved }: { close: () => void; saved: () => Promise<void> }) {
  useDismissOnEscape(close);
  const initial: CreateTenantInput = {
    slug: "",
    businessName: "",
    businessType: "Retail",
    businessEmail: "",
    defaultCurrency: "NPR",
    defaultTimezone: "Asia/Kathmandu",
    country: "Nepal",
    ownerFirstName: "",
    ownerLastName: "",
    ownerEmail: "",
    initialPassword: "",
  };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const mutation = useMutation({ mutationFn: createTenant });
  const update = (field: keyof CreateTenantInput, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      await mutation.mutateAsync(form);
      setForm(initial);
      await saved();
      close();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={close}>
      <section
        className="modal editor-modal tenant-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tenant-creator-title"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          autoFocus
          className="modal__close icon-control"
          type="button"
          aria-label="Close"
          onClick={close}
        >
          <X />
        </button>
        <p className="eyebrow">New Business Owner</p>
        <h2 id="tenant-creator-title">Add a business</h2>
        <p className="modal-intro">
          Create the business and its first Business Owner login together.
        </p>
        {error && <Alert>{error}</Alert>}
        <form className="form-stack" onSubmit={(event) => void handleSubmit(event)}>
          <div className="form-row">
            <label>
              <span>Business name</span>
              <input
                required
                value={form.businessName}
                onChange={(event) => {
                  update("businessName", event.target.value);
                  if (!form.slug)
                    update(
                      "slug",
                      event.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-|-$/g, ""),
                    );
                }}
                placeholder="Sunrise Café"
              />
            </label>
            <label>
              <span>Business Owner slug</span>
              <input
                required
                pattern="[a-z0-9-]+"
                value={form.slug}
                onChange={(event) => {
                  update("slug", event.target.value.toLowerCase());
                }}
                placeholder="sunrise-cafe"
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              <span>Business contact email</span>
              <input
                required
                type="email"
                value={form.businessEmail}
                onChange={(event) => {
                  update("businessEmail", event.target.value);
                }}
                placeholder="hello@sunrisecafe.com"
              />
            </label>
            <label>
              <span>Business type</span>
              <select
                value={form.businessType}
                onChange={(event) => {
                  update("businessType", event.target.value);
                }}
              >
                <option>Retail</option>
                <option>Food & beverage</option>
                <option>Hospitality</option>
                <option>Entertainment</option>
                <option>Travel</option>
                <option>Other</option>
              </select>
            </label>
          </div>
          <div className="form-row form-row--three">
            <label>
              <span>Country</span>
              <input
                required
                value={form.country}
                onChange={(event) => {
                  update("country", event.target.value);
                }}
              />
            </label>
            <label>
              <span>Currency</span>
              <input
                required
                minLength={3}
                maxLength={3}
                value={form.defaultCurrency}
                onChange={(event) => {
                  update("defaultCurrency", event.target.value.toUpperCase());
                }}
              />
            </label>
            <label>
              <span>Timezone</span>
              <input
                required
                value={form.defaultTimezone}
                onChange={(event) => {
                  update("defaultTimezone", event.target.value);
                }}
              />
            </label>
          </div>
          <div className="form-divider">
            <span>Initial owner account</span>
          </div>
          <div className="form-row">
            <label>
              <span>First name</span>
              <input
                required
                autoComplete="off"
                value={form.ownerFirstName}
                onChange={(event) => {
                  update("ownerFirstName", event.target.value);
                }}
              />
            </label>
            <label>
              <span>Last name</span>
              <input
                required
                autoComplete="off"
                value={form.ownerLastName}
                onChange={(event) => {
                  update("ownerLastName", event.target.value);
                }}
              />
            </label>
          </div>
          <label>
            <span>Owner email</span>
            <input
              required
              type="email"
              autoComplete="off"
              value={form.ownerEmail}
              onChange={(event) => {
                update("ownerEmail", event.target.value);
              }}
              placeholder="owner@sunrisecafe.com"
            />
          </label>
          <label>
            <span>Create Business Owner password</span>
            <input
              required
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={form.initialPassword}
              onChange={(event) => {
                update("initialPassword", event.target.value);
              }}
              placeholder="At least 12 characters"
            />
            <small>
              This password belongs to the Business Owner account, even when the email is also used
              for a Customer account.
            </small>
          </label>
          <div className="modal-actions">
            <button className="button button--quiet" type="button" onClick={close}>
              Cancel
            </button>
            <button className="button button--primary" disabled={mutation.isPending} type="submit">
              {mutation.isPending ? "Creating…" : "Create Business Owner"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function TierRuleCreator({ saved }: { saved: () => Promise<void> }) {
  const [spendUnit, setSpendUnit] = useState("100");
  const [pointsPerUnit, setPointsPerUnit] = useState("1");
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
  );
  const [error, setError] = useState("");
  const mutation = useMutation({ mutationFn: createTierRule });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      await mutation.mutateAsync({
        spendUnitMinor: Math.round(Number(spendUnit) * 100),
        tierPointsPerUnit: Number(pointsPerUnit),
        minimumQualifyingMinor: Math.round(Number(spendUnit) * 100),
        maximumTierPointsPerTransaction: null,
        effectiveFrom: new Date(effectiveFrom).toISOString(),
      });
      await saved();
    } catch (caught) {
      setError(messageFromError(caught));
    }
  };
  return (
    <form className="tier-rule-form" onSubmit={(event) => void submit(event)}>
      {error && <Alert>{error}</Alert>}
      <label>
        <span>Eligible spend (NPR)</span>
        <input
          required
          min="1"
          step="1"
          type="number"
          value={spendUnit}
          onChange={(event) => {
            setSpendUnit(event.target.value);
          }}
        />
      </label>
      <label>
        <span>Tier Points earned</span>
        <input
          required
          min="1"
          step="1"
          type="number"
          value={pointsPerUnit}
          onChange={(event) => {
            setPointsPerUnit(event.target.value);
          }}
        />
      </label>
      <label>
        <span>Effective from</span>
        <input
          required
          type="datetime-local"
          value={effectiveFrom}
          onChange={(event) => {
            setEffectiveFrom(event.target.value);
          }}
        />
      </label>
      <button className="button button--primary" disabled={mutation.isPending} type="submit">
        <Plus /> {mutation.isPending ? "Creating…" : "Create draft rule"}
      </button>
    </form>
  );
}

function SuperAdminPortal() {
  const session = useSession();
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: fetchTenants,
    enabled: session.data?.sessionType === "super_admin",
  });
  const tierAdminQuery = useQuery({
    queryKey: ["tier-admin-program"],
    queryFn: fetchTierAdminProgram,
    enabled: session.data?.sessionType === "super_admin",
  });
  const [showCreator, setShowCreator] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [creditTenant, setCreditTenant] = useState<Tenant | null>(null);
  const [tenantToArchive, setTenantToArchive] = useState<Tenant | null>(null);
  const [feedback, setFeedback] = useState<SuperAdminFeedback | null>(null);
  const statusMutation = useMutation({
    mutationFn: ({ tenant, status }: { tenant: Tenant; status: Tenant["status"] }) =>
      setTenantStatus(tenant.publicId, status),
    onSuccess: async () => {
      setFeedback(null);
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (caught) => {
      setFeedback({
        message: messageFromError(caught),
        title: "We couldn’t complete that action",
        tone: "error",
      });
    },
  });
  const passwordResetMutation = useMutation({
    mutationFn: (tenant: Tenant) => sendTenantOwnerPasswordReset(tenant.publicId),
    onSuccess: (result) => {
      setFeedback(businessPasswordResetFeedback(result.email, result.delivery));
    },
    onError: (caught) => {
      setFeedback({
        message: messageFromError(caught),
        title: "We couldn’t complete that action",
        tone: "error",
      });
    },
  });
  const archiveMutation = useMutation({
    mutationFn: ({ tenant, confirmation }: { tenant: Tenant; confirmation: string }) =>
      archivePlatformTenant(tenant.publicId, confirmation),
    onSuccess: async (_archive, variables) => {
      setTenantToArchive(null);
      setFeedback({
        message: `${variables.tenant.name} and its related business records were archived. Its PAN number and Business Owner email registration are available for future use.`,
        title: "Business deleted and archived",
        tone: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tenants"] }),
        queryClient.invalidateQueries({ queryKey: ["platform-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["platform-users"] }),
        queryClient.invalidateQueries({ queryKey: ["public-rewards"] }),
        queryClient.invalidateQueries({ queryKey: ["marketplace"] }),
        queryClient.invalidateQueries({ queryKey: ["marketplace-types"] }),
        queryClient.invalidateQueries({ queryKey: ["customer-experience"] }),
      ]);
    },
  });
  const applicationMutation = useMutation({
    mutationFn: ({ tenant, decision }: { tenant: Tenant; decision: "approve" | "reject" }) =>
      reviewTenantApplication(tenant.publicId, decision),
    onSuccess: async (tenant, variables) => {
      setFeedback(businessApplicationFeedback(tenant.name, variables.decision));
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
      await queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
    },
    onError: (caught) => {
      setFeedback({
        message: messageFromError(caught),
        title: "We couldn’t complete that action",
        tone: "error",
      });
    },
  });
  const publishMutation = useMutation({
    mutationFn: publishTierRule,
    onSuccess: async () => {
      setFeedback(null);
      await queryClient.invalidateQueries({ queryKey: ["tier-admin-program"] });
    },
    onError: (caught) => {
      setFeedback({
        message: messageFromError(caught),
        title: "We couldn’t complete that action",
        tone: "error",
      });
    },
  });
  const assessmentMutation = useMutation({
    mutationFn: runTierAssessment,
    onSuccess: async () => {
      setFeedback(null);
      await queryClient.invalidateQueries({ queryKey: ["tier-admin-program"] });
    },
    onError: (caught) => {
      setFeedback({
        message: messageFromError(caught),
        title: "We couldn’t complete that action",
        tone: "error",
      });
    },
  });
  if (session.isLoading) return <LoadingBlock label="Opening platform administration" />;
  if (!session.data || session.data.sessionType !== "super_admin")
    return <AccessGate portal="super-admin" />;
  const user = session.data;
  const tenants = tenantsQuery.data ?? [];
  const pendingApplications = tenants.filter(
    (tenant) => tenant.applicationStatus === "PENDING_REVIEW",
  );
  const activeCount = tenants.filter((tenant) => tenant.status === "active").length;
  const refreshTenants = async () => {
    await queryClient.invalidateQueries({ queryKey: ["tenants"] });
  };
  return (
    <PortalShell user={user} title="Business Owners" navItems={superAdminNavItems}>
      <main className="portal-content super-admin-tenants">
        <section className="welcome-row">
          <div>
            <p className="eyebrow">Super admin</p>
            <h1>Business Owners</h1>
            <p>Onboard, monitor, and manage every business on Mero Perks.</p>
          </div>
          <button
            className="button button--primary"
            type="button"
            onClick={() => {
              setShowCreator(true);
            }}
          >
            <Plus /> Add Business Owner
          </button>
        </section>
        <section className="compact-stats">
          <article>
            <span>
              <Building2 />
            </span>
            <div>
              <small>Total Business Owners</small>
              <strong>{tenants.length}</strong>
            </div>
          </article>
          <article>
            <span>
              <BadgeCheck />
            </span>
            <div>
              <small>Active Business Owners</small>
              <strong>{activeCount}</strong>
            </div>
          </article>
          <article>
            <span>
              <Users />
            </span>
            <div>
              <small>Business Owner staff</small>
              <strong>
                {formatPoints(tenants.reduce((total, tenant) => total + tenant.staffCount, 0))}
              </strong>
            </div>
          </article>
        </section>
        {feedback && (
          <AppAlert
            message={feedback.message}
            title={feedback.title}
            tone={feedback.tone}
            onDismiss={() => {
              setFeedback(null);
            }}
          />
        )}
        <section className="portal-panel tenant-application-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Partner onboarding</p>
              <h2>Business Owner approval queue</h2>
              <p>
                Review PAN and business details. Approval stays locked until the owner verifies the
                account email.
              </p>
            </div>
            <span className="status-pill status-pill--pending">
              {pendingApplications.length} pending
            </span>
          </div>
          {pendingApplications.length ? (
            <div className="tenant-application-list">
              {pendingApplications.map((tenant) => (
                <article key={tenant.publicId}>
                  <div className="tenant-application-identity">
                    <span className="table-monogram">{initials(tenant.name)}</span>
                    <div>
                      <strong>{tenant.name}</strong>
                      <small>{tenant.legalName ?? tenant.name}</small>
                      <p>
                        PAN {tenant.businessPanNumber ?? "Not supplied"} · {tenant.contactEmail}
                      </p>
                    </div>
                  </div>
                  <dl>
                    <div>
                      <dt>Submitted</dt>
                      <dd>{formatDate(tenant.applicationSubmittedAt)}</dd>
                    </div>
                    <div>
                      <dt>Location</dt>
                      <dd>
                        {[tenant.city, tenant.stateProvince, tenant.country]
                          .filter(Boolean)
                          .join(", ")}
                      </dd>
                    </div>
                    <div>
                      <dt>Owner email</dt>
                      <dd>
                        <span
                          className={`status-pill status-pill--${tenant.ownerEmailVerified ? "active" : "pending"}`}
                        >
                          {tenant.ownerEmailVerified ? "Verified" : "Awaiting verification"}
                        </span>
                      </dd>
                    </div>
                  </dl>
                  <div className="sa-row-actions">
                    <button
                      className="button button--primary"
                      type="button"
                      disabled={!tenant.ownerEmailVerified || applicationMutation.isPending}
                      onClick={() => {
                        applicationMutation.mutate({ tenant, decision: "approve" });
                      }}
                    >
                      <BadgeCheck /> Approve & activate
                    </button>
                    <button
                      className="is-danger"
                      type="button"
                      disabled={applicationMutation.isPending}
                      onClick={() => {
                        applicationMutation.mutate({ tenant, decision: "reject" });
                      }}
                    >
                      <X /> Reject application
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="tenant-application-empty">
              <BadgeCheck />
              <div>
                <strong>No applications need review</strong>
                <p>New verified partner applications will appear here.</p>
              </div>
            </div>
          )}
        </section>
        <section className="portal-panel tier-admin-panel" hidden>
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Platform-owned status</p>
              <h2>Mero tier program</h2>
              <p>Global Tier Points and Business Owner reward points are governed separately.</p>
            </div>
            <button
              className="button button--outline button--compact"
              disabled={assessmentMutation.isPending}
              type="button"
              onClick={() => {
                assessmentMutation.mutate();
              }}
            >
              <RefreshCw /> {assessmentMutation.isPending ? "Assessing…" : "Run assessment"}
            </button>
          </div>
          {tierAdminQuery.isLoading ? (
            <LoadingBlock label="Loading tier program" />
          ) : tierAdminQuery.isError ? (
            <Alert>Tier administration is temporarily unavailable.</Alert>
          ) : (
            <>
              <div className="admin-tier-strip">
                {(tierAdminQuery.data?.tiers ?? []).map((tier) => (
                  <article key={tier.publicId} style={{ borderTopColor: tier.colorHex }}>
                    <span>
                      <Crown />
                    </span>
                    <div>
                      <small>{formatPoints(tier.minimumTierPoints)}+ TIER POINTS</small>
                      <strong>{tier.name}</strong>
                      <p>{tier.description}</p>
                    </div>
                  </article>
                ))}
              </div>
              <div className="tier-rule-layout">
                <div>
                  <h3>Effective-dated earning rules</h3>
                  <div className="rule-list">
                    {(tierAdminQuery.data?.rules ?? []).map((rule) => (
                      <article key={rule.publicId}>
                        <span className={`status-pill status-pill--${rule.status}`}>
                          {rule.status}
                        </span>
                        <div>
                          <strong>
                            {formatMoney(rule.spendUnitMinor, "NPR")} ={" "}
                            {formatPoints(rule.pointsPerUnit)} Tier Point
                            {rule.pointsPerUnit === 1 ? "" : "s"}
                          </strong>
                          <small>
                            Version {rule.versionNumber} · Effective{" "}
                            {formatDate(rule.effectiveFrom)}
                          </small>
                        </div>
                        {rule.status === "draft" && (
                          <button
                            className="button button--outline button--compact"
                            disabled={publishMutation.isPending}
                            type="button"
                            onClick={() => {
                              publishMutation.mutate(rule.publicId);
                            }}
                          >
                            Publish
                          </button>
                        )}
                      </article>
                    ))}
                  </div>
                </div>
                <div>
                  <h3>New rule version</h3>
                  <TierRuleCreator
                    saved={async () => {
                      await queryClient.invalidateQueries({ queryKey: ["tier-admin-program"] });
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </section>
        <section className="portal-panel">
          <div className="panel-heading">
            <div>
              <h2>All Business Owners</h2>
              <p>Manage access and see platform participation at a glance.</p>
            </div>
            <button
              className="button button--outline button--compact"
              type="button"
              onClick={() => {
                setShowCreator(true);
              }}
            >
              <Plus /> New Business Owner
            </button>
          </div>
          {tenantsQuery.isLoading ? (
            <LoadingBlock label="Loading Business Owners" />
          ) : tenantsQuery.isError ? (
            <Alert>{messageFromError(tenantsQuery.error)}</Alert>
          ) : tenants.length ? (
            <div className="data-table-wrap">
              <table className="data-table tenant-table">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Staff</th>
                    <th>Rewards</th>
                    <th>Joined</th>
                    <th>Status</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant) => (
                    <tr key={tenant.publicId}>
                      <td>
                        <div className="table-primary">
                          <span className="table-monogram">{initials(tenant.name)}</span>
                          <div>
                            <strong>{tenant.name}</strong>
                            <small>
                              {tenant.slug} · {tenant.contactEmail}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Staff">{formatPoints(tenant.staffCount)}</td>
                      <td data-label="Rewards">{formatPoints(tenant.rewardCount)}</td>
                      <td data-label="Joined">{formatDate(tenant.createdAt)}</td>
                      <td data-label="Status">
                        {tenant.applicationStatus === "APPROVED" ? (
                          <span className={`status-pill status-pill--${tenant.status}`}>
                            {tenant.status}
                          </span>
                        ) : (
                          <span
                            className={`status-pill status-pill--${tenant.applicationStatus === "REJECTED" ? "rejected" : "pending"}`}
                          >
                            {tenant.applicationStatus === "REJECTED"
                              ? "Rejected"
                              : "Pending approval"}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="sa-row-actions">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingTenant(tenant);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCreditTenant(tenant);
                            }}
                          >
                            <Coins /> Mero Credits
                          </button>
                          <button
                            disabled={passwordResetMutation.isPending}
                            type="button"
                            onClick={() => {
                              passwordResetMutation.mutate(tenant);
                            }}
                          >
                            Send reset
                          </button>
                          <button
                            className={tenant.status === "active" ? "is-danger" : ""}
                            disabled={
                              statusMutation.isPending || tenant.applicationStatus !== "APPROVED"
                            }
                            type="button"
                            onClick={() => {
                              statusMutation.mutate({
                                tenant,
                                status: tenant.status === "active" ? "suspended" : "active",
                              });
                            }}
                          >
                            {tenant.applicationStatus !== "APPROVED"
                              ? "Review application"
                              : tenant.status === "active"
                                ? "Suspend"
                                : "Activate"}
                          </button>
                          <button
                            className="is-danger"
                            disabled={archiveMutation.isPending}
                            type="button"
                            onClick={() => {
                              archiveMutation.reset();
                              setTenantToArchive(tenant);
                            }}
                          >
                            <Trash2 /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <Building2 />
              <h3>Add your first Business Owner</h3>
              <p>Onboard a business and create its owner login in one step.</p>
              <button
                className="button button--primary"
                type="button"
                onClick={() => {
                  setShowCreator(true);
                }}
              >
                <Plus /> Add Business Owner
              </button>
            </div>
          )}
        </section>
      </main>
      {showCreator && (
        <TenantCreator
          close={() => {
            setShowCreator(false);
          }}
          saved={refreshTenants}
        />
      )}
      {editingTenant && (
        <PlatformTenantEditor
          tenant={editingTenant}
          close={() => {
            setEditingTenant(null);
          }}
        />
      )}
      {creditTenant && (
        <PlatformMeroCreditAcceptanceEditor
          tenant={creditTenant}
          close={() => {
            setCreditTenant(null);
          }}
        />
      )}
      {tenantToArchive && (
        <ArchiveAccountDialog
          entityKind="business"
          entityLabel={tenantToArchive.name}
          confirmation={tenantToArchive.name}
          busy={archiveMutation.isPending}
          error={archiveMutation.isError ? messageFromError(archiveMutation.error) : undefined}
          impact={[
            "Staff access, locations, offers, rewards, inventory, transactions, and related business data will leave the live platform.",
            "The business PAN number and Business Owner email registration will be released for a future account.",
            "Shared customer accounts will not be deleted.",
          ]}
          onCancel={() => {
            if (archiveMutation.isPending) return;
            archiveMutation.reset();
            setTenantToArchive(null);
          }}
          onConfirm={(confirmation) => {
            archiveMutation.mutate({ tenant: tenantToArchive, confirmation });
          }}
        />
      )}
    </PortalShell>
  );
}

function SuperAdminWorkspacePortal({ title, children }: { title: string; children: ReactNode }) {
  const session = useSession();
  if (session.isLoading) return <LoadingBlock label={`Opening ${title.toLowerCase()}`} />;
  if (!session.data || session.data.sessionType !== "super_admin")
    return <AccessGate portal="super-admin" />;
  return (
    <PortalShell user={session.data} title={title} navItems={superAdminNavItems}>
      {children}
    </PortalShell>
  );
}

function NotFound() {
  return (
    <PublicFrame>
      <main className="not-found">
        <span>404</span>
        <h1>That page wandered off</h1>
        <p>The reward you are looking for may have moved or expired.</p>
        <Link className="button button--primary" to="/">
          Explore all rewards
        </Link>
      </main>
    </PublicFrame>
  );
}

export function App() {
  return (
    <>
      <RouteScrollManager />
      <Routes>
        <Route path="/" element={<PublicHome />} />
        <Route path="/marketplace" element={<PublicMarketplace />} />
        <Route path="/rewards" element={<PublicRewardsMarketplace />} />
        <Route path="/business" element={<BusinessPage />} />
        <Route path="/platform/rewards" element={<MarketingPage pageKey="rewards" />} />
        <Route path="/platform/wallet" element={<MarketingPage pageKey="wallet" />} />
        <Route path="/platform/tiers" element={<MarketingPage pageKey="tiers" />} />
        <Route path="/platform/credits" element={<MarketingPage pageKey="credits" />} />
        <Route path="/solutions/quick-offers" element={<MarketingPage pageKey="quick-offers" />} />
        <Route path="/solutions/checkout" element={<MarketingPage pageKey="checkout" />} />
        <Route
          path="/solutions/customer-insights"
          element={<MarketingPage pageKey="customer-insights" />}
        />
        <Route path="/solutions/retail" element={<MarketingPage pageKey="retail" />} />
        <Route
          path="/solutions/food-and-beverage"
          element={<MarketingPage pageKey="food-and-beverage" />}
        />
        <Route path="/solutions/hospitality" element={<MarketingPage pageKey="hospitality" />} />
        <Route
          path="/solutions/small-business"
          element={<MarketingPage pageKey="small-business" />}
        />
        <Route path="/how-it-works" element={<MarketingPage pageKey="how-it-works" />} />
        <Route path="/member-benefits" element={<MarketingPage pageKey="member-benefits" />} />
        <Route path="/pricing" element={<MarketingPage pageKey="pricing" />} />
        <Route path="/resources/faq" element={<MarketingPage pageKey="faq" />} />
        <Route
          path="/privacy-policy"
          element={
            <PublicFrame>
              <PrivacyPolicyPage />
            </PublicFrame>
          }
        />
        <Route
          path="/cookies-policy"
          element={
            <PublicFrame>
              <CookiePolicyPage />
            </PublicFrame>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <PublicFrame>
              <TermsAndConditionsPage />
            </PublicFrame>
          }
        />
        <Route
          path="/account-deletion"
          element={
            <PublicFrame>
              <AccountDeletionPage />
            </PublicFrame>
          }
        />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/tenant" element={<TenantRegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/join/:tenantSlug" element={<JoinBusinessPage />} />
        <Route path="/login" element={<Navigate to="/login/customer" replace />} />
        <Route path="/login/customer" element={<LoginPage portal="customer" />} />
        <Route path="/login/tenant" element={<LoginPage portal="tenant" />} />
        <Route path="/login/super-admin" element={<LoginPage portal="super-admin" />} />
        <Route path="/account/legacy" element={<LegacyCustomerAccount />} />
        <Route path="/account/*" element={<CustomerAccount />} />
        <Route element={<TenantSetupRouteGate />}>
          <Route path="/tenant/setup" element={<TenantBusinessSetupPortal />} />
          <Route path="/tenant" element={<TenantCheckoutPortal />} />
          <Route path="/tenant/account" element={<TenantAccountPortal />} />
          <Route path="/tenant/subscription" element={<TenantSubscriptionPortal />} />
          <Route
            path="/tenant/redemption"
            element={<Navigate to="/tenant?operation=coupon" replace />}
          />
          <Route path="/tenant/earning-rules" element={<TenantEarningRulesPortal />} />
          <Route path="/tenant/earning-rules/new" element={<TenantEarningRulesPortal create />} />
          <Route
            path="/tenant/earning-rules/checkout-controls/new"
            element={<TenantCheckoutControlPortal />}
          />
          <Route
            path="/tenant/earning-rules/checkout-controls/:controlId"
            element={<TenantCheckoutControlPortal edit />}
          />
          <Route
            path="/tenant/rewards"
            element={<Navigate to="/tenant/rewards/dashboard" replace />}
          />
          <Route
            path="/tenant/rewards/points-program"
            element={<Navigate to="/tenant/earning-rules" replace />}
          />
          <Route
            path="/tenant/rewards/settings"
            element={<Navigate to="/tenant/earning-rules" replace />}
          />
          <Route
            path="/tenant/rewards/strategy-templates"
            element={<Navigate to="/tenant/quick-offers" replace />}
          />
          <Route
            path="/tenant/rewards/redemptions"
            element={<Navigate to="/tenant?operation=coupon" replace />}
          />
          <Route
            path="/tenant/rewards/catalog"
            element={<Navigate to="/tenant/quick-offers" replace />}
          />
          <Route
            path="/tenant/inventory"
            element={<TenantRewardsPortal fixedView="inventory" fixedTitle="Inventory" />}
          />
          <Route
            path="/tenant/quick-offers"
            element={<TenantRewardsPortal fixedView="quick-offers" fixedTitle="Quick Offers" />}
          />
          <Route path="/tenant/rewards/:view" element={<TenantRewardsPortal />} />
          <Route path="/tenant/mero-credits" element={<TenantMeroCreditsPortal />} />
          <Route path="/tenant/locations" element={<TenantLocationsPortal />} />
          <Route path="/tenant/scanner" element={<Navigate to="/tenant" replace />} />
        </Route>
        <Route
          path="/super-admin"
          element={
            <SuperAdminWorkspacePortal title="Platform overview">
              <SuperAdminHomeView />
            </SuperAdminWorkspacePortal>
          }
        />
        <Route path="/super-admin/tenants" element={<SuperAdminPortal />} />
        <Route
          path="/super-admin/users"
          element={
            <SuperAdminWorkspacePortal title="Users">
              <SuperAdminUsersView />
            </SuperAdminWorkspacePortal>
          }
        />
        <Route
          path="/super-admin/earning-rules"
          element={
            <SuperAdminWorkspacePortal title="Earning Rules">
              <SuperAdminEarningRulesView />
            </SuperAdminWorkspacePortal>
          }
        />
        <Route
          path="/super-admin/settings"
          element={
            <SuperAdminWorkspacePortal title="Settings">
              <SuperAdminSettingsView />
            </SuperAdminWorkspacePortal>
          }
        />
        <Route
          path="/super-admin/mero-credits"
          element={
            <SuperAdminWorkspacePortal title="Mero Credits">
              <PlatformMeroCredits />
            </SuperAdminWorkspacePortal>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
